import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import SearchableSelect from '../../components/common/SearchableSelect';
import TableEmptyState from '../../components/common/TableEmptyState';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import NotificationManager from '../../components/notifications/NotificationManager';
import { Modal } from '../../components/ui/modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { useAnimatedDragReorder } from '../../hooks/useAnimatedDragReorder';
import { useDebouncedValue } from '../../shared/useDebouncedValue';
import {
  getIntakePrimaryContact,
  getIntakeRestaurantName,
  mergePrefill,
  mergeStoreIntakePrefill,
  splitName,
  type OnboardingIntake,
  type OnboardingPrefillTarget,
} from '../../types/onboardingIntake';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  type TableSortState,
  toOrderingParam,
  toggleSortState,
} from '../../utils/listOrdering';
import { phoneExtensionInput } from '../../utils/phoneExtension';
import ActivationOffersTab from './ActivationOffersTab';
import AdminCallsTab from './AdminCallsTab';
import AdminOrdersTab from './AdminOrdersTab';
import AdminReservationsTab from './AdminReservationsTab';
import AdminSentNotificationsTab from './AdminSentNotificationsTab';
import AgentsTab from './AgentsTab';
import DemoLinesTab from './DemoLinesTab';
import ExplicitOnlyTableTypesField, {
  normalizeExplicitOnlyTableTypes,
} from './ExplicitOnlyTableTypesField';
import FaqSupportTab from './FaqSupportTab';
import FinancialsTab from './FinancialsTab';
import HumanTransferScheduleFields, {
  appendHumanTransferFields,
  describeHumanTransferHours,
  humanTransferFormFields,
  normalizeHumanTransferHours,
  type HumanTransferFormFields,
} from './HumanTransferScheduleFields';
import InsightsChatTab from './InsightsChatTab';
import IntegrationsTab from './IntegrationsTab';
import KnowledgeBasesTab from './KnowledgeBasesTab';
import OnboardingIntakesTab from './OnboardingIntakesTab';
import OnboardingStatusTab from './OnboardingStatusTab';
import PlatformAdminNav from './PlatformAdminNav';
import PosConfigTab from './PosConfigTab';
import RefundReviewTab from './RefundReviewTab';
import ReservationLocationScopeSection from './ReservationLocationScopeSection';
import StoreGroupsTab from './StoreGroupsTab';
import StoresTab from './StoresTab';
import SubscriptionsTab from './SubscriptionsTab';
import { TableSearch } from './TableSearch';
import TwilioPhoneNumberPurchaseTab from './TwilioPhoneNumberPurchaseTab';
import TwilioSecondaryProfilesTab from './TwilioSecondaryProfilesTab';
import TwilioTfvVerificationTab from './TwilioTfvVerificationTab';
import UsersTab from './UsersTab';
import VipContactsTab from './VipContactsTab';
import { AGENT_SORT_KEY_TO_FIELD, type AgentSortKey } from './agentsModel';
import { filterAndSortRows, rowSearchText } from './clientTable';

import {
  buildPlatformAdminPath,
  readPlatformAdminFallback,
  resolvePlatformAdminRoute,
  writePlatformAdminSession,
  type AdminTab,
  type IntegrationSubTab,
  type NotificationSubTab,
  type OnboardingBillingSubtab,
  type PlansSubTab,
  type PlatformAdminRouteState,
  type PosConfigSubtab,
  type TwilioConfigSubtab,
} from './platformAdminRoutes';
import type { StoreGroup } from './storeGroupsModel';
import { STORE_SORT_KEY_TO_FIELD, type StoreSortKey } from './storesModel';
import {
  applyNewSecondary,
  numbersSharingOldSecondary,
  type SecondaryFollowerKey,
  secondaryFollowerLabel,
} from './transferNumberSync';
import { useAdminCalls } from './useAdminCalls';
import { useAdminOrders } from './useAdminOrders';
import { useAdminReservationsList } from './useAdminReservationsList';
import { useAdminSentNotifications } from './useAdminSentNotifications';
import {
  STORE_USER_SORT_KEY_TO_FIELD,
  type StoreUserSortKey,
} from './usersModel';
import type { StoreGroupUser, UserFormData } from './usersModel';

function formatValidationErrorValue(value: unknown): string | null {
  if (Array.isArray(value)) {
    return value
      .map((item) => formatValidationErrorValue(item))
      .filter((item): item is string => Boolean(item))
      .join(' ');
  }
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const messages = Object.entries(value)
      .map(([field, fieldValue]) => {
        const message = formatValidationErrorValue(fieldValue);
        return message ? `${field}: ${message}` : null;
      })
      .filter((item): item is string => Boolean(item));
    return messages.join(' ');
  }
  return null;
}

function formatApiErrorMessage(
  data: unknown,
  fallback: string,
  fieldLabels: Record<string, string> = {},
): string {
  if (!data || typeof data !== 'object') {
    return fallback;
  }

  const errorData = data as Record<string, unknown>;
  const validationSource =
    errorData.errors && typeof errorData.errors === 'object'
      ? errorData.errors
      : errorData.error && typeof errorData.error === 'object'
        ? errorData.error
        : errorData;

  const fieldMessages = Object.entries(
    validationSource as Record<string, unknown>,
  )
    .filter(([field]) => !['detail', 'message', 'error'].includes(field))
    .map(([field, value]) => {
      const message = formatValidationErrorValue(value);
      if (!message) return null;
      return `${fieldLabels[field] ?? field}: ${message}`;
    })
    .filter((message): message is string => Boolean(message));

  if (fieldMessages.length > 0) return fieldMessages.join(' ');

  const errorMessage =
    formatValidationErrorValue(errorData.detail) ||
    formatValidationErrorValue(errorData.message) ||
    formatValidationErrorValue(errorData.error);

  return errorMessage || fallback;
}

interface Store {
  id: number;
  name: string;
  city?: string;
  state?: string;
  timezone?: string;
  knowledge_base_id?: string | null;
  livemode?: boolean;
  takes_reservations?: boolean;
  takes_events?: boolean;
  agent_handles_events?: boolean;
  accepts_deliveries?: boolean;
  accepts_phone_orders?: boolean;
  posless_integration?: boolean;
  human_transfer_enabled?: boolean;
  manual_menu_snoozing_enabled?: boolean;
  cross_sell_enabled?: boolean;
  poshub_toast_group_id?: string;
  location_secondary_phone_number?: string;
  location_secondary_phone_number_ext?: string;
  forward_order_requests_to_phone?: boolean;
  order_requests_phone_number?: string;
  order_requests_phone_number_ext?: string;
  created_at?: string;
  [key: string]: any;
}

interface StoreFormData {
  name: string;
  phonetic_name: string;
  call_intro_sentence: string;
  speach_filler_phrase: string;
  vip_intro_phrase: string;
  agent_style: string;
  use_default_call_intro_sentence: boolean;
  default_call_intro_sentence?: string;
  legal_name: string;
  timezone: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  website: string;
  menu_link: string;
  knowledge_base_id: string;
  ordering_link: string;
  stripe_account_id: string;
  reservation_link: string;
  gift_card_link: string;
  reservation_email: string;
  default_delivery_fee_cents: number;
  pass_through_dispatch_quote: boolean;
  send_delivery_cost_to_deliverect: boolean;
  assess_tax_on_fees: boolean;
  make_fees_tax_inclusive: boolean;
  /** Percent used when Stripe Tax is not active. Blank charges 13% HST in Ontario. */
  fallback_tax_percent: string;
  application_fee_default_cents: number;
  delivery_fee_label: string;
  service_fee_label: string;
  default_delivery_mode: string;
  takes_reservations: boolean;
  takes_catering: boolean;
  takes_events: boolean;
  agent_handles_events: boolean;
  agent_handles_catering: boolean;
  outdoor_seating: boolean;
  collect_payment_online: boolean;
  accept_pay_on_site: boolean;
  /** Optional secondary phone number for the location */
  location_secondary_phone_number: string;
  /** Extension dialed after the secondary phone. Blank means no extension. */
  location_secondary_phone_number_ext: string;
  monthly_minutes_limit: number;
  ot_max_party_size: number | '';
  average_reservation_cover: string;
  google_price_level: string;
  explicit_only_table_types: string[];
  annotate_opentable_reservation_notes: boolean;
  opentable_reservation_note_annotation_position: string;
  opentable_reservation_note_annotation: string;
  livemode: boolean;
  store_group: number | null;
  store_group_name: string;
  logo?: File | null;
  accepts_deliveries: boolean;
  how_to_order_delivery_message: string;
  accepts_phone_orders: boolean;
  collect_email: boolean;
  posless_integration: boolean;
  human_transfer_enabled: boolean;
  human_transfer_schedule: 'always' | 'weekly' | 'daily';
  human_transfer_start_time: string;
  human_transfer_end_time: string;
  human_transfer_hours: {
    day_of_week: string;
    start_time: string;
    end_time: string;
    is_closed: boolean;
  }[];
  current_pos_system: string;
  poshub_toast_group_id: string;
  forward_order_requests_to_store: boolean;
  forward_order_requests_to_text_link: boolean;
  forward_order_requests_to_phone: boolean;
  order_requests_phone_number: string;
  order_requests_phone_number_ext: string;
  forward_catering_requests_to_store: boolean;
  forward_catering_requests_to_text_link: boolean;
  forward_event_requests_to_store: boolean;
  forward_event_requests_to_text_link: boolean;
  catering_outreach_contact: string;
  catering_forwarding_number: string;
  catering_forwarding_number_ext: string;
  catering_link: string;
  event_forwarding_number: string;
  event_forwarding_number_ext: string;
  event_inquiry_questions: string[];
  catering_inquiry_questions: string[];
  parking_info: string;
  business_number: string;
  show_sms_on_dashboard: boolean;
  manual_menu_snoozing_enabled: boolean;
  cross_sell_enabled: boolean;
  forward_late_reservations_to_text: boolean;
  forward_reservation_cancelation_failures_to_text: boolean;
}

interface OnboardingWeeklyHour {
  day_of_week: string;
  meal_period: string;
  is_closed: boolean;
  opening_time: string;
  closing_time: string;
}

type StoreBooleanField = keyof StoreFormData & string;

const MAX_CUSTOM_EVENT_INQUIRY_QUESTIONS = 7;

const normalizeEventInquiryQuestions = (questions: unknown): string[] => {
  if (!Array.isArray(questions)) return [];
  return questions
    .filter((question): question is string => typeof question === 'string')
    .map((question) => question.trim())
    .filter(Boolean)
    .slice(0, MAX_CUSTOM_EVENT_INQUIRY_QUESTIONS);
};

const MAX_BOOSTED_KEYWORDS = 10;

const normalizeBoostedKeywords = (keywords: unknown): string[] => {
  if (!Array.isArray(keywords)) return [];
  const seen = new Set<string>();
  const cleaned: string[] = [];
  keywords
    .filter((keyword): keyword is string => typeof keyword === 'string')
    .map((keyword) => keyword.trim())
    .filter(Boolean)
    .forEach((keyword) => {
      const dedupeKey = keyword.toLowerCase();
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      cleaned.push(keyword);
    });
  return cleaned.slice(0, MAX_BOOSTED_KEYWORDS);
};

const getDefaultCallIntroSentence = (storeName?: string | null): string => {
  const displayName = storeName?.trim() || 'the store';
  return `Hello, this is ${displayName}. How may I assist you today?`;
};

interface CallIntroMessageFieldProps {
  customName: string;
  customValue: string;
  defaultIntro: string;
  useDefault: boolean;
  onCustomChange: React.ChangeEventHandler<HTMLTextAreaElement>;
  onUseDefaultChange: (_useDefault: boolean) => void;
}

const CallIntroMessageField: React.FC<CallIntroMessageFieldProps> = ({
  customName,
  customValue,
  defaultIntro,
  useDefault,
  onCustomChange,
  onUseDefaultChange,
}) => {
  return (
    <div className="space-y-3">
      <Label>Call intro message</Label>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <button
          type="button"
          onClick={() => onUseDefaultChange(true)}
          className={`rounded-xl border p-4 text-left transition ${
            useDefault
              ? 'border-brand-300 bg-brand-50 text-brand-800 dark:border-brand-800/70 dark:bg-brand-900/20 dark:text-brand-200'
              : 'border-gray-200 bg-gray-50 text-gray-500 opacity-70 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-400'
          }`}
        >
          <div className="mb-2 flex items-center gap-2">
            <span
              className={`h-3 w-3 rounded-full border ${
                useDefault ? 'border-brand-500 bg-brand-500' : 'border-gray-400'
              }`}
            />
            <span className="text-sm font-semibold">Default intro</span>
          </div>
        </button>
        <button
          type="button"
          onClick={() => onUseDefaultChange(false)}
          className={`rounded-xl border p-4 text-left transition ${
            !useDefault
              ? 'border-brand-300 bg-brand-50 text-brand-800 dark:border-brand-800/70 dark:bg-brand-900/20 dark:text-brand-200'
              : 'border-gray-200 bg-gray-50 text-gray-500 opacity-70 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-400'
          }`}
        >
          <div className="mb-2 flex items-center gap-2">
            <span
              className={`h-3 w-3 rounded-full border ${
                !useDefault
                  ? 'border-brand-500 bg-brand-500'
                  : 'border-gray-400'
              }`}
            />
            <span className="text-sm font-semibold">Custom intro</span>
          </div>
        </button>
      </div>
      <div className={useDefault ? 'opacity-60' : ''}>
        <textarea
          name={customName}
          value={useDefault ? defaultIntro : customValue}
          onChange={onCustomChange}
          disabled={useDefault}
          rows={3}
          placeholder="Thanks for calling, how can I help?"
          className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-base text-gray-800 shadow-theme-xs placeholder:text-gray-400 disabled:cursor-not-allowed focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
        />
      </div>
    </div>
  );
};

interface StoreBooleanSection {
  title: string;
  options: Array<{ field: StoreBooleanField; label: string }>;
  inputs?: Array<{
    field: StoreBooleanField;
    label: string;
    type?: 'text' | 'number' | 'tel' | 'email' | 'url' | 'select';
    placeholder?: string;
    min?: string;
    step?: number;
    options?: Array<{ value: string; label: string }>;
  }>;
}

const STORE_BOOLEAN_SECTIONS: StoreBooleanSection[] = [
  {
    title: 'Order Options',
    options: [
      { field: 'accepts_phone_orders', label: 'Accepts Phone Orders' },
      { field: 'accepts_deliveries', label: 'Accepts Deliveries' },
      {
        field: 'forward_order_requests_to_store',
        label: 'Forward Orders to Store',
      },
      {
        field: 'forward_order_requests_to_text_link',
        label: 'Forward Orders to Text Link',
      },
      {
        field: 'forward_order_requests_to_phone',
        label: 'Forward Orders to Phone',
      },
    ],
    inputs: [
      {
        field: 'order_requests_phone_number',
        label: 'Order Requests Phone Number',
        type: 'tel',
        placeholder: '+1 555-5555',
      },
      {
        field: 'order_requests_phone_number_ext',
        label: 'Order Requests Extension',
        placeholder: '123#',
      },
    ],
  },
  {
    title: 'Catering Options',
    options: [
      { field: 'takes_catering', label: 'Takes Catering' },
      { field: 'agent_handles_catering', label: 'Agent Handles Catering' },
      {
        field: 'forward_catering_requests_to_store',
        label: 'Forward Catering Requests to Store',
      },
      {
        field: 'forward_catering_requests_to_text_link',
        label: 'Forward Catering Requests to Text Link',
      },
    ],
    inputs: [
      {
        field: 'catering_link',
        label: 'Catering Link',
        type: 'url',
        placeholder: 'https://example.com/catering',
      },
      {
        field: 'catering_outreach_contact',
        label: 'Catering Outreach Contact',
        placeholder: 'Phone or contact for SMS',
      },
      {
        field: 'catering_forwarding_number',
        label: 'Catering Forwarding Number',
        placeholder: 'Forwarding number',
      },
      {
        field: 'catering_forwarding_number_ext',
        label: 'Catering Extension',
        placeholder: '123#',
      },
    ],
  },
  {
    title: 'Events Options',
    options: [
      { field: 'takes_events', label: 'Takes Events' },
      { field: 'agent_handles_events', label: 'Agent Handles Events' },
      {
        field: 'forward_event_requests_to_store',
        label: 'Forward Event Requests to Store',
      },
      {
        field: 'forward_event_requests_to_text_link',
        label: 'Forward Event Requests to Text Link',
      },
    ],
    inputs: [
      {
        field: 'event_forwarding_number',
        label: 'Event Forwarding Number',
        placeholder: 'Forwarding number',
      },
      {
        field: 'event_forwarding_number_ext',
        label: 'Event Extension',
        placeholder: '123#',
      },
    ],
  },
  {
    title: 'Reservation & Seating Options',
    options: [
      { field: 'takes_reservations', label: 'Takes Reservations' },
      { field: 'outdoor_seating', label: 'Outdoor Seating' },
      {
        field: 'cross_sell_enabled',
        label: 'Enable Cross-Selling (legacy fallback)',
      },
      {
        field: 'annotate_opentable_reservation_notes',
        label: 'Annotate OpenTable Reservation Notes',
      },
      {
        field: 'forward_late_reservations_to_text',
        label: 'Forward Late Reservations to Text',
      },
      {
        field: 'forward_reservation_cancelation_failures_to_text',
        label: 'Forward Reservation Cancellation Failures to Text',
      },
    ],
    inputs: [
      {
        field: 'ot_max_party_size',
        label: 'OpenTable Max Party Size',
        type: 'number',
        placeholder: 'Leave blank if N/A',
        min: '0',
        step: 1,
      },
      {
        field: 'average_reservation_cover',
        label: 'Average Reservation Cover',
        placeholder: 'Dollars per guest, e.g. 45',
      },
      {
        field: 'google_price_level',
        label: 'Google Price Level',
        placeholder: '$, $$, $$$, or $$$$',
      },
      {
        field: 'opentable_reservation_note_annotation_position',
        label: 'Reservation Annotation Position',
        type: 'select',
        options: [
          { value: 'prepend', label: 'Prepend' },
          { value: 'append', label: 'Append' },
        ],
      },
      {
        field: 'opentable_reservation_note_annotation',
        label: 'Reservation Annotation Message',
        placeholder: '[agent]',
      },
    ],
  },
  {
    title: 'Payment & Fee Options',
    options: [
      { field: 'collect_payment_online', label: 'Collect Payment Online' },
      { field: 'accept_pay_on_site', label: 'Accept Pay on Site' },
      {
        field: 'pass_through_dispatch_quote',
        label: 'Pass Through Dispatch Quote',
      },
      {
        field: 'send_delivery_cost_to_deliverect',
        label: 'Send Delivery Cost to Deliverect',
      },
      { field: 'assess_tax_on_fees', label: 'Assess Tax on Fees' },
      { field: 'make_fees_tax_inclusive', label: 'Make Fees Tax Inclusive' },
    ],
    inputs: [
      {
        field: 'fallback_tax_percent',
        label: 'Fallback Tax Percent',
        placeholder: 'Blank charges 13% HST in Ontario. 0 leaves fallback off.',
      },
    ],
  },
  {
    title: 'Store & POS Options',
    options: [
      { field: 'livemode', label: 'Live Mode' },
      { field: 'posless_integration', label: 'POS-less Integration' },
      { field: 'human_transfer_enabled', label: 'Human Transfer Enabled' },
      {
        field: 'collect_email',
        label: 'Collect Email for Replies from Store',
      },
      { field: 'show_sms_on_dashboard', label: 'Show SMS on Dashboard' },
      {
        field: 'manual_menu_snoozing_enabled',
        label: 'Enable Manual Menu Snoozing',
      },
    ],
  },
];

const adminPrimaryButtonClassName =
  'inline-flex items-center justify-center rounded-2xl bg-[rgb(var(--app-brand))] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgb(var(--app-brand-strong))] focus:outline-none focus:ring-4 focus:ring-[rgb(var(--app-brand)/0.14)] disabled:cursor-not-allowed disabled:opacity-50';

const adminTableShellClassName =
  'relative overflow-hidden rounded-[24px] border border-[rgb(var(--app-border)/0.72)] bg-[rgb(var(--app-surface)/0.74)]';

const adminTableHeaderClassName =
  'border-b border-[rgb(var(--app-border)/0.72)] bg-[rgb(var(--app-surface-muted)/0.72)]';

const STORE_TIMEZONE_OPTIONS = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Toronto',
  'America/Vancouver',
  'America/Montreal',
  'America/Edmonton',
  'America/Winnipeg',
  'America/Halifax',
  'America/St_Johns',
  'America/Regina',
];

const STORE_COUNTRY_OPTIONS = [
  { value: 'US', label: 'US' },
  { value: 'CA', label: 'CA' },
  { value: 'UK', label: 'UK' },
];

const ONBOARDING_DAY_ORDER = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const DEFAULT_ONBOARDING_MEAL_PERIOD = 'ALL_DAY';

const createDefaultOnboardingHours = (): OnboardingWeeklyHour[] =>
  ONBOARDING_DAY_ORDER.map((day) => ({
    day_of_week: day,
    meal_period: DEFAULT_ONBOARDING_MEAL_PERIOD,
    is_closed: false,
    opening_time: '11:00',
    closing_time: '22:00',
  }));

const toOnboardingStoreHourPayload = (row: OnboardingWeeklyHour) => ({
  day_of_week: row.day_of_week,
  meal_period: row.meal_period || DEFAULT_ONBOARDING_MEAL_PERIOD,
  is_closed: row.is_closed,
  opening_time: row.is_closed ? null : `${row.opening_time}:00`,
  closing_time: row.is_closed ? null : `${row.closing_time}:00`,
});

const MANUAL_ASSISTANT_ID_OPTION = '__manual__';

// Retell create-retell-llm `model` values (text LLM, not s2s_model).
const RETELL_LLM_MODELS = [
  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4.1-nano',
  'gpt-5',
  'gpt-5-mini',
  'gpt-5-nano',
  'gpt-5.1',
  'gpt-5.2',
  'gpt-5.4',
  'gpt-5.4-mini',
  'gpt-5.4-nano',
  'gpt-5.5',
  'gpt-5.6-terra',
  'gpt-5.6-luna',
  'claude-4.5-sonnet',
  'claude-4.6-sonnet',
  'claude-5-sonnet',
  'claude-4.5-haiku',
  'gemini-3.0-flash',
  'gemini-3.1-flash-lite',
  'gemini-3.5-flash',
  'gemini-3.5-flash-lite',
  'gemini-3.6-flash',
  'gemini-3.7-flash',
  'gemini-3.8-flash',
] as const;

const RetellLlmModelField: React.FC<{
  id: string;
  value: string;
  onChange: (_e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}> = ({ id, value, onChange, disabled }) => (
  <div>
    <Label>Retell LLM Model</Label>
    <Input
      type="text"
      id={id}
      name="retell_llm_model"
      list={`${id}-options`}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder="e.g., gpt-4.1"
    />
    <datalist id={`${id}-options`}>
      {RETELL_LLM_MODELS.map((model) => (
        <option key={model} value={model} />
      ))}
    </datalist>
    <p className="mt-1 text-xs text-gray-500">
      Optional. Overrides the Retell text LLM on inbound calls. Leave blank to
      keep the agent&apos;s saved model (Retell default is gpt-4.1).
    </p>
  </div>
);

const numberInputWithoutStepperClassName =
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

function KnowledgeBaseSelect({
  value,
  onChange,
  options,
  loading,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  loading: boolean;
  error: string;
}) {
  return (
    <div>
      <Label>Knowledge Base</Label>
      <SearchableSelect
        options={options}
        value={value}
        onChange={onChange}
        placeholder={
          loading ? 'Loading knowledge bases...' : 'Select a knowledge base'
        }
        disabled={loading && options.length === 0}
      />
      {error ? (
        <p className="mt-1 text-xs text-error-500">{error}</p>
      ) : (
        <p className="mt-1 text-xs text-gray-500">
          Search by name. Leave blank to keep the agent&apos;s default knowledge
          base.
        </p>
      )}
    </div>
  );
}

/** Ascending, then descending, then back to the unsorted catalog order. */
function cyclePlanSort<Key extends string>(
  current: TableSortState<Key>,
  key: Key,
): TableSortState<Key> {
  if (current.key !== key) return { key, direction: 'asc' };
  if (current.direction === 'asc') return { key, direction: 'desc' };
  return { key: null, direction: 'asc' };
}

export default function PlatformAdmin() {
  const { user, accessToken, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const routeFallback = useMemo(
    () => readPlatformAdminFallback(),
    [location.pathname],
  );
  const resolvedRoute = useMemo(
    () => resolvePlatformAdminRoute(location.pathname, routeFallback),
    [location.pathname, routeFallback],
  );
  const {
    tab: activeTab,
    onboardingSubtab: onboardingBillingSubtab,
    twilioSubtab: twilioConfigSubtab,
    plansSubtab: plansSubTab,
    integrationSubtab,
    posSubtab,
    notificationsSubtab,
  } = resolvedRoute.state;

  const goToAdminRoute = useCallback(
    (next: Partial<PlatformAdminRouteState> & { tab: AdminTab }) => {
      const path = buildPlatformAdminPath({
        ...resolvedRoute.state,
        ...next,
      });
      if (
        path === location.pathname.replace(/\/+$/, '') ||
        path === location.pathname
      ) {
        return;
      }
      navigate({ pathname: path, search: location.search });
    },
    [location.pathname, location.search, navigate, resolvedRoute.state],
  );

  useEffect(() => {
    if (
      resolvedRoute.redirectTo &&
      resolvedRoute.redirectTo !== location.pathname
    ) {
      navigate(
        { pathname: resolvedRoute.redirectTo, search: location.search },
        { replace: true },
      );
      return;
    }
    writePlatformAdminSession(resolvedRoute.state);
  }, [location.pathname, location.search, navigate, resolvedRoute]);

  const setActiveTab = useCallback(
    (tab: AdminTab) => {
      goToAdminRoute({ tab });
    },
    [goToAdminRoute],
  );
  const setOnboardingBillingSubtab = useCallback(
    (subtab: OnboardingBillingSubtab) => {
      goToAdminRoute({
        tab: 'onboardingBilling',
        onboardingSubtab: subtab,
        plansSubtab: subtab === 'plans' ? plansSubTab : 'catalog',
      });
    },
    [goToAdminRoute, plansSubTab],
  );
  const setPlansSubTab = useCallback(
    (subtab: PlansSubTab) => {
      goToAdminRoute({
        tab: 'onboardingBilling',
        onboardingSubtab: 'plans',
        plansSubtab: subtab,
      });
    },
    [goToAdminRoute],
  );
  const setActiveTwilioConfigSubtab = useCallback(
    (subtab: TwilioConfigSubtab) => {
      goToAdminRoute({ tab: 'tsv', twilioSubtab: subtab });
    },
    [goToAdminRoute],
  );
  const setIntegrationSubtab = useCallback(
    (subtab: IntegrationSubTab) => {
      goToAdminRoute({ tab: 'integrations', integrationSubtab: subtab });
    },
    [goToAdminRoute],
  );
  const setPosSubtab = useCallback(
    (subtab: PosConfigSubtab) => {
      goToAdminRoute({ tab: 'posConfig', posSubtab: subtab });
    },
    [goToAdminRoute],
  );
  const setNotificationsSubtab = useCallback(
    (subtab: NotificationSubTab) => {
      goToAdminRoute({ tab: 'notifications', notificationsSubtab: subtab });
    },
    [goToAdminRoute],
  );

  const [stores, setStores] = useState<Store[]>([]);
  const [allStoresForDropdowns, setAllStoresForDropdowns] = useState<Store[]>(
    [],
  );
  const storeDropdownSource = allStoresForDropdowns.length
    ? allStoresForDropdowns
    : stores;
  const storeOptions = useMemo(
    () =>
      storeDropdownSource.map((s) => ({
        value: s.id,
        label: `${s.name} (ID: ${s.id})`,
      })),
    [storeDropdownSource],
  );
  const storeOptionsNameOnly = useMemo(
    () => storeDropdownSource.map((s) => ({ value: s.id, label: s.name })),
    [storeDropdownSource],
  );
  const [loadingStores, setLoadingStores] = useState(false);
  const [storeGroups, setStoreGroups] = useState<StoreGroup[]>([]);
  const [loadingStoreGroups, setLoadingStoreGroups] = useState(false);
  const storeGroupOptions = useMemo(
    () => storeGroups.map((g) => ({ value: g.id, label: g.name })),
    [storeGroups],
  );
  const [storeGroupUsers, setStoreGroupUsers] = useState<StoreGroupUser[]>([]);
  const [loadingStoreGroupUsers, setLoadingStoreGroupUsers] = useState(false);
  const [storeUsers, setStoreUsers] = useState<any[]>([]);
  const [loadingStoreUsers, setLoadingStoreUsers] = useState(false);
  const storeUserOptions = useMemo(
    () =>
      storeUsers.map((u: any) => ({
        value: u.id,
        label:
          u.email ||
          `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
          `User ID: ${u.id}`,
      })),
    [storeUsers],
  );
  const ONBOARDING_TOTAL_STEPS = 4;
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [storeTableMenuId, setStoreTableMenuId] = useState<number | null>(null);
  const [isEditStoreModalOpen, setIsEditStoreModalOpen] = useState(false);

  // Plans Tab State
  const [plans, setPlans] = useState<any[]>([]);
  const [planSearch, setPlanSearch] = useState('');
  const [planSort, setPlanSort] = useState<
    TableSortState<
      'name' | 'price' | 'minutes' | 'overage' | 'setup' | 'status'
    >
  >({ key: null, direction: 'asc' });
  const visiblePlans = useMemo(
    () =>
      filterAndSortRows(
        plans,
        planSearch,
        (plan) =>
          rowSearchText([
            plan.name,
            plan.code,
            plan.currency,
            plan.base_price_cents,
            plan.included_minutes,
            plan.is_active ? 'Active' : 'Inactive',
          ]),
        planSort,
        (plan, key) => {
          if (key === 'name') return plan.name || plan.code;
          if (key === 'price') return plan.base_price_cents;
          if (key === 'minutes') return plan.included_minutes;
          if (key === 'overage') return plan.overage_rate_cents_per_minute;
          if (key === 'setup') return plan.setup_fee_cents;
          return plan.is_active ? 'Active' : 'Inactive';
        },
      ),
    [planSearch, planSort, plans],
  );
  const planCatalogOrder = !planSearch.trim() && planSort.key == null;
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [planSuccess, setPlanSuccess] = useState<string | null>(null);
  const [reorderLoading, setReorderLoading] = useState(false);

  const [planFormData, setPlanFormData] = useState({
    code: '',
    name: '',
    description: '',
    currency: 'USD',
    base_price_cents: 0,
    included_minutes: 0,
    overage_rate_cents_per_minute: 0,
    setup_fee_cents: 0,
    per_order_rate_cents: 0,
    multilingual_addon_cents: 0,
    multilingual_included: false,
    sms_ordering_addon_cents: 0,
    sms_ordering_included: false,
    opentable_resy_addon_cents: 0,
    opentable_resy_included: false,
    is_active: true,
    visible_on_plan_page: true,
    trial_period_days: '' as number | string,
    feature_point_1: '',
    feature_point_2: '',
    feature_point_3: '',
    feature_point_4: '',
    feature_point_5: '',
    feature_point_6: '',
    feature_point_7: '',
    feature_point_8: '',
  });

  // Custom pricing state
  const [selectedCustomStoreId, setSelectedCustomStoreId] = useState<
    number | null
  >(null);
  const [selectedCustomPlanCode, setSelectedCustomPlanCode] =
    useState<string>('');
  const [customPricingSource, setCustomPricingSource] = useState<
    'default' | 'custom'
  >('default');
  const [customPricingNote, setCustomPricingNote] = useState('');
  const [contractStart, setContractStart] = useState('');
  const [contractEnd, setContractEnd] = useState('');

  // Custom price/add-on fields
  const [customBasePriceCents, setCustomBasePriceCents] = useState(0);
  const [customIncludedMinutes, setCustomIncludedMinutes] = useState(0);
  const [customOverageRateCents, setCustomOverageRateCents] = useState(0);
  const [customSetupFeeCents, setCustomSetupFeeCents] = useState(0);
  const [customPerOrderRateCents, setCustomPerOrderRateCents] = useState(0);

  const [customMultilingualCents, setCustomMultilingualCents] = useState(0);
  const [customMultilingualIncluded, setCustomMultilingualIncluded] =
    useState(false);
  const [customMultilingualActive, setCustomMultilingualActive] =
    useState(false);

  const [customSmsOrderingCents, setCustomSmsOrderingCents] = useState(0);
  const [customSmsOrderingIncluded, setCustomSmsOrderingIncluded] =
    useState(false);
  const [customSmsOrderingActive, setCustomSmsOrderingActive] = useState(false);

  const [customOpentableResyCents, setCustomOpentableResyCents] = useState(0);
  const [customOpentableResyIncluded, setCustomOpentableResyIncluded] =
    useState(false);
  const [customOpentableResyActive, setCustomOpentableResyActive] =
    useState(false);

  const [customPricingLoading, setCustomPricingLoading] = useState(false);
  const [customPricingError, setCustomPricingError] = useState<string | null>(
    null,
  );
  const [customPricingSuccess, setCustomPricingSuccess] = useState<
    string | null
  >(null);

  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const PAGE_SIZE = 20;
  const [storesPage, setStoresPage] = useState(1);
  const [storesTotalPages, setStoresTotalPages] = useState(1);
  const [storesTotalCount, setStoresTotalCount] = useState(0);
  const [storesSearch, setStoresSearch] = useState('');
  const debouncedStoresSearch = useDebouncedValue(storesSearch.trim());
  const [storesSort, setStoresSort] = useState<TableSortState<StoreSortKey>>({
    key: null,
    direction: 'asc',
  });
  const storesOrdering = toOrderingParam(storesSort, STORE_SORT_KEY_TO_FIELD);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersTotalCount, setUsersTotalCount] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const debouncedUsersSearch = useDebouncedValue(usersSearch.trim());
  const [usersSort, setUsersSort] = useState<TableSortState<StoreUserSortKey>>({
    key: null,
    direction: 'asc',
  });
  const usersOrdering = toOrderingParam(
    usersSort,
    STORE_USER_SORT_KEY_TO_FIELD,
  );
  const [assistantsPage, setAssistantsPage] = useState(1);
  const [assistantsSearch, setAssistantsSearch] = useState('');
  const debouncedAssistantsSearch = useDebouncedValue(assistantsSearch.trim());
  const [assistantsSort, setAssistantsSort] = useState<
    TableSortState<AgentSortKey>
  >({ key: null, direction: 'asc' });
  const assistantsOrdering = toOrderingParam(
    assistantsSort,
    AGENT_SORT_KEY_TO_FIELD,
  );
  const [assistantsTotalPages, setAssistantsTotalPages] = useState(1);
  const [assistantsTotalCount, setAssistantsTotalCount] = useState(0);

  const adminOrders = useAdminOrders(activeTab === 'adminOrders');

  const adminCalls = useAdminCalls(activeTab === 'adminCalls');

  const sentNotifications = useAdminSentNotifications(
    activeTab === 'notifications' && notificationsSubtab === 'sent',
  );

  const adminReservationsList = useAdminReservationsList(
    activeTab === 'adminReservations',
  );

  const [obStep, setObStep] = useState(0);
  const [obCreatedStoreId, setObCreatedStoreId] = useState<number | null>(null);
  const [obCreatedStoreName, setObCreatedStoreName] = useState('');
  const [obError, setObError] = useState('');
  const [obSuccess, setObSuccess] = useState('');
  const [obSubmitting, setObSubmitting] = useState(false);
  const [intakePickerOpen, setIntakePickerOpen] = useState(false);
  const [intakePickerTarget, setIntakePickerTarget] =
    useState<OnboardingPrefillTarget>('store');
  const [intakePickerOptions, setIntakePickerOptions] = useState<
    OnboardingIntake[]
  >([]);
  const [intakePickerSelectedId, setIntakePickerSelectedId] = useState('');
  const [intakePickerOverwrite, setIntakePickerOverwrite] = useState(false);
  const [intakePickerLoading, setIntakePickerLoading] = useState(false);
  const [intakePickerError, setIntakePickerError] = useState('');

  const intakePickerInUsersTab =
    activeTab === 'user' &&
    intakePickerTarget === 'owner_user' &&
    !isOnboardingOpen;
  const [selectedStoreIntakeId, setSelectedStoreIntakeId] = useState<
    number | null
  >(null);
  const [obWeeklyHours, setObWeeklyHours] = useState<OnboardingWeeklyHour[]>(
    createDefaultOnboardingHours,
  );

  const [obUserForm, setObUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'Owner' as 'Manager' | 'Owner',
  });

  const [obAssistantForm, setObAssistantForm] = useState({
    assistant_id: '',
    assistant_provider: 'retell',
    phone_number: '',
    retell_llm_model: '',
    language: 'en',
    is_active: true,
  });
  const [obSelectedRetellAgentId, setObSelectedRetellAgentId] = useState(
    MANUAL_ASSISTANT_ID_OPTION,
  );

  const handleStartOnboarding = useCallback(() => {
    setObStep(0);
    setObCreatedStoreId(null);
    setObCreatedStoreName('');
    setSelectedStoreIntakeId(null);
    setObError('');
    setObSuccess('');
    setObSubmitting(false);
    setObWeeklyHours(createDefaultOnboardingHours());
    setObUserForm({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      role: 'Owner',
    });
    setObAssistantForm({
      assistant_id: '',
      assistant_provider: 'retell',
      phone_number: '',
      retell_llm_model: '',
      language: 'en',
      is_active: true,
    });
    setObSelectedRetellAgentId(MANUAL_ASSISTANT_ID_OPTION);
    setIsOnboardingOpen(true);
  }, []);

  const handleCloseOnboarding = useCallback(() => {
    setIsOnboardingOpen(false);
  }, []);

  const obNext = useCallback(() => {
    setObError('');
    setObSuccess('');
    setObStep((prev) => Math.min(prev + 1, ONBOARDING_TOTAL_STEPS));
  }, []);

  const obPrev = useCallback(() => {
    setObError('');
    setObSuccess('');
    setObStep((prev) => Math.max(0, prev - 1));
  }, []);

  const handleObUserChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setObUserForm((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleObAssistantChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      setObAssistantForm((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    },
    [],
  );

  const handleObRetellAgentSelect = useCallback((agentId: string) => {
    setObSelectedRetellAgentId(agentId);
    setObAssistantForm((prev) => ({
      ...prev,
      assistant_id:
        agentId === MANUAL_ASSISTANT_ID_OPTION ? '' : agentId.trim(),
    }));
  }, []);

  const fetchIntakePickerOptions = useCallback(async () => {
    setIntakePickerLoading(true);
    setIntakePickerError('');
    try {
      const response = await api.get(apiEndpoints.getOnboardingIntakes());
      if (response?.ok) {
        const data = await response.json();
        const intakes = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : Array.isArray(data?.intakes)
              ? data.intakes
              : [];
        setIntakePickerOptions(intakes);
      } else {
        setIntakePickerOptions([]);
        setIntakePickerError('Failed to load onboarding intakes.');
      }
    } catch {
      setIntakePickerOptions([]);
      setIntakePickerError('Failed to load onboarding intakes.');
    } finally {
      setIntakePickerLoading(false);
    }
  }, []);

  const openIntakePicker = useCallback(
    (target: OnboardingPrefillTarget, intake?: OnboardingIntake | null) => {
      setIntakePickerTarget(target);
      setIntakePickerSelectedId(intake ? String(intake.id) : '');
      setIntakePickerOverwrite(false);
      setIntakePickerError('');
      setIntakePickerOpen(true);
      fetchIntakePickerOptions();
    },
    [fetchIntakePickerOptions],
  );

  const updateObWeeklyHour = useCallback(
    (day: string, patch: Partial<OnboardingWeeklyHour>) => {
      setObWeeklyHours((prev) =>
        prev.map((row) =>
          row.day_of_week === day ? { ...row, ...patch } : row,
        ),
      );
    },
    [],
  );

  // Store form state
  const [storeFormData, setStoreFormData] = useState<StoreFormData>({
    name: '',
    phonetic_name: '',
    call_intro_sentence: '',
    speach_filler_phrase: 'Sure, one moment please',
    vip_intro_phrase:
      'Hello {{vip_caller_name}}! Thanks for being a valued member at {{store_name}}.',
    agent_style: '',
    use_default_call_intro_sentence: true,
    legal_name: '',
    timezone: 'America/New_York',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    website: '',
    menu_link: '',
    knowledge_base_id: '',
    ordering_link: '',
    stripe_account_id: '',
    reservation_link: '',
    gift_card_link: '',
    reservation_email: '',
    default_delivery_fee_cents: 0,
    pass_through_dispatch_quote: true,
    send_delivery_cost_to_deliverect: true,
    assess_tax_on_fees: false,
    make_fees_tax_inclusive: false,
    fallback_tax_percent: '',
    application_fee_default_cents: 50,
    delivery_fee_label: '',
    service_fee_label: '',
    default_delivery_mode: 'none',
    takes_reservations: false,
    takes_catering: false,
    takes_events: false,
    agent_handles_events: false,
    agent_handles_catering: false,
    outdoor_seating: false,
    collect_payment_online: true,
    accept_pay_on_site: false,
    location_secondary_phone_number: '',
    location_secondary_phone_number_ext: '',
    monthly_minutes_limit: 500,
    ot_max_party_size: '',
    average_reservation_cover: '',
    google_price_level: '',
    explicit_only_table_types: [],
    annotate_opentable_reservation_notes: false,
    opentable_reservation_note_annotation_position: 'prepend',
    opentable_reservation_note_annotation: '',
    livemode: false,
    store_group: null,
    store_group_name: '',
    logo: null,
    accepts_deliveries: false,
    how_to_order_delivery_message: '',
    accepts_phone_orders: true,
    collect_email: false,
    posless_integration: false,
    ...humanTransferFormFields(),
    current_pos_system: 'UNKNOWN',
    poshub_toast_group_id: '',
    forward_order_requests_to_store: false,
    forward_order_requests_to_text_link: false,
    forward_order_requests_to_phone: false,
    order_requests_phone_number: '',
    order_requests_phone_number_ext: '',
    forward_catering_requests_to_store: false,
    forward_catering_requests_to_text_link: false,
    forward_event_requests_to_store: false,
    forward_event_requests_to_text_link: false,
    catering_outreach_contact: '',
    catering_forwarding_number: '',
    catering_forwarding_number_ext: '',
    catering_link: '',
    event_forwarding_number: '',
    event_forwarding_number_ext: '',
    event_inquiry_questions: [],
    catering_inquiry_questions: [],
    parking_info: '',
    business_number: '',
    show_sms_on_dashboard: false,
    manual_menu_snoozing_enabled: false,
    cross_sell_enabled: true,
    forward_late_reservations_to_text: false,
    forward_reservation_cancelation_failures_to_text: false,
  });
  const [storeError, setStoreError] = useState<string>('');
  const [storeSuccess, setStoreSuccess] = useState<string>('');

  // Edit Store form state
  const [editStoreId, setEditStoreId] = useState<number | null>(null);
  const [editStoreFormData, setEditStoreFormData] = useState<any | null>(null);
  const [editStoreBaseline, setEditStoreBaseline] = useState<any | null>(null);
  const [editStoreLoading, setEditStoreLoading] = useState(false);
  const [editStoreError, setEditStoreError] = useState<string>('');
  const [editStoreSuccess, setEditStoreSuccess] = useState<string>('');
  const [editStoreLogo, setEditStoreLogo] = useState<File | null>(null);
  const [editStoreCurrentLogoUrl, setEditStoreCurrentLogoUrl] =
    useState<string>('');
  const [editStoreLogoPreviewUrl, setEditStoreLogoPreviewUrl] =
    useState<string>('');
  const [isEditConfirmOpen, setIsEditConfirmOpen] = useState(false);
  const [editStorePendingChanges, setEditStorePendingChanges] = useState<
    Array<{ key: string; label: string; from: any; to: any }>
  >([]);
  // Transfer numbers that were the same as the old secondary phone, and the
  // ones the admin chose to move to the new number.
  const [secondaryFollowers, setSecondaryFollowers] = useState<
    SecondaryFollowerKey[]
  >([]);
  const [secondaryFollowersToUpdate, setSecondaryFollowersToUpdate] = useState<
    SecondaryFollowerKey[]
  >([]);

  const [userFormData, setUserFormData] = useState<UserFormData>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'Manager',
    store_id: 0,
  });
  const [userSuccess, setUserSuccess] = useState<string>('');

  // TSV form state
  const [tsvFormData, setTsvFormData] = useState({
    phone_sid: '',
  });
  const [tsvError, setTsvError] = useState<string>('');
  const [tsvSuccess, setTsvSuccess] = useState<string>('');
  const [submittingTsv, setSubmittingTsv] = useState(false);

  // Twilio configuration form state
  const [twilioConfigFormData, setTwilioConfigFormData] = useState({
    phone_sid: '',
  });
  const [twilioConfigError, setTwilioConfigError] = useState<string>('');
  const [twilioConfigSuccess, setTwilioConfigSuccess] = useState<string>('');
  const [submittingTwilioConfig, setSubmittingTwilioConfig] = useState(false);

  const [tsvRefreshError, setTsvRefreshError] = useState<string>('');
  const [refreshingTsvStatus, setRefreshingTsvStatus] = useState(false);

  const [retellAgents, setRetellAgents] = useState<any[]>([]);
  const [loadingRetellAgents, setLoadingRetellAgents] = useState(false);
  const [retellKnowledgeBases, setRetellKnowledgeBases] = useState<any[]>([]);
  const [loadingRetellKnowledgeBases, setLoadingRetellKnowledgeBases] =
    useState(false);
  const [retellKnowledgeBaseError, setRetellKnowledgeBaseError] = useState('');
  const [approvedUnassignedPhoneNumbers, setApprovedUnassignedPhoneNumbers] =
    useState<any[]>([]);
  const [
    loadingApprovedUnassignedPhoneNumbers,
    setLoadingApprovedUnassignedPhoneNumbers,
  ] = useState(false);
  const retellAgentOptions = useMemo(() => {
    const options = retellAgents
      .map((agent: any) => {
        const agentId = String(
          agent.agent_id || agent.assistant_id || agent.id || '',
        );
        if (!agentId) return null;

        const name =
          agent.agent_name ||
          agent.name ||
          agent.nickname ||
          agent.display_name ||
          'Retell Agent';

        return {
          value: agentId,
          label: `${name} (${agentId})`,
        };
      })
      .filter(
        (
          option,
        ): option is {
          value: string;
          label: string;
        } => option !== null,
      );

    return [{ value: MANUAL_ASSISTANT_ID_OPTION, label: 'none' }, ...options];
  }, [retellAgents]);
  const retellKnowledgeBaseOptions = useMemo(() => {
    const options = retellKnowledgeBases
      .map((knowledgeBase: any) => {
        const knowledgeBaseId = String(
          knowledgeBase.knowledge_base_id || knowledgeBase.id || '',
        ).trim();
        if (!knowledgeBaseId) return null;
        const name = String(
          knowledgeBase.knowledge_base_name ||
            knowledgeBase.name ||
            'Knowledge base',
        ).trim();
        const status = String(knowledgeBase.status || '').trim();
        const statusLabel =
          status && status !== 'complete'
            ? ` · ${status.replace(/_/g, ' ')}`
            : '';
        return {
          value: knowledgeBaseId,
          label: `${name}${statusLabel} (${knowledgeBaseId})`,
        };
      })
      .filter(
        (
          option,
        ): option is {
          value: string;
          label: string;
        } => option !== null,
      )
      .sort((left, right) => left.label.localeCompare(right.label));

    const known = new Set(options.map((option) => option.value));
    const savedIds = [
      storeFormData.knowledge_base_id,
      editStoreFormData?.knowledge_base_id,
    ]
      .map((value) => String(value || '').trim())
      .filter(
        (value, index, all) =>
          value && !known.has(value) && all.indexOf(value) === index,
      );
    savedIds.forEach((knowledgeBaseId) => {
      options.push({
        value: knowledgeBaseId,
        label: `${knowledgeBaseId} (saved on this store)`,
      });
    });
    return options;
  }, [
    editStoreFormData?.knowledge_base_id,
    retellKnowledgeBases,
    storeFormData.knowledge_base_id,
  ]);
  const approvedUnassignedPhoneNumberOptions = useMemo(
    () =>
      approvedUnassignedPhoneNumbers
        .map((number: any) => {
          const phoneNumber = String(
            number.phone_number || number.number || number.e164 || '',
          );
          if (!phoneNumber) return null;

          const labelParts = [
            phoneNumber,
            number.friendly_name,
            number.phone_number_sid || number.sid,
          ].filter(Boolean);

          return {
            value: phoneNumber,
            label: labelParts.join(' - '),
          };
        })
        .filter(
          (
            option,
          ): option is {
            value: string;
            label: string;
          } => option !== null,
        ),
    [approvedUnassignedPhoneNumbers],
  );

  // Assistants list state
  const [assistants, setAssistants] = useState<any[]>([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);

  // TSV requests state
  const [tsvRequests, setTsvRequests] = useState<any[]>([]);
  const [tsvSearch, setTsvSearch] = useState('');
  const [tsvSort, setTsvSort] = useState<
    TableSortState<
      | 'business_name'
      | 'tollfree_phone_number_sid'
      | 'status'
      | 'business_website'
      | 'notification_email'
      | 'date_created'
      | 'message_volume'
    >
  >({ key: null, direction: 'asc' });
  const visibleTsvRequests = useMemo(
    () =>
      filterAndSortRows(
        tsvRequests,
        tsvSearch,
        (request) =>
          rowSearchText([
            request.business_name,
            request.tollfree_phone_number_sid,
            request.status,
            request.business_website,
            request.notification_email,
            request.date_created,
            request.message_volume,
          ]),
        tsvSort,
        (request, key) => request[key] ?? '',
      ),
    [tsvRequests, tsvSearch, tsvSort],
  );
  const [loadingTsvRequests, setLoadingTsvRequests] = useState(false);

  const fetchStores = useCallback(
    async (page = 1) => {
      setLoadingStores(true);
      try {
        const response = await api.get(
          apiEndpoints.getAllStores(
            page,
            PAGE_SIZE,
            storesOrdering ?? undefined,
            debouncedStoresSearch || undefined,
          ),
        );
        if (response && response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setStores(data);
            setStoresTotalCount(data.length);
            setStoresTotalPages(1);
          } else if (data.results && Array.isArray(data.results)) {
            setStores(data.results);
            setStoresTotalCount(data.count || data.results.length);
            setStoresTotalPages(
              data.total_pages ||
                Math.ceil((data.count || data.results.length) / PAGE_SIZE),
            );
          } else if (user?.available_stores) {
            setStores(user.available_stores);
            setStoresTotalCount(user.available_stores.length);
            setStoresTotalPages(1);
          }
        } else if (user?.available_stores) {
          setStores(user.available_stores);
          setStoresTotalCount(user.available_stores.length);
          setStoresTotalPages(1);
        }
      } catch (error) {
        console.error('Failed to fetch stores:', error);
        if (user?.available_stores) {
          setStores(user.available_stores);
          setStoresTotalCount(user.available_stores.length);
          setStoresTotalPages(1);
        }
      } finally {
        setLoadingStores(false);
      }
    },
    [debouncedStoresSearch, storesOrdering, user?.available_stores],
  );

  const fetchAllStoresForDropdowns = useCallback(async () => {
    try {
      const response = await api.get(apiEndpoints.getAllStoresUnpaginated());
      if (response && response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setAllStoresForDropdowns(data);
        } else if (data.results && Array.isArray(data.results)) {
          setAllStoresForDropdowns(data.results);
        } else {
          setAllStoresForDropdowns([]);
        }
      } else if (user?.available_stores) {
        setAllStoresForDropdowns(user.available_stores);
      }
    } catch (error) {
      console.error('Failed to fetch all stores for dropdowns:', error);
      if (user?.available_stores) {
        setAllStoresForDropdowns(user.available_stores);
      }
    }
  }, [user?.available_stores]);

  useEffect(() => {
    if (user?.is_platform_admin) {
      fetchAllStoresForDropdowns();
    } else if (user?.available_stores) {
      setAllStoresForDropdowns(user.available_stores);
    }
  }, [
    fetchAllStoresForDropdowns,
    user?.available_stores,
    user?.is_platform_admin,
  ]);

  const fetchStoreGroups = useCallback(async () => {
    setLoadingStoreGroups(true);
    try {
      const response = await api.get(apiEndpoints.getStoreGroups());
      if (response && response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setStoreGroups(data);
        } else if (data.results && Array.isArray(data.results)) {
          setStoreGroups(data.results);
        } else {
          setStoreGroups([]);
        }
      } else {
        setStoreGroups([]);
      }
    } catch (error) {
      console.error('Failed to fetch store groups:', error);
      setStoreGroups([]);
    } finally {
      setLoadingStoreGroups(false);
    }
  }, []);

  // Fetch stores on mount and whenever the active tab or page changes
  useEffect(() => {
    fetchStores(storesPage);
  }, [activeTab, storesPage, fetchStores]);

  const fetchStoreUsers = useCallback(
    async (page = 1) => {
      setLoadingStoreUsers(true);
      try {
        const response = await api.get(
          apiEndpoints.getAdminStoreUsers(
            page,
            PAGE_SIZE,
            usersOrdering ?? undefined,
            debouncedUsersSearch || undefined,
          ),
        );
        if (response && response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setStoreUsers(data);
            setUsersTotalCount(data.length);
            setUsersTotalPages(1);
          } else if (data.results && Array.isArray(data.results)) {
            setStoreUsers(data.results);
            setUsersTotalCount(data.count || data.results.length);
            setUsersTotalPages(
              data.total_pages ||
                Math.ceil((data.count || data.results.length) / PAGE_SIZE),
            );
          } else {
            setStoreUsers([]);
          }
        } else {
          setStoreUsers([]);
        }
      } catch {
        setStoreUsers([]);
      } finally {
        setLoadingStoreUsers(false);
      }
    },
    [debouncedUsersSearch, usersOrdering],
  );

  const fetchStoreGroupUsers = useCallback(async () => {
    setLoadingStoreGroupUsers(true);
    try {
      const response = await api.get(apiEndpoints.getStoreGroupUsers());
      if (response && response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setStoreGroupUsers(data);
        } else if (data.results && Array.isArray(data.results)) {
          setStoreGroupUsers(data.results);
        } else {
          setStoreGroupUsers([]);
        }
      } else {
        setStoreGroupUsers([]);
      }
    } catch {
      setStoreGroupUsers([]);
    } finally {
      setLoadingStoreGroupUsers(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'edit' || activeTab === 'user') {
      fetchStoreUsers(usersPage);
    }
  }, [activeTab, usersPage, fetchStoreUsers]);

  useEffect(() => {
    if (activeTab === 'user') {
      fetchStoreGroupUsers();
    }
  }, [activeTab, fetchStoreGroupUsers]);

  const loadEditStoreDetails = async (id: number) => {
    setEditStoreLoading(true);
    setEditStoreError('');
    try {
      const response = await api.get(apiEndpoints.getStoreById(id));
      if (response && response.ok) {
        const data = await response.json();
        setEditStoreCurrentLogoUrl(data.logo_url || '');
        setEditStoreFormData({
          name: data.name || '',
          phonetic_name: data.phonetic_name || '',
          call_intro_sentence: data.call_intro_sentence || '',
          speach_filler_phrase:
            data.speach_filler_phrase || 'Sure, one moment please',
          vip_intro_phrase:
            data.vip_intro_phrase ||
            'Hello {{vip_caller_name}}! Thanks for being a valued member at {{store_name}}.',
          agent_style: data.agent_style || '',
          use_default_call_intro_sentence:
            data.use_default_call_intro_sentence ?? true,
          default_call_intro_sentence: data.default_call_intro_sentence || '',
          legal_name: data.legal_name || '',
          timezone: data.timezone || 'America/New_York',
          address_line_1: data.address_line_1 || '',
          address_line_2: data.address_line_2 || '',
          city: data.city || '',
          state: data.state || '',
          postal_code: data.postal_code || '',
          country: data.country || 'US',
          website: data.website || '',
          menu_link: data.menu_link || '',
          knowledge_base_id: data.knowledge_base_id || '',
          ordering_link: data.ordering_link || '',
          stripe_account_id: data.stripe_account_id || '',
          reservation_link: data.reservation_link || '',
          gift_card_link: data.gift_card_link || '',
          reservation_email: data.reservation_email || '',
          location_secondary_phone_number:
            data.location_secondary_phone_number || '',
          location_secondary_phone_number_ext:
            data.location_secondary_phone_number_ext || '',
          default_delivery_fee_cents: data.default_delivery_fee_cents ?? 0,
          pass_through_dispatch_quote: data.pass_through_dispatch_quote ?? true,
          send_delivery_cost_to_deliverect:
            data.send_delivery_cost_to_deliverect ?? true,
          assess_tax_on_fees: data.assess_tax_on_fees ?? false,
          make_fees_tax_inclusive: data.make_fees_tax_inclusive ?? false,
          fallback_tax_percent:
            data.fallback_tax_percent === null ||
            data.fallback_tax_percent === undefined
              ? ''
              : String(data.fallback_tax_percent),
          application_fee_default_cents:
            data.application_fee_default_cents ?? 50,
          delivery_fee_label: data.delivery_fee_label || '',
          service_fee_label: data.service_fee_label || '',
          default_delivery_mode: data.default_delivery_mode || 'none',
          takes_reservations: data.takes_reservations ?? false,
          takes_catering: data.takes_catering ?? false,
          takes_events: data.takes_events ?? false,
          agent_handles_events: data.agent_handles_events ?? false,
          agent_handles_catering: data.agent_handles_catering ?? false,
          outdoor_seating: data.outdoor_seating ?? false,
          collect_payment_online: data.collect_payment_online ?? true,
          accept_pay_on_site: data.accept_pay_on_site ?? false,
          monthly_minutes_limit: data.monthly_minutes_limit ?? 500,
          ot_max_party_size: data.ot_max_party_size ?? '',
          average_reservation_cover:
            data.average_reservation_cover != null
              ? String(data.average_reservation_cover)
              : '',
          google_price_level: data.google_price_level || '',
          explicit_only_table_types: normalizeExplicitOnlyTableTypes(
            data.explicit_only_table_types,
          ),
          annotate_opentable_reservation_notes:
            data.annotate_opentable_reservation_notes ?? false,
          opentable_reservation_note_annotation_position:
            data.opentable_reservation_note_annotation_position || 'prepend',
          opentable_reservation_note_annotation:
            data.opentable_reservation_note_annotation || '',
          livemode: data.livemode ?? false,
          store_group: data.store_group ?? null,
          store_group_name: '',
          accepts_deliveries: data.accepts_deliveries ?? false,
          accepts_phone_orders: data.accepts_phone_orders ?? true,
          collect_email: data.collect_email ?? false,
          how_to_order_delivery_message:
            data.how_to_order_delivery_message || '',
          posless_integration: data.posless_integration ?? false,
          ...humanTransferFormFields(data),
          current_pos_system: data.current_pos_system || 'UNKNOWN',
          poshub_toast_group_id: data.poshub_toast_group_id || '',
          forward_order_requests_to_store:
            data.forward_order_requests_to_store ?? false,
          forward_order_requests_to_text_link:
            data.forward_order_requests_to_text_link ?? false,
          forward_order_requests_to_phone:
            data.forward_order_requests_to_phone ?? false,
          order_requests_phone_number: data.order_requests_phone_number || '',
          order_requests_phone_number_ext:
            data.order_requests_phone_number_ext || '',
          forward_catering_requests_to_store:
            data.forward_catering_requests_to_store ?? false,
          forward_catering_requests_to_text_link:
            data.forward_catering_requests_to_text_link ?? false,
          forward_event_requests_to_store:
            data.forward_event_requests_to_store ?? false,
          forward_event_requests_to_text_link:
            data.forward_event_requests_to_text_link ?? false,
          catering_outreach_contact: data.catering_outreach_contact || '',
          catering_forwarding_number: data.catering_forwarding_number || '',
          catering_forwarding_number_ext:
            data.catering_forwarding_number_ext || '',
          catering_link: data.catering_link || '',
          event_forwarding_number: data.event_forwarding_number || '',
          event_forwarding_number_ext: data.event_forwarding_number_ext || '',
          event_inquiry_questions: normalizeEventInquiryQuestions(
            data.event_inquiry_questions,
          ),
          catering_inquiry_questions: normalizeEventInquiryQuestions(
            data.catering_inquiry_questions,
          ),
          boosted_keywords: normalizeBoostedKeywords(data.boosted_keywords),
          parking_info: data.parking_info || '',
          business_number: data.business_number || '',
          show_sms_on_dashboard: data.show_sms_on_dashboard ?? false,
          manual_menu_snoozing_enabled:
            data.manual_menu_snoozing_enabled ?? false,
          cross_sell_enabled: data.cross_sell_enabled ?? true,
          forward_late_reservations_to_text:
            data.forward_late_reservations_to_text ?? false,
          forward_reservation_cancelation_failures_to_text:
            data.forward_reservation_cancelation_failures_to_text ?? false,
        });
        setEditStoreBaseline({
          name: data.name || '',
          phonetic_name: data.phonetic_name || '',
          call_intro_sentence: data.call_intro_sentence || '',
          speach_filler_phrase:
            data.speach_filler_phrase || 'Sure, one moment please',
          vip_intro_phrase:
            data.vip_intro_phrase ||
            'Hello {{vip_caller_name}}! Thanks for being a valued member at {{store_name}}.',
          agent_style: data.agent_style || '',
          use_default_call_intro_sentence:
            data.use_default_call_intro_sentence ?? true,
          default_call_intro_sentence: data.default_call_intro_sentence || '',
          legal_name: data.legal_name || '',
          timezone: data.timezone || 'America/New_York',
          address_line_1: data.address_line_1 || '',
          address_line_2: data.address_line_2 || '',
          city: data.city || '',
          state: data.state || '',
          postal_code: data.postal_code || '',
          country: data.country || 'US',
          website: data.website || '',
          menu_link: data.menu_link || '',
          knowledge_base_id: data.knowledge_base_id || '',
          ordering_link: data.ordering_link || '',
          stripe_account_id: data.stripe_account_id || '',
          reservation_link: data.reservation_link || '',
          gift_card_link: data.gift_card_link || '',
          reservation_email: data.reservation_email || '',
          location_secondary_phone_number:
            data.location_secondary_phone_number || '',
          location_secondary_phone_number_ext:
            data.location_secondary_phone_number_ext || '',
          default_delivery_fee_cents: data.default_delivery_fee_cents ?? 0,
          pass_through_dispatch_quote: data.pass_through_dispatch_quote ?? true,
          send_delivery_cost_to_deliverect:
            data.send_delivery_cost_to_deliverect ?? true,
          assess_tax_on_fees: data.assess_tax_on_fees ?? false,
          make_fees_tax_inclusive: data.make_fees_tax_inclusive ?? false,
          fallback_tax_percent:
            data.fallback_tax_percent === null ||
            data.fallback_tax_percent === undefined
              ? ''
              : String(data.fallback_tax_percent),
          application_fee_default_cents:
            data.application_fee_default_cents ?? 50,
          delivery_fee_label: data.delivery_fee_label || '',
          service_fee_label: data.service_fee_label || '',
          default_delivery_mode: data.default_delivery_mode || 'none',
          takes_reservations: data.takes_reservations ?? false,
          takes_catering: data.takes_catering ?? false,
          takes_events: data.takes_events ?? false,
          agent_handles_events: data.agent_handles_events ?? false,
          agent_handles_catering: data.agent_handles_catering ?? false,
          outdoor_seating: data.outdoor_seating ?? false,
          collect_payment_online: data.collect_payment_online ?? true,
          accept_pay_on_site: data.accept_pay_on_site ?? false,
          monthly_minutes_limit: data.monthly_minutes_limit ?? 500,
          ot_max_party_size: data.ot_max_party_size ?? '',
          average_reservation_cover:
            data.average_reservation_cover != null
              ? String(data.average_reservation_cover)
              : '',
          google_price_level: data.google_price_level || '',
          explicit_only_table_types: normalizeExplicitOnlyTableTypes(
            data.explicit_only_table_types,
          ),
          annotate_opentable_reservation_notes:
            data.annotate_opentable_reservation_notes ?? false,
          opentable_reservation_note_annotation_position:
            data.opentable_reservation_note_annotation_position || 'prepend',
          opentable_reservation_note_annotation:
            data.opentable_reservation_note_annotation || '',
          livemode: data.livemode ?? false,
          store_group: data.store_group ?? null,
          accepts_deliveries: data.accepts_deliveries ?? false,
          accepts_phone_orders: data.accepts_phone_orders ?? true,
          collect_email: data.collect_email ?? false,
          how_to_order_delivery_message:
            data.how_to_order_delivery_message || '',
          posless_integration: data.posless_integration ?? false,
          ...humanTransferFormFields(data),
          current_pos_system: data.current_pos_system || 'UNKNOWN',
          poshub_toast_group_id: data.poshub_toast_group_id || '',
          forward_order_requests_to_store:
            data.forward_order_requests_to_store ?? false,
          forward_order_requests_to_text_link:
            data.forward_order_requests_to_text_link ?? false,
          forward_order_requests_to_phone:
            data.forward_order_requests_to_phone ?? false,
          order_requests_phone_number: data.order_requests_phone_number || '',
          order_requests_phone_number_ext:
            data.order_requests_phone_number_ext || '',
          forward_catering_requests_to_store:
            data.forward_catering_requests_to_store ?? false,
          forward_catering_requests_to_text_link:
            data.forward_catering_requests_to_text_link ?? false,
          forward_event_requests_to_store:
            data.forward_event_requests_to_store ?? false,
          forward_event_requests_to_text_link:
            data.forward_event_requests_to_text_link ?? false,
          catering_outreach_contact: data.catering_outreach_contact || '',
          catering_forwarding_number: data.catering_forwarding_number || '',
          catering_forwarding_number_ext:
            data.catering_forwarding_number_ext || '',
          catering_link: data.catering_link || '',
          event_forwarding_number: data.event_forwarding_number || '',
          event_forwarding_number_ext: data.event_forwarding_number_ext || '',
          event_inquiry_questions: normalizeEventInquiryQuestions(
            data.event_inquiry_questions,
          ),
          catering_inquiry_questions: normalizeEventInquiryQuestions(
            data.catering_inquiry_questions,
          ),
          boosted_keywords: normalizeBoostedKeywords(data.boosted_keywords),
          parking_info: data.parking_info || '',
          business_number: data.business_number || '',
          show_sms_on_dashboard: data.show_sms_on_dashboard ?? false,
          manual_menu_snoozing_enabled:
            data.manual_menu_snoozing_enabled ?? false,
          cross_sell_enabled: data.cross_sell_enabled ?? true,
          forward_late_reservations_to_text:
            data.forward_late_reservations_to_text ?? false,
          forward_reservation_cancelation_failures_to_text:
            data.forward_reservation_cancelation_failures_to_text ?? false,
        });
      } else {
        setEditStoreFormData(null);
        setEditStoreError('Failed to load store details');
        setEditStoreCurrentLogoUrl('');
        setEditStoreBaseline(null);
      }
    } catch {
      setEditStoreFormData(null);
      setEditStoreError('Failed to load store details');
      setEditStoreCurrentLogoUrl('');
      setEditStoreBaseline(null);
    } finally {
      setEditStoreLoading(false);
    }
  };

  const handleEditStoreChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const nextValue = name.endsWith('_ext')
      ? phoneExtensionInput(value)
      : value;
    setEditStoreFormData((prev: any) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : type === 'number'
            ? parseInt(value, 10) || 0
            : name === 'store_group'
              ? value === ''
                ? null
                : parseInt(value as string, 10) || null
              : nextValue,
    }));
  };

  const updateEditStoreEventInquiryQuestions = (
    updater: (_questions: string[]) => string[],
  ) => {
    setEditStoreFormData((prev: any) => {
      if (!prev) return prev;
      const currentQuestions = Array.isArray(prev.event_inquiry_questions)
        ? prev.event_inquiry_questions
        : [];
      return {
        ...prev,
        event_inquiry_questions: updater(currentQuestions).slice(
          0,
          MAX_CUSTOM_EVENT_INQUIRY_QUESTIONS,
        ),
      };
    });
  };

  const handleEventInquiryQuestionChange = (index: number, value: string) => {
    updateEditStoreEventInquiryQuestions((questions) =>
      questions.map((question, questionIndex) =>
        questionIndex === index ? value : question,
      ),
    );
  };

  const handleAddEventInquiryQuestion = () => {
    updateEditStoreEventInquiryQuestions((questions) =>
      questions.length >= MAX_CUSTOM_EVENT_INQUIRY_QUESTIONS
        ? questions
        : [...questions, ''],
    );
  };

  const handleRemoveEventInquiryQuestion = (index: number) => {
    if (!questionDrag.removeItem(index)) return;
    updateEditStoreEventInquiryQuestions((questions) =>
      questions.filter((_, questionIndex) => questionIndex !== index),
    );
  };

  const handleReorderEventInquiryQuestions = (
    fromIndex: number,
    toIndex: number,
  ) => {
    const questions = Array.isArray(editStoreFormData?.event_inquiry_questions)
      ? editStoreFormData.event_inquiry_questions
      : [];
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= questions.length ||
      toIndex >= questions.length
    ) {
      return false;
    }
    const nextQuestions = [...questions];
    const [movedQuestion] = nextQuestions.splice(fromIndex, 1);
    nextQuestions.splice(toIndex, 0, movedQuestion);
    updateEditStoreEventInquiryQuestions(() => nextQuestions);
    return true;
  };

  const updateEditStoreCateringInquiryQuestions = (
    updater: (_questions: string[]) => string[],
  ) => {
    setEditStoreFormData((prev: any) => {
      if (!prev) return prev;
      const currentQuestions = Array.isArray(prev.catering_inquiry_questions)
        ? prev.catering_inquiry_questions
        : [];
      return {
        ...prev,
        catering_inquiry_questions: updater(currentQuestions).slice(
          0,
          MAX_CUSTOM_EVENT_INQUIRY_QUESTIONS,
        ),
      };
    });
  };

  const handleCateringInquiryQuestionChange = (
    index: number,
    value: string,
  ) => {
    updateEditStoreCateringInquiryQuestions((questions) =>
      questions.map((question, questionIndex) =>
        questionIndex === index ? value : question,
      ),
    );
  };

  const handleAddCateringInquiryQuestion = () => {
    updateEditStoreCateringInquiryQuestions((questions) =>
      questions.length >= MAX_CUSTOM_EVENT_INQUIRY_QUESTIONS
        ? questions
        : [...questions, ''],
    );
  };

  const handleRemoveCateringInquiryQuestion = (index: number) => {
    if (!cateringQuestionDrag.removeItem(index)) return;
    updateEditStoreCateringInquiryQuestions((questions) =>
      questions.filter((_, questionIndex) => questionIndex !== index),
    );
  };

  const handleReorderCateringInquiryQuestions = (
    fromIndex: number,
    toIndex: number,
  ) => {
    const questions = Array.isArray(
      editStoreFormData?.catering_inquiry_questions,
    )
      ? editStoreFormData.catering_inquiry_questions
      : [];
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= questions.length ||
      toIndex >= questions.length
    ) {
      return false;
    }
    const nextQuestions = [...questions];
    const [movedQuestion] = nextQuestions.splice(fromIndex, 1);
    nextQuestions.splice(toIndex, 0, movedQuestion);
    updateEditStoreCateringInquiryQuestions(() => nextQuestions);
    return true;
  };

  const editStoreQuestionCount = Array.isArray(
    editStoreFormData?.event_inquiry_questions,
  )
    ? editStoreFormData.event_inquiry_questions.length
    : 0;
  const questionDrag = useAnimatedDragReorder(
    editStoreQuestionCount,
    handleReorderEventInquiryQuestions,
  );
  const editStoreCateringQuestionCount = Array.isArray(
    editStoreFormData?.catering_inquiry_questions,
  )
    ? editStoreFormData.catering_inquiry_questions.length
    : 0;
  const cateringQuestionDrag = useAnimatedDragReorder(
    editStoreCateringQuestionCount,
    handleReorderCateringInquiryQuestions,
  );

  const updateEditStoreBoostedKeywords = (
    updater: (_keywords: string[]) => string[],
  ) => {
    setEditStoreFormData((prev: any) => {
      if (!prev) return prev;
      const currentKeywords = Array.isArray(prev.boosted_keywords)
        ? prev.boosted_keywords
        : [];
      return {
        ...prev,
        boosted_keywords: updater(currentKeywords).slice(
          0,
          MAX_BOOSTED_KEYWORDS,
        ),
      };
    });
  };

  const handleBoostedKeywordChange = (index: number, value: string) => {
    updateEditStoreBoostedKeywords((keywords) =>
      keywords.map((keyword, keywordIndex) =>
        keywordIndex === index ? value : keyword,
      ),
    );
  };

  const handleAddBoostedKeyword = () => {
    updateEditStoreBoostedKeywords((keywords) =>
      keywords.length >= MAX_BOOSTED_KEYWORDS ? keywords : [...keywords, ''],
    );
  };

  const handleRemoveBoostedKeyword = (index: number) => {
    updateEditStoreBoostedKeywords((keywords) =>
      keywords.filter((_, keywordIndex) => keywordIndex !== index),
    );
  };

  const handleEditStoreLogoChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setEditStoreLogo(f);
    // Preview URL management
    if (editStoreLogoPreviewUrl) {
      URL.revokeObjectURL(editStoreLogoPreviewUrl);
      setEditStoreLogoPreviewUrl('');
    }
    if (f) {
      const url = URL.createObjectURL(f);
      setEditStoreLogoPreviewUrl(url);
    }
  };

  const clearLocalLogoSelection = () => {
    if (editStoreLogoPreviewUrl) {
      URL.revokeObjectURL(editStoreLogoPreviewUrl);
      setEditStoreLogoPreviewUrl('');
    }
    setEditStoreLogo(null);
  };

  const handleRemoveStoredLogo = async () => {
    if (!editStoreId) return;
    setEditStoreError('');
    setEditStoreSuccess('');
    try {
      // Send JSON PATCH to clear the logo on the server
      const response = await api.patch(apiEndpoints.updateStore(editStoreId), {
        logo: null,
      });
      if (response && response.ok) {
        setEditStoreSuccess('Store logo removed.');
        setEditStoreCurrentLogoUrl('');
        clearLocalLogoSelection();
        // Reload store details to reflect server state
        await loadEditStoreDetails(editStoreId);
      } else if (response) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await response.json().catch(() => ({}));
          setEditStoreError(
            data.detail || data.message || 'Failed to remove logo',
          );
        } else {
          const text = await response.text();
          setEditStoreError(text || 'Failed to remove logo');
        }
      } else {
        setEditStoreError('Failed to remove logo');
      }
    } catch {
      setEditStoreError('Failed to remove logo');
    }
  };

  const handleEditStoreSubmit = async (
    e: React.FormEvent,
    formOverride?: any,
  ) => {
    e.preventDefault();
    const storeForm = formOverride ?? editStoreFormData;
    if (!editStoreId || !storeForm) return;
    setEditStoreError('');
    setEditStoreSuccess('');
    try {
      const eventInquiryQuestions = normalizeEventInquiryQuestions(
        storeForm.event_inquiry_questions,
      );
      const cateringInquiryQuestions = normalizeEventInquiryQuestions(
        storeForm.catering_inquiry_questions,
      );
      const boostedKeywords = normalizeBoostedKeywords(
        storeForm.boosted_keywords,
      );
      const explicitOnlyTableTypes = normalizeExplicitOnlyTableTypes(
        storeForm.explicit_only_table_types,
      );
      const storeData = Object.fromEntries(
        Object.entries(storeForm).filter(
          ([key]) =>
            key !== 'event_inquiry_questions' &&
            key !== 'catering_inquiry_questions' &&
            key !== 'boosted_keywords' &&
            key !== 'explicit_only_table_types' &&
            key !== 'default_call_intro_sentence',
        ),
      );
      if (storeData.ot_max_party_size === '') {
        delete storeData.ot_max_party_size;
      }
      if (
        storeData.fallback_tax_percent === '' ||
        storeData.fallback_tax_percent === undefined
      ) {
        storeData.fallback_tax_percent = null;
      }
      if (storeData.average_reservation_cover === '') {
        storeData.average_reservation_cover = null;
      }
      delete storeData.poshub_toast_group_id;
      let response: Response | null;

      if (editStoreLogo) {
        const formData = new FormData();
        if (storeFormData.legal_name) {
          formData.append('legal_name', storeFormData.legal_name);
        }
        Object.entries(storeData).forEach(([k, v]) => {
          if (k === 'human_transfer_hours') {
            formData.append(k, JSON.stringify(v ?? []));
            return;
          }
          if (k === 'fallback_tax_percent' && (v === null || v === '')) {
            formData.append(k, '');
            return;
          }
          if (v !== undefined && v !== null) {
            if (typeof v === 'boolean' || typeof v === 'number') {
              formData.append(k, String(v));
            } else {
              formData.append(k, v as any);
            }
          }
        });
        formData.append('logo', editStoreLogo);
        response = await api.patchForm(
          apiEndpoints.updateStore(editStoreId),
          formData,
        );
        if (response && response.ok) {
          response = await api.patch(apiEndpoints.updateStore(editStoreId), {
            event_inquiry_questions: eventInquiryQuestions,
            catering_inquiry_questions: cateringInquiryQuestions,
            boosted_keywords: boostedKeywords,
            explicit_only_table_types: explicitOnlyTableTypes,
          });
        }
      } else {
        response = await api.patch(apiEndpoints.updateStore(editStoreId), {
          ...storeData,
          event_inquiry_questions: eventInquiryQuestions,
          catering_inquiry_questions: cateringInquiryQuestions,
          boosted_keywords: boostedKeywords,
          explicit_only_table_types: explicitOnlyTableTypes,
        });
      }
      if (response && response.ok) {
        setEditStoreSuccess('Store updated successfully.');
        setEditStoreLogo(null);
        if (editStoreLogoPreviewUrl) {
          URL.revokeObjectURL(editStoreLogoPreviewUrl);
          setEditStoreLogoPreviewUrl('');
        }
        if (editStoreId) {
          await loadEditStoreDetails(editStoreId);
        }
        fetchStores(storesPage);
        window.dispatchEvent(new Event('store-settings-updated'));
        if (isEditStoreModalOpen) {
          setTimeout(() => setIsEditStoreModalOpen(false), 800);
        }
      } else {
        if (response) {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await response.json().catch(() => ({}));
            setEditStoreError(
              formatApiErrorMessage(
                data,
                'Failed to update store',
                storeFieldLabels,
              ),
            );
          } else {
            const text = await response.text();
            setEditStoreError(text || 'Failed to update store');
          }
        } else {
          setEditStoreError('Failed to update store');
        }
      }
    } catch (err) {
      setEditStoreError('Failed to update store');
    }
  };

  const storeFieldLabels: Record<string, string> = {
    name: 'Store Name',
    phonetic_name: 'Phonetic Name',
    call_intro_sentence: 'Call Intro Message',
    speach_filler_phrase: 'Speech Filler Phrase',
    vip_intro_phrase: 'VIP Intro Phrase',
    agent_style: 'Agent Style',
    use_default_call_intro_sentence: 'Use Default Call Intro',
    legal_name: 'Legal Name',
    timezone: 'Timezone',
    location_secondary_phone_number: 'Secondary Phone Number',
    location_secondary_phone_number_ext: 'Secondary Phone Extension',
    address_line_1: 'Address Line 1',
    address_line_2: 'Address Line 2',
    city: 'City',
    state: 'State',
    postal_code: 'Postal Code',
    country: 'Country',
    website: 'Website',
    menu_link: 'Menu Link',
    knowledge_base_id: 'Knowledge Base',
    ordering_link: 'Ordering Link',
    stripe_account_id: 'Stripe Account ID',
    reservation_link: 'Reservation Link',
    gift_card_link: 'Gift Card Link',
    reservation_email: 'Reservation Email',
    default_delivery_fee_cents: 'Default Delivery Fee (cents)',
    application_fee_default_cents: 'Application Fee Default (cents)',
    delivery_fee_label: 'Delivery Fee Label',
    service_fee_label: 'Service Fee Label',
    default_delivery_mode: 'Default Delivery Mode',
    monthly_minutes_limit: 'Monthly Minutes Limit',
    ot_max_party_size: 'OT Max Party Size',
    average_reservation_cover: 'Average Reservation Cover',
    google_price_level: 'Google Price Level',
    explicit_only_table_types: 'Call Out Only-Available Seating',
    annotate_opentable_reservation_notes:
      'Annotate OpenTable Reservation Notes',
    opentable_reservation_note_annotation_position:
      'Reservation Annotation Position',
    opentable_reservation_note_annotation: 'Reservation Annotation Message',
    takes_reservations: 'Takes Reservations',
    takes_catering: 'Takes Catering',
    takes_events: 'Takes Events',
    agent_handles_events: 'Agent Handles Events',
    agent_handles_catering: 'Agent Handles Catering',
    outdoor_seating: 'Outdoor Seating',
    collect_payment_online: 'Collect Payment Online',
    accept_pay_on_site: 'Accept Pay on Site',
    pass_through_dispatch_quote: 'Pass Through Dispatch Quote',
    send_delivery_cost_to_deliverect: 'Send Delivery Cost to Deliverect',
    assess_tax_on_fees: 'Assess Tax on Fees',
    make_fees_tax_inclusive: 'Make Fees Tax Inclusive',
    fallback_tax_percent: 'Fallback Tax Percent',
    livemode: 'Live Mode',
    accepts_deliveries: 'Accepts Deliveries',
    accepts_phone_orders: 'Accepts Phone Orders',
    collect_email: 'Collect Email for Replies from Store',
    how_to_order_delivery_message: 'How To Order Delivery Message',
    store_group: 'Store Group',
    posless_integration: 'POS-less Integration',
    human_transfer_enabled: 'Human Transfer Enabled',
    human_transfer_schedule: 'Human Transfer Hours',
    human_transfer_start_time: 'Human Transfer Start',
    human_transfer_end_time: 'Human Transfer End',
    human_transfer_hours: 'Human Transfer Days',
    current_pos_system: 'POS System',
    forward_order_requests_to_store: 'Forward Order Requests to Store',
    forward_order_requests_to_text_link: 'Forward Order Requests to Text Link',
    forward_order_requests_to_phone: 'Forward Order Requests to Phone',
    order_requests_phone_number: 'Order Requests Phone Number',
    order_requests_phone_number_ext: 'Order Requests Extension',
    forward_catering_requests_to_store: 'Forward Catering Requests to Store',
    forward_catering_requests_to_text_link:
      'Forward Catering Requests to Text Link',
    forward_event_requests_to_store: 'Forward Event Requests to Store',
    forward_event_requests_to_text_link: 'Forward Event Requests to Text Link',
    catering_outreach_contact: 'Catering Outreach Contact',
    catering_forwarding_number: 'Catering Forwarding Number',
    catering_forwarding_number_ext: 'Catering Extension',
    catering_link: 'Catering Link',
    event_forwarding_number: 'Event Forwarding Number',
    event_forwarding_number_ext: 'Event Extension',
    event_inquiry_questions: 'Event Inquiry Questions',
    catering_inquiry_questions: 'Catering Inquiry Questions',
    boosted_keywords: 'Boosted Keywords',
    parking_info: 'Parking Info',
    business_number: 'Business Number',
    show_sms_on_dashboard: 'Show SMS on Dashboard',
    manual_menu_snoozing_enabled: 'Enable Manual Menu Snoozing',
    cross_sell_enabled: 'Enable Cross-Selling (legacy fallback)',
    forward_late_reservations_to_text: 'Forward Late Reservations to Text',
    forward_reservation_cancelation_failures_to_text:
      'Forward Reservation Cancellation Failures to Text',
  };

  const getEditStoreChanges = () => {
    if (!editStoreBaseline || !editStoreFormData) return [];
    const keys = Object.keys(storeFieldLabels);
    const changes: Array<{ key: string; label: string; from: any; to: any }> =
      [];
    keys.forEach((k) => {
      const before = editStoreBaseline[k];
      const after = editStoreFormData[k];
      if (k === 'store_group') {
        if ((before || null) !== (after || null)) {
          changes.push({
            key: k,
            label: storeFieldLabels[k],
            from: before,
            to: after,
          });
        }
      } else if (
        k === 'event_inquiry_questions' ||
        k === 'catering_inquiry_questions'
      ) {
        const normalizedBefore = normalizeEventInquiryQuestions(before);
        const normalizedAfter = normalizeEventInquiryQuestions(after);
        if (
          JSON.stringify(normalizedBefore) !== JSON.stringify(normalizedAfter)
        ) {
          changes.push({
            key: k,
            label: storeFieldLabels[k],
            from: normalizedBefore.join(' | ') || 'Default questions',
            to: normalizedAfter.join(' | ') || 'Default questions',
          });
        }
      } else if (k === 'boosted_keywords') {
        const normalizedBefore = normalizeBoostedKeywords(before);
        const normalizedAfter = normalizeBoostedKeywords(after);
        if (
          JSON.stringify(normalizedBefore) !== JSON.stringify(normalizedAfter)
        ) {
          changes.push({
            key: k,
            label: storeFieldLabels[k],
            from: normalizedBefore.join(' | ') || 'None',
            to: normalizedAfter.join(' | ') || 'None',
          });
        }
      } else if (k === 'human_transfer_hours') {
        const normalizedBefore = normalizeHumanTransferHours(before);
        const normalizedAfter = normalizeHumanTransferHours(after);
        if (
          JSON.stringify(normalizedBefore) !== JSON.stringify(normalizedAfter)
        ) {
          changes.push({
            key: k,
            label: storeFieldLabels[k],
            from: describeHumanTransferHours(normalizedBefore),
            to: describeHumanTransferHours(normalizedAfter),
          });
        }
      } else if (k === 'explicit_only_table_types') {
        const normalizedBefore = normalizeExplicitOnlyTableTypes(before);
        const normalizedAfter = normalizeExplicitOnlyTableTypes(after);
        if (
          JSON.stringify(normalizedBefore) !== JSON.stringify(normalizedAfter)
        ) {
          changes.push({
            key: k,
            label: storeFieldLabels[k],
            from: normalizedBefore.join(', ') || 'None',
            to: normalizedAfter.join(', ') || 'None',
          });
        }
      } else if (before !== after) {
        changes.push({
          key: k,
          label: storeFieldLabels[k],
          from: before,
          to: after,
        });
      }
    });
    if (editStoreLogo) {
      changes.push({
        key: 'logo',
        label: 'Logo',
        from: editStoreCurrentLogoUrl ? 'Existing Logo' : 'None',
        to: editStoreLogo.name,
      });
    }
    return changes;
  };

  const handleOpenConfirmEdit = (e: React.FormEvent) => {
    e.preventDefault();
    const changes = getEditStoreChanges();
    if (changes.length === 0) {
      setEditStoreError('No changes to save.');
      return;
    }
    setEditStorePendingChanges(changes);
    setSecondaryFollowers(
      numbersSharingOldSecondary(editStoreBaseline, editStoreFormData),
    );
    setSecondaryFollowersToUpdate([]);
    setIsEditConfirmOpen(true);
  };

  const handleConfirmEditSubmit = async () => {
    setIsEditConfirmOpen(false);
    const chosen = secondaryFollowers.filter((k) =>
      secondaryFollowersToUpdate.includes(k),
    );
    if (chosen.length === 0) {
      await handleEditStoreSubmit(new Event('submit') as any);
      return;
    }
    const updated = applyNewSecondary(editStoreFormData, chosen);
    setEditStoreFormData(updated);
    await handleEditStoreSubmit(new Event('submit') as any, updated);
  };

  const renderEventInquiryQuestionsEditor = () => {
    if (!editStoreFormData) return null;
    const questions = Array.isArray(editStoreFormData.event_inquiry_questions)
      ? editStoreFormData.event_inquiry_questions
      : [];
    const canAddQuestion =
      questions.length < MAX_CUSTOM_EVENT_INQUIRY_QUESTIONS;
    const eventsEnabled = !!editStoreFormData.takes_events;

    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/40">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h4 className="text-sm font-semibold text-gray-800 dark:text-white">
              Events
            </h4>
          </div>
          <label className="inline-flex items-center gap-3 text-xs font-medium text-gray-500 dark:text-gray-400">
            <span>{eventsEnabled ? 'On' : 'Off'}</span>
            <input
              type="checkbox"
              checked={eventsEnabled}
              onChange={(event) =>
                setEditStoreFormData((prev: any) =>
                  prev ? { ...prev, takes_events: event.target.checked } : prev,
                )
              }
              className="peer sr-only"
            />
            <span className="relative h-6 w-11 rounded-full bg-gray-300 transition peer-checked:bg-brand-500 dark:bg-gray-700">
              <span
                className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${
                  eventsEnabled ? 'translate-x-5' : ''
                }`}
              />
            </span>
          </label>
        </div>

        {eventsEnabled ? (
          <>
            <div className="space-y-3">
              <p className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm font-semibold text-brand-700 dark:border-brand-800/60 dark:bg-brand-900/20 dark:text-brand-300">
                Note: Name, Phone, and Preferred Contact are automatically
                collected
              </p>

              {questions.map((question: string, index: number) => {
                const isBlank = !question.trim();
                const itemId = questionDrag.ids[index];
                return (
                  <div
                    key={itemId}
                    ref={questionDrag.registerItem(itemId)}
                    draggable
                    onDragStart={(event) =>
                      questionDrag.handleDragStart(event, index)
                    }
                    onDragOver={(event) =>
                      questionDrag.handleDragOver(event, index)
                    }
                    onDrop={(event) => event.preventDefault()}
                    onDragEnd={questionDrag.handleDragEnd}
                    className={`grid cursor-grab grid-cols-1 gap-2 rounded-lg border p-3 transition hover:border-brand-300 hover:bg-gray-50 active:cursor-grabbing dark:hover:border-brand-800 dark:hover:bg-gray-800/70 sm:grid-cols-[auto_1fr_auto] ${
                      questionDrag.draggedIndex === index
                        ? 'border-brand-300 bg-gray-50 opacity-60 dark:border-brand-800 dark:bg-gray-800/70'
                        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
                    }`}
                  >
                    <span
                      className="flex h-11 w-9 items-center justify-center rounded-lg text-gray-400 dark:text-gray-500"
                      aria-hidden="true"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.8}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8 7h.01M8 12h.01M8 17h.01M16 7h.01M16 12h.01M16 17h.01"
                        />
                      </svg>
                    </span>
                    <Input
                      type="text"
                      value={question}
                      onChange={(event) =>
                        handleEventInquiryQuestionChange(
                          index,
                          event.target.value,
                        )
                      }
                      placeholder="Add an event inquiry question"
                      error={isBlank}
                      hint={`Event question ${index + 1}`}
                    />
                    <div className="flex flex-wrap items-start gap-2">
                      <button
                        type="button"
                        onClick={() => handleRemoveEventInquiryQuestion(index)}
                        className="flex h-11 w-11 items-center justify-center rounded-lg border border-error-200 text-error-600 transition hover:bg-error-50 dark:border-error-900/50 dark:text-error-400 dark:hover:bg-error-900/20"
                        aria-label={`Remove event question ${index + 1}`}
                        title="Remove question"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.8}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 3h6m-8 4h10m-9 0 .7 13h6.6L16 7M10 11v5m4-5v5"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              {canAddQuestion && (
                <button
                  type="button"
                  onClick={handleAddEventInquiryQuestion}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm text-white transition hover:bg-brand-600"
                >
                  Add question
                </button>
              )}
            </div>
          </>
        ) : null}
      </div>
    );
  };

  const renderCateringInquiryQuestionsEditor = () => {
    if (!editStoreFormData) return null;
    const questions = Array.isArray(
      editStoreFormData.catering_inquiry_questions,
    )
      ? editStoreFormData.catering_inquiry_questions
      : [];
    const canAddQuestion =
      questions.length < MAX_CUSTOM_EVENT_INQUIRY_QUESTIONS;
    const cateringEnabled = !!editStoreFormData.takes_catering;

    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/40">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h4 className="text-sm font-semibold text-gray-800 dark:text-white">
              Catering
            </h4>
          </div>
          <label className="inline-flex items-center gap-3 text-xs font-medium text-gray-500 dark:text-gray-400">
            <span>{cateringEnabled ? 'On' : 'Off'}</span>
            <input
              type="checkbox"
              checked={cateringEnabled}
              onChange={(event) =>
                setEditStoreFormData((prev: any) =>
                  prev
                    ? { ...prev, takes_catering: event.target.checked }
                    : prev,
                )
              }
              className="peer sr-only"
            />
            <span className="relative h-6 w-11 rounded-full bg-gray-300 transition peer-checked:bg-brand-500 dark:bg-gray-700">
              <span
                className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${
                  cateringEnabled ? 'translate-x-5' : ''
                }`}
              />
            </span>
          </label>
        </div>

        {cateringEnabled ? (
          <>
            <div className="space-y-3">
              <p className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm font-semibold text-brand-700 dark:border-brand-800/60 dark:bg-brand-900/20 dark:text-brand-300">
                Note: Name, Phone, and Preferred Contact are automatically
                collected
              </p>

              {questions.map((question: string, index: number) => {
                const isBlank = !question.trim();
                const itemId = cateringQuestionDrag.ids[index];
                return (
                  <div
                    key={itemId}
                    ref={cateringQuestionDrag.registerItem(itemId)}
                    draggable
                    onDragStart={(event) =>
                      cateringQuestionDrag.handleDragStart(event, index)
                    }
                    onDragOver={(event) =>
                      cateringQuestionDrag.handleDragOver(event, index)
                    }
                    onDrop={(event) => event.preventDefault()}
                    onDragEnd={cateringQuestionDrag.handleDragEnd}
                    className={`grid cursor-grab grid-cols-1 gap-2 rounded-lg border p-3 transition hover:border-brand-300 hover:bg-gray-50 active:cursor-grabbing dark:hover:border-brand-800 dark:hover:bg-gray-800/70 sm:grid-cols-[auto_1fr_auto] ${
                      cateringQuestionDrag.draggedIndex === index
                        ? 'border-brand-300 bg-gray-50 opacity-60 dark:border-brand-800 dark:bg-gray-800/70'
                        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
                    }`}
                  >
                    <span
                      className="flex h-11 w-9 items-center justify-center rounded-lg text-gray-400 dark:text-gray-500"
                      aria-hidden="true"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.8}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8 7h.01M8 12h.01M8 17h.01M16 7h.01M16 12h.01M16 17h.01"
                        />
                      </svg>
                    </span>
                    <Input
                      type="text"
                      value={question}
                      onChange={(event) =>
                        handleCateringInquiryQuestionChange(
                          index,
                          event.target.value,
                        )
                      }
                      placeholder="Add a catering inquiry question"
                      error={isBlank}
                      hint={`Catering question ${index + 1}`}
                    />
                    <div className="flex flex-wrap items-start gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveCateringInquiryQuestion(index)
                        }
                        className="flex h-11 w-11 items-center justify-center rounded-lg border border-error-200 text-error-600 transition hover:bg-error-50 dark:border-error-900/50 dark:text-error-400 dark:hover:bg-error-900/20"
                        aria-label={`Remove catering question ${index + 1}`}
                        title="Remove question"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.8}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 3h6m-8 4h10m-9 0 .7 13h6.6L16 7M10 11v5m4-5v5"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              {canAddQuestion && (
                <button
                  type="button"
                  onClick={handleAddCateringInquiryQuestion}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm text-white transition hover:bg-brand-600"
                >
                  Add question
                </button>
              )}
            </div>
          </>
        ) : null}
      </div>
    );
  };

  const renderBoostedKeywordsEditor = () => {
    if (!editStoreFormData) return null;
    const keywords = Array.isArray(editStoreFormData.boosted_keywords)
      ? editStoreFormData.boosted_keywords
      : [];
    const canAddKeyword = keywords.length < MAX_BOOSTED_KEYWORDS;

    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/40">
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-white">
            Boosted Keywords
          </h4>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Words or phrases (like menu item names) that the voice agent should
            recognize more accurately. Up to {MAX_BOOSTED_KEYWORDS}.
          </p>
        </div>

        <div className="space-y-3">
          {keywords.map((keyword: string, index: number) => (
            <div
              key={index}
              className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900 sm:grid-cols-[1fr_auto]"
            >
              <Input
                type="text"
                value={keyword}
                onChange={(event) =>
                  handleBoostedKeywordChange(index, event.target.value)
                }
                placeholder="Add a boosted keyword"
                hint={`Keyword ${index + 1}`}
              />
              <div className="flex flex-wrap items-start gap-2">
                <button
                  type="button"
                  onClick={() => handleRemoveBoostedKeyword(index)}
                  className="flex h-11 w-11 items-center justify-center rounded-lg border border-error-200 text-error-600 transition hover:bg-error-50 dark:border-error-900/50 dark:text-error-400 dark:hover:bg-error-900/20"
                  aria-label={`Remove keyword ${index + 1}`}
                  title="Remove keyword"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.8}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 3h6m-8 4h10m-9 0 .7 13h6.6L16 7M10 11v5m4-5v5"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          {canAddKeyword && (
            <button
              type="button"
              onClick={handleAddBoostedKeyword}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm text-white transition hover:bg-brand-600"
            >
              Add keyword
            </button>
          )}
        </div>
      </div>
    );
  };

  // Edit store user state
  const [editUserId, setEditUserId] = useState<number | null>(null);
  const [editUserFormData, setEditUserFormData] = useState<any | null>(null);
  const [editUserError, setEditUserError] = useState<string>('');
  const [editUserSuccess, setEditUserSuccess] = useState<string>('');

  const loadEditUserDetails = async (id: number) => {
    setEditUserError('');
    try {
      const response = await api.get(apiEndpoints.updateStoreUser(id));
      if (response && response.ok) {
        const data = await response.json();
        setEditUserFormData({
          email: data.email || '',
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          role: data.role || 'Manager',
          store_id: data.store_id || data.store || 0,
          password: '',
        });
      } else {
        setEditUserFormData(null);
        setEditUserError('Failed to load user details');
      }
    } catch {
      setEditUserFormData(null);
      setEditUserError('Failed to load user details');
    }
  };

  const handleEditUserChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    setEditUserFormData((prev: any) => ({
      ...prev,
      [name]:
        type === 'number'
          ? parseInt(value, 10) || 0
          : name === 'store_id'
            ? value === ''
              ? 0
              : parseInt(value, 10) || 0
            : value,
    }));
  };

  const handleEditUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUserId || !editUserFormData) return;
    setEditUserError('');
    setEditUserSuccess('');
    try {
      const payload: any = {
        email: editUserFormData.email,
        first_name: editUserFormData.first_name,
        last_name: editUserFormData.last_name,
        role: editUserFormData.role,
        store_id: editUserFormData.store_id,
      };
      if (editUserFormData.password) {
        payload.password = editUserFormData.password;
      }
      const response = await api.patch(
        apiEndpoints.updateStoreUser(editUserId),
        payload,
      );
      if (response && response.ok) {
        setEditUserSuccess('User updated successfully.');
        fetchStoreUsers(usersPage);
        if (isEditUserModalOpen) {
          setTimeout(() => setIsEditUserModalOpen(false), 800);
        }
      } else if (response) {
        const data = await response.json();
        setEditUserError(
          data.detail || data.message || 'Failed to update user',
        );
      } else {
        setEditUserError('Failed to update user');
      }
    } catch {
      setEditUserError('Failed to update user');
    }
  };

  // Edit assistant in subtab
  const [editAssistantId, setEditAssistantId] = useState<number | null>(null);
  const [editAssistantSubtabData, setEditAssistantSubtabData] = useState<
    any | null
  >(null);
  const [editAssistantSubtabError, setEditAssistantSubtabError] =
    useState<string>('');
  const [editAssistantSubtabSuccess, setEditAssistantSubtabSuccess] =
    useState<string>('');

  const loadEditAssistantDetails = async (id: number) => {
    setEditAssistantSubtabError('');
    try {
      const response = await api.get(apiEndpoints.getAssistantById(id));
      if (response && response.ok) {
        const data = await response.json();
        setEditAssistantSubtabData({
          store: data.store_id || data.store || 0,
          assistant_id: data.assistant_id || '',
          assistant_provider: data.assistant_provider || 'retell',
          phone_number: data.phone_number || '',
          retell_voice_id: data.retell_voice_id || '',
          retell_llm_model: data.retell_llm_model || '',
          language: data.language || 'en',
          is_active: data.is_active !== undefined ? data.is_active : true,
        });
      } else {
        setEditAssistantSubtabData(null);
        setEditAssistantSubtabError('Failed to load agent details');
      }
    } catch {
      setEditAssistantSubtabData(null);
      setEditAssistantSubtabError('Failed to load agent details');
    }
  };

  const handleEditAssistantSubtabChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    setEditAssistantSubtabData((prev: any) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : name === 'store'
            ? value === ''
              ? 0
              : parseInt(value, 10) || 0
            : value,
    }));
  };

  const handleEditAssistantSubtabSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editAssistantId || !editAssistantSubtabData) return;
    setEditAssistantSubtabError('');
    setEditAssistantSubtabSuccess('');
    try {
      const response = await api.patch(
        apiEndpoints.updateAssistant(editAssistantId),
        {
          store: editAssistantSubtabData.store,
          assistant_id: editAssistantSubtabData.assistant_id.trim(),
          assistant_provider: editAssistantSubtabData.assistant_provider.trim(),
          phone_number: editAssistantSubtabData.phone_number.trim() || null,
          retell_voice_id:
            editAssistantSubtabData.retell_voice_id?.trim() || null,
          retell_llm_model:
            editAssistantSubtabData.retell_llm_model?.trim() || null,
          language: editAssistantSubtabData.language.trim(),
          is_active: editAssistantSubtabData.is_active,
        },
      );
      if (response && response.ok) {
        setEditAssistantSubtabSuccess('Agent updated successfully.');
        fetchAssistants(assistantsPage);
      } else if (response) {
        const data = await response.json();
        setEditAssistantSubtabError(
          data.detail || data.message || 'Failed to update agent',
        );
      } else {
        setEditAssistantSubtabError('Failed to update agent');
      }
    } catch {
      setEditAssistantSubtabError('Failed to update agent');
    }
  };

  // Fetch store groups when needed
  useEffect(() => {
    if (
      activeTab === 'store' ||
      activeTab === 'storeGroups' ||
      activeTab === 'user' ||
      activeTab === 'edit' ||
      activeTab === 'vipContacts'
    ) {
      fetchStoreGroups();
    }
  }, [activeTab, fetchStoreGroups]);

  // Fetch TSV requests
  const fetchTsvRequests = useCallback(async () => {
    setLoadingTsvRequests(true);
    try {
      const response = await api.get(apiEndpoints.getTollFreeVerifications());
      if (response && response.ok) {
        const data = await response.json();
        // Handle both array and paginated response
        if (Array.isArray(data)) {
          setTsvRequests(data);
        } else if (data.results && Array.isArray(data.results)) {
          setTsvRequests(data.results);
        } else {
          setTsvRequests([]);
        }
      } else {
        setTsvRequests([]);
      }
    } catch (error) {
      console.error('Failed to fetch TSV requests:', error);
      setTsvRequests([]);
    } finally {
      setLoadingTsvRequests(false);
    }
  }, []);

  // Fetch assistants
  const fetchAssistants = useCallback(
    async (page = 1) => {
      setLoadingAssistants(true);
      try {
        const response = await api.get(
          apiEndpoints.getAssistants(
            page,
            PAGE_SIZE,
            assistantsOrdering ?? undefined,
            debouncedAssistantsSearch || undefined,
          ),
        );
        if (response && response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setAssistants(data);
            setAssistantsTotalCount(data.length);
            setAssistantsTotalPages(1);
          } else if (data.results && Array.isArray(data.results)) {
            setAssistants(data.results);
            setAssistantsTotalCount(data.count || data.results.length);
            setAssistantsTotalPages(
              data.total_pages ||
                Math.ceil((data.count || data.results.length) / PAGE_SIZE),
            );
          } else {
            setAssistants([]);
          }
        } else {
          setAssistants([]);
        }
      } catch (error) {
        console.error('Failed to fetch assistants:', error);
        setAssistants([]);
      } finally {
        setLoadingAssistants(false);
      }
    },
    [assistantsOrdering, debouncedAssistantsSearch],
  );

  const fetchRetellKnowledgeBases = useCallback(async () => {
    setLoadingRetellKnowledgeBases(true);
    setRetellKnowledgeBaseError('');
    try {
      const response = await api.get(apiEndpoints.getRetellKnowledgeBases());
      if (response && response.ok) {
        const data = await response.json();
        const knowledgeBases = Array.isArray(data)
          ? data
          : Array.isArray(data.knowledge_bases)
            ? data.knowledge_bases
            : Array.isArray(data.results)
              ? data.results
              : [];
        setRetellKnowledgeBases(knowledgeBases);
      } else {
        setRetellKnowledgeBases([]);
        setRetellKnowledgeBaseError('Could not load knowledge bases.');
      }
    } catch (error) {
      console.error('Failed to fetch Retell knowledge bases:', error);
      setRetellKnowledgeBases([]);
      setRetellKnowledgeBaseError('Could not load knowledge bases.');
    } finally {
      setLoadingRetellKnowledgeBases(false);
    }
  }, []);

  useEffect(() => {
    if (
      activeTab === 'store' ||
      activeTab === 'storeGroups' ||
      activeTab === 'edit' ||
      isOnboardingOpen
    ) {
      fetchRetellKnowledgeBases();
    }
  }, [activeTab, fetchRetellKnowledgeBases, isOnboardingOpen]);

  const fetchRetellAgents = useCallback(async () => {
    setLoadingRetellAgents(true);
    try {
      const response = await api.get(apiEndpoints.getRetellAgents());
      if (response && response.ok) {
        const data = await response.json();
        const agents = Array.isArray(data)
          ? data
          : Array.isArray(data.agents)
            ? data.agents
            : Array.isArray(data.results)
              ? data.results
              : [];
        setRetellAgents(agents);
      } else {
        setRetellAgents([]);
      }
    } catch (error) {
      console.error('Failed to fetch Retell agents:', error);
      setRetellAgents([]);
    } finally {
      setLoadingRetellAgents(false);
    }
  }, []);

  const fetchApprovedUnassignedPhoneNumbers = useCallback(async () => {
    setLoadingApprovedUnassignedPhoneNumbers(true);
    try {
      const response = await api.get(
        apiEndpoints.getApprovedUnassignedPhoneNumbers(),
      );
      if (response && response.ok) {
        const data = await response.json();
        const phoneNumbers = Array.isArray(data)
          ? data
          : Array.isArray(data.phone_numbers)
            ? data.phone_numbers
            : Array.isArray(data.results)
              ? data.results
              : [];
        setApprovedUnassignedPhoneNumbers(phoneNumbers);
      } else {
        setApprovedUnassignedPhoneNumbers([]);
      }
    } catch (error) {
      console.error(
        'Failed to fetch approved unassigned phone numbers:',
        error,
      );
      setApprovedUnassignedPhoneNumbers([]);
    } finally {
      setLoadingApprovedUnassignedPhoneNumbers(false);
    }
  }, []);

  useEffect(() => {
    if (isOnboardingOpen && obStep === 3) {
      fetchRetellAgents();
      fetchApprovedUnassignedPhoneNumbers();
    }
  }, [
    fetchApprovedUnassignedPhoneNumbers,
    fetchRetellAgents,
    isOnboardingOpen,
    obStep,
  ]);

  // Fetch assistants when assistant tab is active
  useEffect(() => {
    if (activeTab === 'assistant') {
      fetchStores(storesPage);
      fetchAssistants(assistantsPage);
      fetchRetellAgents();
      fetchApprovedUnassignedPhoneNumbers();
    }
  }, [
    activeTab,
    assistantsPage,
    fetchApprovedUnassignedPhoneNumbers,
    fetchAssistants,
    fetchRetellAgents,
    fetchStores,
    storesPage,
  ]);

  // Legacy TSV requests are hidden behind the new TFV tab.
  useEffect(() => {
    if (false && activeTab === 'tsv') {
      fetchTsvRequests();
    }
  }, [activeTab, fetchTsvRequests]);

  const refreshTsvStatus = useCallback(async () => {
    setRefreshingTsvStatus(true);
    setTsvRefreshError('');
    try {
      const response = await api.post(apiEndpoints.refreshTollFreeStatus());
      if (response && response.ok) {
        await response.json().catch(() => null);
      } else if (response) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          try {
            const data = await response.json();
            setTsvRefreshError(
              data.detail ||
                data.message ||
                data.error ||
                `Failed to refresh Toll-Free status (Status: ${response.status})`,
            );
          } catch (parseError) {
            setTsvRefreshError(
              `Failed to refresh Toll-Free status (Status: ${response.status}). Could not parse error response.`,
            );
          }
        } else {
          const text = await response.text();
          setTsvRefreshError(
            `Failed to refresh Toll-Free status (Status: ${response.status}). The server returned an error page.`,
          );
          console.error('Non-JSON response:', text.substring(0, 200));
        }
      } else {
        setTsvRefreshError(
          'Failed to refresh Toll-Free status: No response from server',
        );
      }
    } catch (err: any) {
      console.error('Refresh Toll-Free status error:', err);
      let errorMessage = 'An unexpected error occurred. Please try again.';
      if (err instanceof Error) {
        errorMessage = err.message;
        if (err.message.includes('JSON') || err.message.includes('DOCTYPE')) {
          errorMessage =
            'The server returned an unexpected response. Please check that the API endpoint is correct and the server is running.';
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setTsvRefreshError(errorMessage);
    } finally {
      setRefreshingTsvStatus(false);
    }

    await fetchTsvRequests();
  }, [fetchTsvRequests]);

  useEffect(() => {
    if (false && activeTab === 'tsv') {
      refreshTsvStatus();
    }
  }, [activeTab, refreshTsvStatus]);

  const fetchPlans = useCallback(async () => {
    setLoadingPlans(true);
    setPlanError(null);
    try {
      const res = await api.get(apiEndpoints.getPlans());
      if (res && res.ok) {
        const data = await res.json();
        setPlans(Array.isArray(data) ? data : data.results || []);
      } else {
        throw new Error('Failed to load plans');
      }
    } catch (err: any) {
      setPlanError(err.message || 'An error occurred fetching plans');
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  useEffect(() => {
    if (
      activeTab === 'onboardingBilling' &&
      onboardingBillingSubtab === 'plans'
    ) {
      fetchPlans();
    }
  }, [activeTab, onboardingBillingSubtab, fetchPlans]);

  const handleSavePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    setPlanError(null);
    setPlanSuccess(null);
    try {
      const payload = {
        ...planFormData,
        trial_period_days:
          planFormData.trial_period_days === ''
            ? null
            : Number(planFormData.trial_period_days),
      };

      let res;
      if (selectedPlan) {
        res = await api.patch(
          `${apiEndpoints.getPlans()}${selectedPlan.id}/`,
          payload,
        );
      } else {
        res = await api.post(apiEndpoints.getPlans(), payload);
      }

      if (!res || !res.ok) {
        const err = await res?.json().catch(() => ({}));
        throw new Error(
          err?.detail || err?.non_field_errors?.[0] || 'Failed to save plan',
        );
      }

      setPlanSuccess(
        selectedPlan
          ? 'Plan updated successfully'
          : 'Plan created successfully',
      );
      setIsPlanModalOpen(false);
      fetchPlans();
    } catch (err: any) {
      setPlanError(err.message || 'An error occurred saving plan');
    }
  };

  const handleDeletePlan = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    setPlanError(null);
    setPlanSuccess(null);
    try {
      const res = await api.delete(`${apiEndpoints.getPlans()}${id}/`);
      if (!res || !res.ok) {
        throw new Error('Failed to delete plan');
      }
      setPlanSuccess('Plan deleted successfully');
      fetchPlans();
    } catch (err: any) {
      setPlanError(err.message || 'An error occurred deleting plan');
    }
  };

  const handleReorderPlan = async (index: number, direction: 'up' | 'down') => {
    if (reorderLoading) return;
    const newPlans = [...plans];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newPlans.length) return;

    const temp = newPlans[index];
    newPlans[index] = newPlans[targetIndex];
    newPlans[targetIndex] = temp;

    setPlans(newPlans);
    setReorderLoading(true);
    setPlanError(null);
    try {
      const planIds = newPlans.map((p) => p.id);
      const res = await api.post(`${apiEndpoints.getPlans()}reorder/`, {
        plan_ids: planIds,
      });
      if (!res || !res.ok) {
        throw new Error('Failed to save display order');
      }
      setPlanSuccess('Plans reordered successfully');
      fetchPlans();
    } catch (err: any) {
      setPlanError(err.message || 'Failed to reorder plans');
      fetchPlans();
    } finally {
      setReorderLoading(false);
    }
  };

  const loadStoreCurrentSubscription = async (storeId: number) => {
    setCustomPricingError(null);
    try {
      const res = await api.get(apiEndpoints.getBillingSubscription(), {
        headers: { 'X-Store-ID': String(storeId) },
      });
      if (res && res.status === 404) {
        setCustomPricingNote('');
        setContractStart('');
        setContractEnd('');
        setCustomPricingSource('default');
        return;
      }
      if (res && res.ok) {
        const sub = await res.json();
        setSelectedCustomPlanCode(sub.plan?.code || sub.plan_code || '');
        setCustomPricingSource(sub.pricing_source || 'default');
        setCustomPricingNote(sub.custom_pricing_note || '');
        setContractStart(
          sub.contract_start ? sub.contract_start.substring(0, 16) : '',
        );
        setContractEnd(
          sub.contract_end ? sub.contract_end.substring(0, 16) : '',
        );

        setCustomBasePriceCents(sub.base_price_cents ?? 0);
        setCustomIncludedMinutes(sub.included_minutes ?? 0);
        setCustomOverageRateCents(sub.overage_rate_cents_per_minute ?? 0);
        setCustomSetupFeeCents(sub.setup_fee_cents ?? 0);
        setCustomPerOrderRateCents(sub.per_order_rate_cents ?? 0);

        setCustomMultilingualCents(sub.multilingual_addon_cents ?? 0);
        setCustomMultilingualIncluded(sub.multilingual_included ?? false);
        setCustomMultilingualActive(sub.multilingual_addon_active ?? false);

        setCustomSmsOrderingCents(sub.sms_ordering_addon_cents ?? 0);
        setCustomSmsOrderingIncluded(sub.sms_ordering_included ?? false);
        setCustomSmsOrderingActive(sub.sms_ordering_addon_active ?? false);

        setCustomOpentableResyCents(sub.opentable_resy_addon_cents ?? 0);
        setCustomOpentableResyIncluded(sub.opentable_resy_included ?? false);
        setCustomOpentableResyActive(sub.opentable_resy_addon_active ?? false);
      }
    } catch (err: any) {
      console.error('Error fetching store subscription:', err);
    }
  };

  const handleBasePlanChange = (planCode: string) => {
    setSelectedCustomPlanCode(planCode);
    const plan = plans.find((p) => p.code === planCode);
    if (plan && customPricingSource === 'default') {
      setCustomBasePriceCents(plan.base_price_cents);
      setCustomIncludedMinutes(plan.included_minutes);
      setCustomOverageRateCents(plan.overage_rate_cents_per_minute);
      setCustomSetupFeeCents(plan.setup_fee_cents);
      setCustomPerOrderRateCents(plan.per_order_rate_cents);

      setCustomMultilingualCents(plan.multilingual_addon_cents);
      setCustomMultilingualIncluded(plan.multilingual_included);

      setCustomSmsOrderingCents(plan.sms_ordering_addon_cents);
      setCustomSmsOrderingIncluded(plan.sms_ordering_included);

      setCustomOpentableResyCents(plan.opentable_resy_addon_cents);
      setCustomOpentableResyIncluded(plan.opentable_resy_included);
    }
  };

  const handleApplyCustomPricing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomStoreId) {
      setCustomPricingError('Please select a store');
      return;
    }
    if (!selectedCustomPlanCode) {
      setCustomPricingError('Please select a base plan');
      return;
    }
    setCustomPricingError(null);
    setCustomPricingSuccess(null);
    setCustomPricingLoading(true);
    try {
      const payload: any = {
        store_id: selectedCustomStoreId,
        plan_code: selectedCustomPlanCode,
        pricing_source: customPricingSource,
        custom_pricing_note: customPricingNote,
        contract_start: contractStart || null,
        contract_end: contractEnd || null,
      };

      if (customPricingSource === 'custom') {
        payload.base_price_cents = customBasePriceCents;
        payload.included_minutes = customIncludedMinutes;
        payload.overage_rate_cents_per_minute = customOverageRateCents;
        payload.setup_fee_cents = customSetupFeeCents;
        payload.per_order_rate_cents = customPerOrderRateCents;

        payload.multilingual_addon_cents = customMultilingualCents;
        payload.multilingual_included = customMultilingualIncluded;
        payload.multilingual_addon_active = customMultilingualActive;

        payload.sms_ordering_addon_cents = customSmsOrderingCents;
        payload.sms_ordering_included = customSmsOrderingIncluded;
        payload.sms_ordering_addon_active = customSmsOrderingActive;

        payload.opentable_resy_addon_cents = customOpentableResyCents;
        payload.opentable_resy_included = customOpentableResyIncluded;
        payload.opentable_resy_addon_active = customOpentableResyActive;
      }

      const res = await api.post(
        apiEndpoints.getBillingSubscription(),
        payload,
      );
      if (!res || !res.ok) {
        const err = await res?.json().catch(() => ({}));
        throw new Error(
          err?.detail ||
            err?.non_field_errors?.[0] ||
            'Failed to apply subscription override',
        );
      }

      setCustomPricingSuccess('Subscription override applied successfully');
    } catch (err: any) {
      setCustomPricingError(
        err.message || 'An error occurred applying override',
      );
    } finally {
      setCustomPricingLoading(false);
    }
  };

  const handleStoreChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const nextValue = name.endsWith('_ext')
      ? phoneExtensionInput(value)
      : value;
    setStoreFormData({
      ...storeFormData,
      [name]:
        type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : type === 'number'
            ? parseInt(value, 10) || 0
            : name === 'store_group'
              ? value === ''
                ? null
                : parseInt(value as string, 10) || null
              : nextValue,
    });
  };

  const handleStoreLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setStoreFormData({
      ...storeFormData,
      logo: file,
    });
  };

  // Store Group creation UI removed

  const handleStoreSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStoreError('');
    setStoreSuccess('');

    if (!accessToken) {
      setStoreError('You must be logged in to create a store');
      return;
    }

    try {
      // Build multipart form data for StoreSerializer + optional logo file
      const formData = new FormData();
      formData.append('name', storeFormData.name);
      if (storeFormData.phonetic_name) {
        formData.append('phonetic_name', storeFormData.phonetic_name);
      }
      formData.append('call_intro_sentence', storeFormData.call_intro_sentence);
      formData.append(
        'speach_filler_phrase',
        storeFormData.speach_filler_phrase,
      );
      formData.append('vip_intro_phrase', storeFormData.vip_intro_phrase);
      formData.append('agent_style', storeFormData.agent_style);
      formData.append(
        'use_default_call_intro_sentence',
        storeFormData.use_default_call_intro_sentence ? 'true' : 'false',
      );
      if (storeFormData.timezone)
        formData.append('timezone', storeFormData.timezone);
      if (storeFormData.address_line_1)
        formData.append('address_line_1', storeFormData.address_line_1);
      if (storeFormData.address_line_2)
        formData.append('address_line_2', storeFormData.address_line_2);
      if (storeFormData.city) formData.append('city', storeFormData.city);
      if (storeFormData.state) formData.append('state', storeFormData.state);
      if (storeFormData.postal_code)
        formData.append('postal_code', storeFormData.postal_code);
      if (storeFormData.country)
        formData.append('country', storeFormData.country);
      if (storeFormData.website)
        formData.append('website', storeFormData.website);
      if (storeFormData.menu_link)
        formData.append('menu_link', storeFormData.menu_link);
      if (storeFormData.knowledge_base_id.trim()) {
        formData.append(
          'knowledge_base_id',
          storeFormData.knowledge_base_id.trim(),
        );
      }
      if (storeFormData.ordering_link)
        formData.append('ordering_link', storeFormData.ordering_link);
      if (storeFormData.stripe_account_id)
        formData.append('stripe_account_id', storeFormData.stripe_account_id);
      if (storeFormData.reservation_link)
        formData.append('reservation_link', storeFormData.reservation_link);
      if (storeFormData.gift_card_link)
        formData.append('gift_card_link', storeFormData.gift_card_link);
      if (storeFormData.reservation_email)
        formData.append('reservation_email', storeFormData.reservation_email);
      if (storeFormData.location_secondary_phone_number)
        formData.append(
          'location_secondary_phone_number',
          storeFormData.location_secondary_phone_number,
        );
      formData.append(
        'location_secondary_phone_number_ext',
        storeFormData.location_secondary_phone_number_ext || '',
      );
      formData.append(
        'default_delivery_fee_cents',
        String(storeFormData.default_delivery_fee_cents ?? 0),
      );
      formData.append(
        'application_fee_default_cents',
        String(storeFormData.application_fee_default_cents ?? 0),
      );
      if (storeFormData.delivery_fee_label)
        formData.append('delivery_fee_label', storeFormData.delivery_fee_label);
      if (storeFormData.service_fee_label)
        formData.append('service_fee_label', storeFormData.service_fee_label);
      if (storeFormData.default_delivery_mode)
        formData.append(
          'default_delivery_mode',
          storeFormData.default_delivery_mode,
        );
      formData.append(
        'takes_reservations',
        storeFormData.takes_reservations ? 'true' : 'false',
      );
      formData.append(
        'takes_catering',
        storeFormData.takes_catering ? 'true' : 'false',
      );
      formData.append(
        'takes_events',
        storeFormData.takes_events ? 'true' : 'false',
      );
      formData.append(
        'agent_handles_events',
        storeFormData.agent_handles_events ? 'true' : 'false',
      );
      formData.append(
        'agent_handles_catering',
        storeFormData.agent_handles_catering ? 'true' : 'false',
      );
      formData.append(
        'outdoor_seating',
        storeFormData.outdoor_seating ? 'true' : 'false',
      );
      formData.append(
        'collect_payment_online',
        storeFormData.collect_payment_online ? 'true' : 'false',
      );
      formData.append(
        'accept_pay_on_site',
        storeFormData.accept_pay_on_site ? 'true' : 'false',
      );
      formData.append(
        'pass_through_dispatch_quote',
        storeFormData.pass_through_dispatch_quote ? 'true' : 'false',
      );
      formData.append(
        'send_delivery_cost_to_deliverect',
        storeFormData.send_delivery_cost_to_deliverect ? 'true' : 'false',
      );
      formData.append(
        'assess_tax_on_fees',
        storeFormData.assess_tax_on_fees ? 'true' : 'false',
      );
      formData.append(
        'make_fees_tax_inclusive',
        storeFormData.make_fees_tax_inclusive ? 'true' : 'false',
      );
      const fallbackTaxPercent = String(
        storeFormData.fallback_tax_percent ?? '',
      ).trim();
      if (fallbackTaxPercent !== '') {
        formData.append('fallback_tax_percent', fallbackTaxPercent);
      }
      formData.append(
        'monthly_minutes_limit',
        String(storeFormData.monthly_minutes_limit ?? 0),
      );
      if (storeFormData.ot_max_party_size !== '')
        formData.append(
          'ot_max_party_size',
          String(storeFormData.ot_max_party_size),
        );
      if (storeFormData.average_reservation_cover !== '')
        formData.append(
          'average_reservation_cover',
          storeFormData.average_reservation_cover,
        );
      if (storeFormData.google_price_level !== '')
        formData.append('google_price_level', storeFormData.google_price_level);
      normalizeExplicitOnlyTableTypes(
        storeFormData.explicit_only_table_types,
      ).forEach((tableType) => {
        formData.append('explicit_only_table_types', tableType);
      });
      formData.append(
        'annotate_opentable_reservation_notes',
        storeFormData.annotate_opentable_reservation_notes ? 'true' : 'false',
      );
      formData.append(
        'opentable_reservation_note_annotation_position',
        storeFormData.opentable_reservation_note_annotation_position ||
          'prepend',
      );
      formData.append(
        'opentable_reservation_note_annotation',
        storeFormData.opentable_reservation_note_annotation || '',
      );
      formData.append('livemode', storeFormData.livemode ? 'true' : 'false');
      formData.append(
        'accepts_deliveries',
        storeFormData.accepts_deliveries ? 'true' : 'false',
      );
      formData.append(
        'accepts_phone_orders',
        storeFormData.accepts_phone_orders ? 'true' : 'false',
      );
      formData.append(
        'collect_email',
        storeFormData.collect_email ? 'true' : 'false',
      );
      formData.append(
        'posless_integration',
        storeFormData.posless_integration ? 'true' : 'false',
      );
      appendHumanTransferFields(formData, storeFormData);
      formData.append(
        'show_sms_on_dashboard',
        storeFormData.show_sms_on_dashboard ? 'true' : 'false',
      );
      formData.append(
        'manual_menu_snoozing_enabled',
        storeFormData.manual_menu_snoozing_enabled ? 'true' : 'false',
      );
      formData.append(
        'cross_sell_enabled',
        storeFormData.cross_sell_enabled ? 'true' : 'false',
      );
      formData.append(
        'forward_late_reservations_to_text',
        storeFormData.forward_late_reservations_to_text ? 'true' : 'false',
      );
      formData.append(
        'forward_reservation_cancelation_failures_to_text',
        storeFormData.forward_reservation_cancelation_failures_to_text
          ? 'true'
          : 'false',
      );
      formData.append(
        'current_pos_system',
        storeFormData.current_pos_system || 'UNKNOWN',
      );
      formData.append(
        'forward_order_requests_to_store',
        storeFormData.forward_order_requests_to_store ? 'true' : 'false',
      );
      formData.append(
        'forward_order_requests_to_text_link',
        storeFormData.forward_order_requests_to_text_link ? 'true' : 'false',
      );
      formData.append(
        'forward_order_requests_to_phone',
        storeFormData.forward_order_requests_to_phone ? 'true' : 'false',
      );
      if (storeFormData.order_requests_phone_number.trim()) {
        formData.append(
          'order_requests_phone_number',
          storeFormData.order_requests_phone_number.trim(),
        );
      }
      formData.append(
        'order_requests_phone_number_ext',
        storeFormData.order_requests_phone_number_ext || '',
      );
      formData.append(
        'forward_catering_requests_to_store',
        storeFormData.forward_catering_requests_to_store ? 'true' : 'false',
      );
      formData.append(
        'forward_catering_requests_to_text_link',
        storeFormData.forward_catering_requests_to_text_link ? 'true' : 'false',
      );
      formData.append(
        'forward_event_requests_to_store',
        storeFormData.forward_event_requests_to_store ? 'true' : 'false',
      );
      formData.append(
        'forward_event_requests_to_text_link',
        storeFormData.forward_event_requests_to_text_link ? 'true' : 'false',
      );
      if (storeFormData.catering_link.trim()) {
        formData.append('catering_link', storeFormData.catering_link.trim());
      }
      if (storeFormData.catering_outreach_contact.trim()) {
        formData.append(
          'catering_outreach_contact',
          storeFormData.catering_outreach_contact.trim(),
        );
      }
      if (storeFormData.catering_forwarding_number.trim()) {
        formData.append(
          'catering_forwarding_number',
          storeFormData.catering_forwarding_number.trim(),
        );
      }
      formData.append(
        'catering_forwarding_number_ext',
        storeFormData.catering_forwarding_number_ext || '',
      );
      if (storeFormData.event_forwarding_number.trim()) {
        formData.append(
          'event_forwarding_number',
          storeFormData.event_forwarding_number.trim(),
        );
      }
      formData.append(
        'event_forwarding_number_ext',
        storeFormData.event_forwarding_number_ext || '',
      );
      if (storeFormData.parking_info)
        formData.append('parking_info', storeFormData.parking_info);
      if (storeFormData.business_number.trim()) {
        formData.append(
          'business_number',
          storeFormData.business_number.trim(),
        );
      }
      if (storeFormData.how_to_order_delivery_message) {
        formData.append(
          'how_to_order_delivery_message',
          storeFormData.how_to_order_delivery_message,
        );
      }
      if (storeFormData.store_group) {
        formData.append('store_group', String(storeFormData.store_group));
      } else if (storeFormData.store_group_name) {
        formData.append('store_group_name', storeFormData.store_group_name);
      }
      if (storeFormData.logo) {
        formData.append('logo', storeFormData.logo);
      }

      let response = await api.postForm(apiEndpoints.createStore(), formData);

      // Fallback to legacy route if prod is still on older path
      if (response && response.status === 404) {
        response = await api.postForm(
          apiEndpoints.createStoreLegacy(),
          formData,
        );
      }

      if (response && response.ok) {
        const data = await response.json();
        setStoreSuccess(`Store "${data.name}" created successfully!`);
        setStoreFormData({
          name: '',
          phonetic_name: '',
          call_intro_sentence: '',
          speach_filler_phrase: 'Sure, one moment please',
          vip_intro_phrase:
            'Hello {{vip_caller_name}}! Thanks for being a valued member at {{store_name}}.',
          agent_style: '',
          use_default_call_intro_sentence: true,
          legal_name: '',
          timezone: 'America/New_York',
          address_line_1: '',
          address_line_2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'US',
          website: '',
          menu_link: '',
          knowledge_base_id: '',
          ordering_link: '',
          stripe_account_id: '',
          reservation_link: '',
          gift_card_link: '',
          reservation_email: '',
          location_secondary_phone_number: '',
          location_secondary_phone_number_ext: '',
          default_delivery_fee_cents: 0,
          pass_through_dispatch_quote: true,
          send_delivery_cost_to_deliverect: true,
          assess_tax_on_fees: false,
          make_fees_tax_inclusive: false,
          fallback_tax_percent: '',
          application_fee_default_cents: 50,
          delivery_fee_label: '',
          service_fee_label: '',
          default_delivery_mode: 'none',
          takes_reservations: false,
          takes_catering: false,
          takes_events: false,
          agent_handles_events: false,
          agent_handles_catering: false,
          outdoor_seating: false,
          collect_payment_online: true,
          accept_pay_on_site: false,
          monthly_minutes_limit: 500,
          ot_max_party_size: '',
          average_reservation_cover: '',
          google_price_level: '',
          explicit_only_table_types: [],
          annotate_opentable_reservation_notes: false,
          opentable_reservation_note_annotation_position: 'prepend',
          opentable_reservation_note_annotation: '',
          livemode: false,
          store_group: null,
          store_group_name: '',
          logo: null,
          accepts_deliveries: false,
          how_to_order_delivery_message: '',
          accepts_phone_orders: true,
          collect_email: false,
          posless_integration: false,
          ...humanTransferFormFields(),
          current_pos_system: 'UNKNOWN',
          poshub_toast_group_id: '',
          forward_order_requests_to_store: false,
          forward_order_requests_to_text_link: false,
          forward_order_requests_to_phone: false,
          order_requests_phone_number: '',
          order_requests_phone_number_ext: '',
          forward_catering_requests_to_store: false,
          forward_catering_requests_to_text_link: false,
          forward_event_requests_to_store: false,
          forward_event_requests_to_text_link: false,
          catering_outreach_contact: '',
          catering_forwarding_number: '',
          catering_forwarding_number_ext: '',
          catering_link: '',
          event_forwarding_number: '',
          event_forwarding_number_ext: '',
          event_inquiry_questions: [],
          catering_inquiry_questions: [],
          parking_info: '',
          business_number: '',
          show_sms_on_dashboard: false,
          manual_menu_snoozing_enabled: false,
          cross_sell_enabled: true,
          forward_late_reservations_to_text: false,
          forward_reservation_cancelation_failures_to_text: false,
        });
        fetchStores(storesPage);
        refreshUser();
      } else {
        if (response) {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await response.json().catch(() => ({}));
            setStoreError(
              (data && (data.detail || data.message)) ||
                'Failed to create store',
            );
          } else {
            const text = await response.text();
            setStoreError(text || 'Failed to create store');
          }
        } else {
          setStoreError('Failed to create store');
        }
      }
    } catch (err: any) {
      console.error('Store creation error:', err);
      let errorMessage = 'An unexpected error occurred. Please try again.';
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setStoreError(errorMessage);
    }
  };

  // Store Group creation UI removed

  const handleTwilioConfigChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTwilioConfigFormData({
      ...twilioConfigFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTwilioConfigSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwilioConfigError('');
    setTwilioConfigSuccess('');
    setSubmittingTwilioConfig(true);

    if (!accessToken) {
      setTwilioConfigError(
        'You must be logged in to configure Twilio webhooks',
      );
      setSubmittingTwilioConfig(false);
      return;
    }

    if (!twilioConfigFormData.phone_sid.trim()) {
      setTwilioConfigError('Phone SID is required');
      setSubmittingTwilioConfig(false);
      return;
    }

    try {
      const response = await api.post(apiEndpoints.configureTwilio(), {
        phone_sid: twilioConfigFormData.phone_sid.trim(),
      });

      if (response && response.ok) {
        await response.json().catch(() => null);
        setTwilioConfigSuccess(
          'Twilio Elastic SIP and SMS webhook configured successfully!',
        );
        setTwilioConfigFormData({ phone_sid: '' });
      } else if (response) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          try {
            const data = await response.json();
            setTwilioConfigError(
              data.detail ||
                data.message ||
                data.error ||
                `Failed to configure Twilio (Status: ${response.status})`,
            );
          } catch (parseError) {
            setTwilioConfigError(
              `Failed to configure Twilio (Status: ${response.status}). Could not parse error response.`,
            );
          }
        } else {
          const text = await response.text();
          setTwilioConfigError(
            `Failed to configure Twilio (Status: ${response.status}). The server returned an error page.`,
          );
          console.error('Non-JSON response:', text.substring(0, 200));
        }
      } else {
        setTwilioConfigError(
          'Failed to configure Twilio: No response from server',
        );
      }
    } catch (err: any) {
      console.error('Twilio configuration error:', err);
      let errorMessage = 'An unexpected error occurred. Please try again.';
      if (err instanceof Error) {
        errorMessage = err.message;
        if (err.message.includes('JSON') || err.message.includes('DOCTYPE')) {
          errorMessage =
            'The server returned an unexpected response. Please check that the API endpoint is correct and the server is running.';
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setTwilioConfigError(errorMessage);
    } finally {
      setSubmittingTwilioConfig(false);
    }
  };

  const handleTsvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTsvFormData({
      ...tsvFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTsvSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTsvError('');
    setTsvSuccess('');
    setSubmittingTsv(true);

    if (!accessToken) {
      setTsvError('You must be logged in to submit TSV');
      setSubmittingTsv(false);
      return;
    }

    if (!tsvFormData.phone_sid.trim()) {
      setTsvError('Phone SID is required');
      setSubmittingTsv(false);
      return;
    }

    try {
      const response = await api.post(apiEndpoints.submitTsv(), {
        phone_sid: tsvFormData.phone_sid.trim(),
      });

      if (response && response.ok) {
        const data = await response.json();
        setTsvSuccess('TSV submitted successfully!');
        setTsvFormData({
          phone_sid: '',
        });
        // Refresh the TSV requests list
        fetchTsvRequests();
      } else {
        if (response) {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            try {
              const data = await response.json();
              setTsvError(
                data.detail ||
                  data.message ||
                  data.error ||
                  `Failed to submit TSV (Status: ${response.status})`,
              );
            } catch (parseError) {
              setTsvError(
                `Failed to submit TSV (Status: ${response.status}). Could not parse error response.`,
              );
            }
          } else {
            const text = await response.text();
            setTsvError(
              `Failed to submit TSV (Status: ${response.status}). The server returned an error page.`,
            );
            console.error('Non-JSON response:', text.substring(0, 200));
          }
        } else {
          setTsvError('Failed to submit TSV: No response from server');
        }
      }
    } catch (err: any) {
      console.error('TSV submission error:', err);
      let errorMessage = 'An unexpected error occurred. Please try again.';
      if (err instanceof Error) {
        errorMessage = err.message;
        if (err.message.includes('JSON') || err.message.includes('DOCTYPE')) {
          errorMessage =
            'The server returned an unexpected response. Please check that the API endpoint is correct and the server is running.';
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setTsvError(errorMessage);
    } finally {
      setSubmittingTsv(false);
    }
  };

  const handleObStoreSubmit = useCallback(async () => {
    setObError('');
    setObSuccess('');
    setObSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', storeFormData.name);
      if (storeFormData.phonetic_name)
        formData.append('phonetic_name', storeFormData.phonetic_name);
      formData.append('call_intro_sentence', storeFormData.call_intro_sentence);
      formData.append(
        'speach_filler_phrase',
        storeFormData.speach_filler_phrase,
      );
      formData.append('vip_intro_phrase', storeFormData.vip_intro_phrase);
      formData.append('agent_style', storeFormData.agent_style);
      formData.append(
        'use_default_call_intro_sentence',
        storeFormData.use_default_call_intro_sentence ? 'true' : 'false',
      );
      if (storeFormData.timezone)
        formData.append('timezone', storeFormData.timezone);
      if (storeFormData.address_line_1)
        formData.append('address_line_1', storeFormData.address_line_1);
      if (storeFormData.address_line_2)
        formData.append('address_line_2', storeFormData.address_line_2);
      if (storeFormData.city) formData.append('city', storeFormData.city);
      if (storeFormData.state) formData.append('state', storeFormData.state);
      if (storeFormData.postal_code)
        formData.append('postal_code', storeFormData.postal_code);
      if (storeFormData.country)
        formData.append('country', storeFormData.country);
      if (storeFormData.website)
        formData.append('website', storeFormData.website);
      if (storeFormData.menu_link)
        formData.append('menu_link', storeFormData.menu_link);
      if (storeFormData.knowledge_base_id.trim())
        formData.append(
          'knowledge_base_id',
          storeFormData.knowledge_base_id.trim(),
        );
      if (storeFormData.ordering_link)
        formData.append('ordering_link', storeFormData.ordering_link);
      if (storeFormData.stripe_account_id)
        formData.append('stripe_account_id', storeFormData.stripe_account_id);
      if (storeFormData.reservation_link)
        formData.append('reservation_link', storeFormData.reservation_link);
      if (storeFormData.reservation_email)
        formData.append('reservation_email', storeFormData.reservation_email);
      if (storeFormData.location_secondary_phone_number)
        formData.append(
          'location_secondary_phone_number',
          storeFormData.location_secondary_phone_number,
        );
      formData.append(
        'location_secondary_phone_number_ext',
        storeFormData.location_secondary_phone_number_ext || '',
      );
      formData.append(
        'default_delivery_fee_cents',
        String(storeFormData.default_delivery_fee_cents ?? 0),
      );
      formData.append(
        'application_fee_default_cents',
        String(storeFormData.application_fee_default_cents ?? 0),
      );
      if (storeFormData.delivery_fee_label)
        formData.append('delivery_fee_label', storeFormData.delivery_fee_label);
      if (storeFormData.service_fee_label)
        formData.append('service_fee_label', storeFormData.service_fee_label);
      if (storeFormData.default_delivery_mode)
        formData.append(
          'default_delivery_mode',
          storeFormData.default_delivery_mode,
        );
      formData.append(
        'takes_reservations',
        storeFormData.takes_reservations ? 'true' : 'false',
      );
      formData.append(
        'takes_catering',
        storeFormData.takes_catering ? 'true' : 'false',
      );
      formData.append(
        'takes_events',
        storeFormData.takes_events ? 'true' : 'false',
      );
      formData.append(
        'agent_handles_events',
        storeFormData.agent_handles_events ? 'true' : 'false',
      );
      formData.append(
        'agent_handles_catering',
        storeFormData.agent_handles_catering ? 'true' : 'false',
      );
      formData.append(
        'outdoor_seating',
        storeFormData.outdoor_seating ? 'true' : 'false',
      );
      formData.append(
        'collect_payment_online',
        storeFormData.collect_payment_online ? 'true' : 'false',
      );
      formData.append(
        'accept_pay_on_site',
        storeFormData.accept_pay_on_site ? 'true' : 'false',
      );
      formData.append(
        'pass_through_dispatch_quote',
        storeFormData.pass_through_dispatch_quote ? 'true' : 'false',
      );
      formData.append(
        'send_delivery_cost_to_deliverect',
        storeFormData.send_delivery_cost_to_deliverect ? 'true' : 'false',
      );
      formData.append(
        'assess_tax_on_fees',
        storeFormData.assess_tax_on_fees ? 'true' : 'false',
      );
      formData.append(
        'make_fees_tax_inclusive',
        storeFormData.make_fees_tax_inclusive ? 'true' : 'false',
      );
      const fallbackTaxPercent = String(
        storeFormData.fallback_tax_percent ?? '',
      ).trim();
      if (fallbackTaxPercent !== '') {
        formData.append('fallback_tax_percent', fallbackTaxPercent);
      }
      formData.append(
        'monthly_minutes_limit',
        String(storeFormData.monthly_minutes_limit ?? 0),
      );
      if (storeFormData.ot_max_party_size !== '')
        formData.append(
          'ot_max_party_size',
          String(storeFormData.ot_max_party_size),
        );
      if (storeFormData.average_reservation_cover !== '')
        formData.append(
          'average_reservation_cover',
          storeFormData.average_reservation_cover,
        );
      if (storeFormData.google_price_level !== '')
        formData.append('google_price_level', storeFormData.google_price_level);
      normalizeExplicitOnlyTableTypes(
        storeFormData.explicit_only_table_types,
      ).forEach((tableType) => {
        formData.append('explicit_only_table_types', tableType);
      });
      formData.append(
        'annotate_opentable_reservation_notes',
        storeFormData.annotate_opentable_reservation_notes ? 'true' : 'false',
      );
      formData.append(
        'opentable_reservation_note_annotation_position',
        storeFormData.opentable_reservation_note_annotation_position ||
          'prepend',
      );
      formData.append(
        'opentable_reservation_note_annotation',
        storeFormData.opentable_reservation_note_annotation || '',
      );
      formData.append('livemode', storeFormData.livemode ? 'true' : 'false');
      formData.append(
        'accepts_deliveries',
        storeFormData.accepts_deliveries ? 'true' : 'false',
      );
      formData.append(
        'accepts_phone_orders',
        storeFormData.accepts_phone_orders ? 'true' : 'false',
      );
      formData.append(
        'collect_email',
        storeFormData.collect_email ? 'true' : 'false',
      );
      formData.append(
        'posless_integration',
        storeFormData.posless_integration ? 'true' : 'false',
      );
      appendHumanTransferFields(formData, storeFormData);
      formData.append(
        'show_sms_on_dashboard',
        storeFormData.show_sms_on_dashboard ? 'true' : 'false',
      );
      formData.append(
        'manual_menu_snoozing_enabled',
        storeFormData.manual_menu_snoozing_enabled ? 'true' : 'false',
      );
      formData.append(
        'cross_sell_enabled',
        storeFormData.cross_sell_enabled ? 'true' : 'false',
      );
      formData.append(
        'forward_late_reservations_to_text',
        storeFormData.forward_late_reservations_to_text ? 'true' : 'false',
      );
      formData.append(
        'forward_reservation_cancelation_failures_to_text',
        storeFormData.forward_reservation_cancelation_failures_to_text
          ? 'true'
          : 'false',
      );
      formData.append(
        'current_pos_system',
        storeFormData.current_pos_system || 'UNKNOWN',
      );
      formData.append(
        'forward_order_requests_to_store',
        storeFormData.forward_order_requests_to_store ? 'true' : 'false',
      );
      formData.append(
        'forward_order_requests_to_text_link',
        storeFormData.forward_order_requests_to_text_link ? 'true' : 'false',
      );
      formData.append(
        'forward_order_requests_to_phone',
        storeFormData.forward_order_requests_to_phone ? 'true' : 'false',
      );
      if (storeFormData.order_requests_phone_number.trim()) {
        formData.append(
          'order_requests_phone_number',
          storeFormData.order_requests_phone_number.trim(),
        );
      }
      formData.append(
        'order_requests_phone_number_ext',
        storeFormData.order_requests_phone_number_ext || '',
      );
      formData.append(
        'forward_catering_requests_to_store',
        storeFormData.forward_catering_requests_to_store ? 'true' : 'false',
      );
      formData.append(
        'forward_catering_requests_to_text_link',
        storeFormData.forward_catering_requests_to_text_link ? 'true' : 'false',
      );
      formData.append(
        'forward_event_requests_to_store',
        storeFormData.forward_event_requests_to_store ? 'true' : 'false',
      );
      formData.append(
        'forward_event_requests_to_text_link',
        storeFormData.forward_event_requests_to_text_link ? 'true' : 'false',
      );
      if (storeFormData.catering_link.trim()) {
        formData.append('catering_link', storeFormData.catering_link.trim());
      }
      if (storeFormData.catering_outreach_contact.trim()) {
        formData.append(
          'catering_outreach_contact',
          storeFormData.catering_outreach_contact.trim(),
        );
      }
      if (storeFormData.catering_forwarding_number.trim()) {
        formData.append(
          'catering_forwarding_number',
          storeFormData.catering_forwarding_number.trim(),
        );
      }
      formData.append(
        'catering_forwarding_number_ext',
        storeFormData.catering_forwarding_number_ext || '',
      );
      if (storeFormData.event_forwarding_number.trim()) {
        formData.append(
          'event_forwarding_number',
          storeFormData.event_forwarding_number.trim(),
        );
      }
      formData.append(
        'event_forwarding_number_ext',
        storeFormData.event_forwarding_number_ext || '',
      );
      if (storeFormData.parking_info)
        formData.append('parking_info', storeFormData.parking_info);
      if (storeFormData.business_number.trim()) {
        formData.append(
          'business_number',
          storeFormData.business_number.trim(),
        );
      }
      if (storeFormData.how_to_order_delivery_message)
        formData.append(
          'how_to_order_delivery_message',
          storeFormData.how_to_order_delivery_message,
        );
      if (storeFormData.store_group)
        formData.append('store_group', String(storeFormData.store_group));
      else if (storeFormData.store_group_name)
        formData.append('store_group_name', storeFormData.store_group_name);
      if (storeFormData.logo) formData.append('logo', storeFormData.logo);

      let response = await api.postForm(apiEndpoints.createStore(), formData);
      if (response && response.status === 404) {
        response = await api.postForm(
          apiEndpoints.createStoreLegacy(),
          formData,
        );
      }
      if (response && response.ok) {
        const data = await response.json();
        setObCreatedStoreId(data.id);
        setObCreatedStoreName(data.name);
        if (selectedStoreIntakeId && data.id) {
          await api
            .post(
              apiEndpoints.linkOnboardingIntakeStore(selectedStoreIntakeId),
              {
                store_id: data.id,
              },
            )
            .catch(() => null);
        }
        setObSuccess(`Store "${data.name}" created!`);
        fetchStores(storesPage);
        refreshUser();
        setTimeout(() => obNext(), 600);
      } else {
        if (response) {
          const ct = response.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const data = await response.json().catch(() => ({}));
            setObError(
              (data && (data.detail || data.message)) ||
                `Failed (status ${response.status})`,
            );
          } else {
            setObError(`Failed (status ${response.status})`);
          }
        } else {
          setObError('No response from server');
        }
      }
    } catch (err: any) {
      setObError(err?.message || 'Unexpected error');
    } finally {
      setObSubmitting(false);
    }
  }, [
    storeFormData,
    fetchStores,
    obNext,
    refreshUser,
    selectedStoreIntakeId,
    storesPage,
  ]);

  const handleObStoreHoursSubmit = useCallback(async () => {
    setObError('');
    setObSuccess('');
    if (!obCreatedStoreId) {
      setObError('Create a store before setting store hours.');
      return;
    }

    const invalidRow = obWeeklyHours.find(
      (row) => !row.is_closed && (!row.opening_time || !row.closing_time),
    );
    if (invalidRow) {
      setObError(
        `Please set both opening and closing times for ${invalidRow.day_of_week}, or mark it closed.`,
      );
      return;
    }

    setObSubmitting(true);
    try {
      const storeHeaders = {
        'X-Store-ID': String(obCreatedStoreId),
      };
      const sourceResponse = await api.patch(
        apiEndpoints.updateStoreHours(),
        { use_manual_hours: true },
        { headers: storeHeaders },
      );
      if (!sourceResponse?.ok) {
        const data = await sourceResponse?.json().catch(() => ({}));
        setObError(
          data?.detail ||
            data?.message ||
            'Failed to enable manual store hours.',
        );
        return;
      }

      const payload = {
        hours: obWeeklyHours.map(toOnboardingStoreHourPayload),
      };
      const response = await api.put(apiEndpoints.updateStoreHours(), payload, {
        headers: storeHeaders,
      });
      if (response && response.ok) {
        setObSuccess('Store hours saved.');
        setTimeout(() => obNext(), 600);
      } else if (response) {
        const data = await response.json().catch(() => ({}));
        setObError(
          data.detail || data.message || 'Failed to save store hours.',
        );
      } else {
        setObError('No response from server');
      }
    } catch (err: any) {
      setObError(err?.message || 'Unexpected error');
    } finally {
      setObSubmitting(false);
    }
  }, [obCreatedStoreId, obWeeklyHours, obNext]);

  const handleObUserSubmit = useCallback(async () => {
    setObError('');
    setObSuccess('');
    if (!obCreatedStoreId) {
      setObError(
        'No store created yet. Go back and create one first, or skip.',
      );
      return;
    }
    setObSubmitting(true);
    try {
      const payload = { ...obUserForm, store_id: obCreatedStoreId };
      const response = await api.post(apiEndpoints.createStoreUser(), payload);
      if (response && response.ok) {
        const data = await response.json();
        setObSuccess(`User "${data.email}" created as ${obUserForm.role}!`);
        setTimeout(() => obNext(), 600);
      } else {
        if (response) {
          const data = await response.json().catch(() => ({}));
          setObError(
            (data && (data.detail || data.message)) || 'Failed to create user',
          );
        } else {
          setObError('No response from server');
        }
      }
    } catch (err: any) {
      setObError(err?.message || 'Unexpected error');
    } finally {
      setObSubmitting(false);
    }
  }, [obUserForm, obCreatedStoreId, obNext]);

  const handleObAssistantSubmit = useCallback(async () => {
    setObError('');
    setObSuccess('');
    if (!obCreatedStoreId) {
      setObError(
        'No store created yet. Go back and create one first, or skip.',
      );
      return;
    }
    setObSubmitting(true);
    try {
      if (!obAssistantForm.assistant_id.trim()) {
        setObError('Agent ID is required');
        setObSubmitting(false);
        return;
      }

      const payload = {
        store: obCreatedStoreId,
        assistant_id: obAssistantForm.assistant_id.trim(),
        assistant_provider: obAssistantForm.assistant_provider.trim(),
        phone_number: obAssistantForm.phone_number.trim() || null,
        retell_llm_model: obAssistantForm.retell_llm_model.trim() || null,
        language: obAssistantForm.language.trim(),
        is_active: obAssistantForm.is_active,
      };
      const response = await api.post(apiEndpoints.createAssistant(), payload);
      if (response && response.ok) {
        setObSuccess('Agent created!');
        setTimeout(() => {
          setObStep(ONBOARDING_TOTAL_STEPS);
        }, 600);
      } else {
        if (response) {
          const data = await response.json().catch(() => ({}));
          setObError(
            (data && (data.detail || data.message)) || 'Failed to create agent',
          );
        } else {
          setObError('No response from server');
        }
      }
    } catch (err: any) {
      setObError(err?.message || 'Unexpected error');
    } finally {
      setObSubmitting(false);
    }
  }, [obAssistantForm, obCreatedStoreId]);

  const handleApplyIntakePrefill = useCallback(async () => {
    if (!intakePickerSelectedId) {
      setIntakePickerError('Select an onboarding intake first.');
      return;
    }

    setIntakePickerLoading(true);
    setIntakePickerError('');
    try {
      const response = await api.get(
        apiEndpoints.getOnboardingIntakePrefill(
          intakePickerSelectedId,
          intakePickerTarget,
        ),
      );
      if (!response?.ok) {
        setIntakePickerError('Failed to load onboarding intake prefill.');
        return;
      }

      const data = await response.json();
      const incoming = (data.payload || data.fields || data) as Record<
        string,
        any
      >;

      if (intakePickerTarget === 'store') {
        setStoreFormData((prev) =>
          mergeStoreIntakePrefill(
            prev as any,
            incoming as any,
            intakePickerOverwrite,
          ),
        );
        setSelectedStoreIntakeId(Number(intakePickerSelectedId));
        setObSuccess('Store fields populated from onboarding intake.');
      } else if (intakePickerTarget === 'owner_user') {
        const contact = incoming.name
          ? splitName(String(incoming.name))
          : {
              first_name: incoming.first_name || '',
              last_name: incoming.last_name || '',
            };
        const ownerPrefill = {
          ...incoming,
          first_name: incoming.first_name || contact.first_name,
          last_name: incoming.last_name || contact.last_name,
          email: incoming.email || incoming.primary_contact_email || '',
          role: incoming.role || 'Owner',
        };
        setObUserForm((prev) =>
          mergePrefill(prev as any, ownerPrefill as any, intakePickerOverwrite),
        );
        setUserFormData((prev) =>
          mergePrefill(
            prev as any,
            {
              ...ownerPrefill,
              store_id: incoming.store_id ?? prev.store_id,
            } as any,
            intakePickerOverwrite,
          ),
        );
        setObSuccess('Owner user fields populated from onboarding intake.');
        setUserSuccess('User fields populated from onboarding intake.');
      }

      setIntakePickerOpen(false);
    } catch {
      setIntakePickerError('Failed to load onboarding intake prefill.');
    } finally {
      setIntakePickerLoading(false);
    }
  }, [
    intakePickerOverwrite,
    intakePickerSelectedId,
    intakePickerTarget,
    setUserFormData,
  ]);

  const renderStoreBooleanSections = (
    formData: Partial<Record<StoreBooleanField, unknown>>,
    onChange: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>,
    sectionExtras?: Partial<Record<string, React.ReactNode>>,
  ) => (
    <div className="space-y-5">
      {STORE_BOOLEAN_SECTIONS.map((section) => (
        <section
          key={section.title}
          className="rounded-2xl border border-gray-200 bg-white/70 p-5 shadow-sm dark:border-gray-700 dark:bg-white/[0.03] md:p-6"
        >
          <h4 className="text-base font-semibold tracking-tight text-gray-900 dark:text-white">
            {section.title}
          </h4>
          <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {section.options.map(({ field, label }) => (
              <label
                key={field}
                className="flex items-start gap-3 rounded-lg py-1 text-sm"
              >
                <input
                  type="checkbox"
                  name={field}
                  checked={Boolean(formData[field])}
                  onChange={onChange}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="leading-5 text-gray-800 dark:text-gray-100">
                  {label}
                </span>
              </label>
            ))}
          </div>
          {section.inputs && section.inputs.length > 0 && (
            <div className="mt-5 grid grid-cols-1 gap-4 border-t border-gray-200 pt-5 dark:border-gray-700 md:grid-cols-2">
              {section.inputs.map(
                ({
                  field,
                  label,
                  type = 'text',
                  placeholder,
                  min,
                  step,
                  options,
                }) => {
                  if (type === 'select') {
                    return (
                      <div key={field}>
                        <Label>{label}</Label>
                        <select
                          name={field}
                          value={String(formData[field] ?? '')}
                          onChange={(event) => onChange(event)}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        >
                          {(options || []).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }
                  return (
                    <div key={field}>
                      <Label>{label}</Label>
                      <Input
                        type={type}
                        name={field}
                        value={String(formData[field] ?? '')}
                        onChange={onChange}
                        placeholder={placeholder}
                        min={min}
                        step={step}
                      />
                    </div>
                  );
                },
              )}
            </div>
          )}
          {sectionExtras?.[section.title]}
          {section.title === 'Store & POS Options' && (
            <HumanTransferScheduleFields
              embedded
              value={formData as Partial<HumanTransferFormFields>}
              onChange={(patch) => {
                Object.entries(patch).forEach(([name, value]) => {
                  onChange({
                    target: {
                      name,
                      value,
                      type: typeof value === 'boolean' ? 'checkbox' : 'text',
                      checked: Boolean(value),
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                });
              }}
            />
          )}
        </section>
      ))}
    </div>
  );

  return (
    <>
      <PageMeta
        title="AskArthur | Platform Admin"
        description="Platform Admin - Manage Stores and Users"
      />
      <div className="platform-admin-page space-y-5 md:space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="app-page-title">Platform Admin</h1>
          </div>
          <span className="app-kicker">Admin tools</span>
        </div>

        <PlatformAdminNav
          activeTab={activeTab}
          onTab={setActiveTab}
          onboardingSubtab={onboardingBillingSubtab}
          onOnboardingSubtab={setOnboardingBillingSubtab}
          plansSubtab={plansSubTab}
          onPlansSubtab={setPlansSubTab}
          twilioSubtab={twilioConfigSubtab}
          onTwilioSubtab={setActiveTwilioConfigSubtab}
          integrationSubtab={integrationSubtab}
          onIntegrationSubtab={setIntegrationSubtab}
          posSubtab={posSubtab}
          onPosSubtab={setPosSubtab}
          notificationsSubtab={notificationsSubtab}
          onNotificationsSubtab={setNotificationsSubtab}
        />

        {/* Stores Tab */}
        {activeTab === 'store' && (
          <StoresTab
            stores={stores}
            loading={loadingStores}
            page={storesPage}
            totalPages={storesTotalPages}
            totalCount={storesTotalCount}
            pageSize={PAGE_SIZE}
            onPage={setStoresPage}
            search={storesSearch}
            filtering={debouncedStoresSearch.length > 0}
            onSearchChange={(event) => {
              setStoresSearch(event.target.value);
              setStoresPage(1);
            }}
            sort={storesSort}
            onSort={(key) => {
              setStoresSort((current) => toggleSortState(current, key));
              setStoresPage(1);
            }}
            onCreate={handleStartOnboarding}
            onEditStore={(storeId) => {
              setEditStoreId(storeId);
              loadEditStoreDetails(storeId);
              setIsEditStoreModalOpen(true);
            }}
          />
        )}

        {/* Store Groups Tab */}
        {activeTab === 'storeGroups' && (
          <StoreGroupsTab
            storeGroups={storeGroups}
            loading={loadingStoreGroups}
            onChanged={fetchStoreGroups}
          />
        )}

        {/* Users Tab */}
        {activeTab === 'user' && (
          <UsersTab
            storeUsers={storeUsers}
            loadingStoreUsers={loadingStoreUsers}
            storeGroupUsers={storeGroupUsers}
            loadingStoreGroupUsers={loadingStoreGroupUsers}
            stores={stores}
            storeOptions={storeOptionsNameOnly}
            loadingStores={loadingStores}
            storeUserOptions={storeUserOptions}
            storeGroupOptions={storeGroupOptions}
            loadingStoreGroups={loadingStoreGroups}
            page={usersPage}
            totalPages={usersTotalPages}
            totalCount={usersTotalCount}
            pageSize={PAGE_SIZE}
            onPageChange={setUsersPage}
            onStoreUsersChanged={() => fetchStoreUsers(usersPage)}
            onStoreGroupUsersChanged={fetchStoreGroupUsers}
            search={usersSearch}
            filtering={debouncedUsersSearch.length > 0}
            onSearchChange={(event) => {
              setUsersSearch(event.target.value);
              setUsersPage(1);
            }}
            sort={usersSort}
            onSort={(key) => {
              if (
                key === 'id' ||
                key === 'name' ||
                key === 'email' ||
                key === 'role' ||
                key === 'store_name'
              ) {
                setUsersSort((current) => toggleSortState(current, key));
                setUsersPage(1);
              }
            }}
            userForm={userFormData}
            setUserForm={setUserFormData}
            userSuccess={userSuccess}
            setUserSuccess={setUserSuccess}
            onPopulateFromIntake={() => openIntakePicker('owner_user')}
            intakePicker={{
              open: intakePickerOpen && intakePickerInUsersTab,
              intakes: intakePickerOptions,
              selectedId: intakePickerSelectedId,
              onSelect: setIntakePickerSelectedId,
              overwrite: intakePickerOverwrite,
              onOverwriteChange: setIntakePickerOverwrite,
              loading: intakePickerLoading,
              error: intakePickerError,
              onApply: handleApplyIntakePrefill,
              onClose: () => setIntakePickerOpen(false),
            }}
            editUser={{
              open: isEditUserModalOpen,
              setOpen: setIsEditUserModalOpen,
              setId: setEditUserId,
              load: loadEditUserDetails,
              form: editUserFormData,
              error: editUserError,
              success: editUserSuccess,
              setError: setEditUserError,
              setSuccess: setEditUserSuccess,
              onChange: handleEditUserChange,
              onSubmit: handleEditUserSubmit,
            }}
          />
        )}

        {/* Agents Tab */}
        {activeTab === 'assistant' && (
          <AgentsTab
            assistants={assistants}
            loading={loadingAssistants}
            page={assistantsPage}
            totalPages={assistantsTotalPages}
            totalCount={assistantsTotalCount}
            pageSize={PAGE_SIZE}
            onPageChange={setAssistantsPage}
            onChanged={() => fetchAssistants(assistantsPage)}
            storeOptions={storeOptions}
            loadingStores={loadingStores}
            retellAgentOptions={retellAgentOptions}
            loadingRetellAgents={loadingRetellAgents}
            phoneNumberOptions={approvedUnassignedPhoneNumberOptions}
            loadingPhoneNumbers={loadingApprovedUnassignedPhoneNumbers}
            onOpenCreate={() => {
              fetchRetellAgents();
              fetchApprovedUnassignedPhoneNumbers();
            }}
            manualAgentOption={MANUAL_ASSISTANT_ID_OPTION}
            retellLlmModels={RETELL_LLM_MODELS}
            search={assistantsSearch}
            filtering={debouncedAssistantsSearch.length > 0}
            onSearchChange={(event) => {
              setAssistantsSearch(event.target.value);
              setAssistantsPage(1);
            }}
            sort={assistantsSort}
            onSort={(key) => {
              setAssistantsSort((current) => toggleSortState(current, key));
              setAssistantsPage(1);
            }}
          />
        )}

        {/* Admin Orders Tab */}
        {activeTab === 'adminOrders' && <AdminOrdersTab {...adminOrders} />}

        {activeTab === 'adminCalls' && <AdminCallsTab {...adminCalls} />}

        {/* Admin Reservations Tab */}
        {activeTab === 'adminReservations' && (
          <AdminReservationsTab list={adminReservationsList} />
        )}

        {activeTab === 'insights' && <InsightsChatTab />}

        {/* Refund Review Tab */}
        {activeTab === 'refundReview' && <RefundReviewTab />}

        {/* Integrations Tab */}
        {activeTab === 'integrations' && (
          <IntegrationsTab storeOptions={storeOptions} />
        )}

        {/* Knowledge Bases Tab */}
        {activeTab === 'knowledgeBases' && (
          <KnowledgeBasesTab storeOptions={storeOptions} />
        )}

        {activeTab === 'faqSupport' && <FaqSupportTab />}

        {activeTab === 'vipContacts' && (
          <VipContactsTab
            storeOptions={storeOptions}
            storeGroupOptions={storeGroupOptions}
          />
        )}

        {activeTab === 'demoLines' && <DemoLinesTab />}

        {/* POS Config Tab */}
        {activeTab === 'posConfig' && (
          <PosConfigTab storeOptions={storeOptions} activeSubtab={posSubtab} />
        )}

        {/* Twilio Config Tab */}
        {activeTab === 'tsv' && (
          <div className="space-y-6">
            {twilioConfigSubtab === 'tollFree' && (
              <TwilioTfvVerificationTab storeOptions={storeOptions} />
            )}

            {twilioConfigSubtab === 'phoneNumbers' && (
              <TwilioPhoneNumberPurchaseTab />
            )}

            {false && twilioConfigSubtab === 'tollFree' && (
              <>
                <ComponentCard
                  title="Configure Twilio Elastic SIP and SMS Webhook"
                  className="overflow-hidden"
                >
                  <form
                    onSubmit={handleTwilioConfigSubmit}
                    className="space-y-6"
                  >
                    <div>
                      <Label>
                        Phone SID <span className="text-error-500">*</span>
                      </Label>
                      <Input
                        type="text"
                        name="phone_sid"
                        value={twilioConfigFormData.phone_sid}
                        onChange={handleTwilioConfigChange}
                        required
                        placeholder="e.g., PNxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Enter the Twilio Phone Number SID to configure Elastic
                        SIP and SMS webhook
                      </p>
                    </div>

                    {twilioConfigError && (
                      <div className="p-4 text-sm text-error-500 bg-error-50 dark:bg-error-900/20 rounded-lg border border-error-200 dark:border-error-800">
                        {twilioConfigError}
                      </div>
                    )}

                    {twilioConfigSuccess && (
                      <div className="p-4 text-sm text-success-500 bg-success-50 dark:bg-success-900/20 rounded-lg border border-success-200 dark:border-success-800">
                        {twilioConfigSuccess}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submittingTwilioConfig}
                      className={`inline-flex items-center justify-center gap-2 rounded-lg transition px-5 py-3.5 text-sm bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600 w-full md:w-auto ${
                        submittingTwilioConfig
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                    >
                      {submittingTwilioConfig
                        ? 'Configuring...'
                        : 'Configure Webhook'}
                    </button>
                  </form>
                </ComponentCard>

                <ComponentCard
                  title="Submit Twilio Toll-Free Verification"
                  className="overflow-hidden"
                >
                  <form onSubmit={handleTsvSubmit} className="space-y-6">
                    <div>
                      <Label>
                        Phone SID <span className="text-error-500">*</span>
                      </Label>
                      <Input
                        type="text"
                        name="phone_sid"
                        value={tsvFormData.phone_sid}
                        onChange={handleTsvChange}
                        required
                        placeholder="e.g., PNxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Enter the Twilio Phone Number SID to submit for
                        toll-free verification
                      </p>
                    </div>

                    {tsvError && (
                      <div className="p-4 text-sm text-error-500 bg-error-50 dark:bg-error-900/20 rounded-lg border border-error-200 dark:border-error-800">
                        {tsvError}
                      </div>
                    )}

                    {tsvSuccess && (
                      <div className="p-4 text-sm text-success-500 bg-success-50 dark:bg-success-900/20 rounded-lg border border-success-200 dark:border-success-800">
                        {tsvSuccess}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submittingTsv}
                      className={`inline-flex items-center justify-center gap-2 rounded-lg transition px-5 py-3.5 text-sm bg-brand-500 text-white shadow-theme-xs hover:bg-brand-600 w-full md:w-auto ${
                        submittingTsv ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {submittingTsv ? 'Submitting...' : 'Submit TSV'}
                    </button>
                  </form>

                  {refreshingTsvStatus && (
                    <div className="p-3 text-sm text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400 rounded-lg border border-blue-200 dark:border-blue-800 mb-4">
                      Refreshing Toll-Free statuses...
                    </div>
                  )}

                  {tsvRefreshError && (
                    <div className="p-4 text-sm text-error-500 bg-error-50 dark:bg-error-900/20 rounded-lg border border-error-200 dark:border-error-800 mb-4">
                      {tsvRefreshError}
                    </div>
                  )}

                  {loadingTsvRequests ? (
                    <div className="text-gray-900 dark:text-white p-4">
                      Loading...
                    </div>
                  ) : tsvRequests.length === 0 ? (
                    <div className="text-gray-500 dark:text-gray-400 p-4">
                      No verification requests found.
                    </div>
                  ) : (
                    <div>
                      <div className="mb-4 max-w-md px-4">
                        <Label htmlFor="tfv-search">Search</Label>
                        <Input
                          id="tfv-search"
                          type="search"
                          value={tsvSearch}
                          onChange={(event) => setTsvSearch(event.target.value)}
                          placeholder="Search business, phone SID, or status"
                        />
                      </div>
                      {visibleTsvRequests.length === 0 ? (
                        <div className="text-gray-500 dark:text-gray-400 p-4">
                          No verification requests match that search.
                        </div>
                      ) : (
                        <div className={adminTableShellClassName}>
                          <div className="max-w-full overflow-x-auto">
                            <div className="min-w-[1200px]">
                              <Table>
                                <TableHeader
                                  className={adminTableHeaderClassName}
                                >
                                  <TableRow>
                                    {(
                                      [
                                        ['business_name', 'Business Name'],
                                        [
                                          'tollfree_phone_number_sid',
                                          'Phone Number SID',
                                        ],
                                        ['status', 'Status'],
                                        [
                                          'business_website',
                                          'Business Website',
                                        ],
                                        [
                                          'notification_email',
                                          'Notification Email',
                                        ],
                                        ['date_created', 'Date Created'],
                                        ['message_volume', 'Message Volume'],
                                      ] as const
                                    ).map(([key, label]) => (
                                      <TableCell
                                        key={key}
                                        isHeader
                                        className="px-6 py-4 text-center text-gray-500 text-theme-xs dark:text-gray-400"
                                      >
                                        <button
                                          type="button"
                                          aria-label={`Sort by ${label}`}
                                          onClick={() =>
                                            setTsvSort((current) =>
                                              toggleSortState(current, key),
                                            )
                                          }
                                        >
                                          {label}
                                          {tsvSort.key === key
                                            ? tsvSort.direction === 'asc'
                                              ? ' ↑'
                                              : ' ↓'
                                            : ''}
                                        </button>
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHeader>
                                <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                                  {visibleTsvRequests.map((request, idx) => (
                                    <TableRow key={request.id || idx}>
                                      <TableCell className="text-center align-middle">
                                        <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                          {request.business_name || 'N/A'}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-center align-middle">
                                        <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                          {request.tollfree_phone_number_sid ||
                                            'N/A'}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-center align-middle">
                                        <span
                                          className={`inline-block px-2 py-1 text-xs font-medium rounded ${
                                            request.status === 'TWILIO_APPROVED'
                                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                              : request.status ===
                                                  'TWILIO_REJECTED'
                                                ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                                : request.status ===
                                                    'PENDING_REVIEW'
                                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                                          }`}
                                        >
                                          {request.status === 'TWILIO_APPROVED'
                                            ? 'Approved'
                                            : request.status ===
                                                'TWILIO_REJECTED'
                                              ? 'Rejected'
                                              : request.status ===
                                                  'PENDING_REVIEW'
                                                ? 'Pending Review'
                                                : request.status === 'DELETED'
                                                  ? 'Deleted'
                                                  : request.status || 'N/A'}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-center align-middle">
                                        {request.business_website ? (
                                          <a
                                            href={request.business_website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 underline hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                          >
                                            {request.business_website}
                                          </a>
                                        ) : (
                                          <span className="text-gray-500 dark:text-gray-400">
                                            N/A
                                          </span>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-center align-middle">
                                        <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                          {request.notification_email || 'N/A'}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-center align-middle">
                                        <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                          {request.date_created
                                            ? new Date(
                                                request.date_created,
                                              ).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                              })
                                            : 'N/A'}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-center align-middle">
                                        <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                          {request.message_volume || 'N/A'}
                                        </span>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </ComponentCard>
              </>
            )}

            {twilioConfigSubtab === 'secondaryProfiles' && (
              <TwilioSecondaryProfilesTab storeOptions={storeOptions} />
            )}
          </div>
        )}

        {/* Onboarding Intakes Tab */}
        {activeTab === 'onboardingBilling' &&
          onboardingBillingSubtab === 'intakes' && (
            <div className="space-y-6">
              <OnboardingIntakesTab
                storeOptions={storeOptions}
                onOpenLinkedStore={(storeId) => {
                  setEditStoreId(storeId);
                  setActiveTab('edit');
                  loadEditStoreDetails(storeId);
                }}
                onPopulateStoreCreate={(intake: OnboardingIntake) => {
                  handleStartOnboarding();
                  openIntakePicker('store', intake);
                }}
                onPopulateExistingStore={async (intakeId, storeId) => {
                  setEditStoreId(storeId);
                  setActiveTab('edit');
                  setEditStoreLoading(true);
                  setIsEditStoreModalOpen(true);

                  try {
                    const storeRes = await api.get(
                      apiEndpoints.getStoreById(storeId),
                    );
                    if (storeRes?.ok) {
                      const storeData = await storeRes.json();

                      const prefillRes = await api.get(
                        apiEndpoints.getOnboardingIntakePrefill(
                          intakeId,
                          'store',
                        ),
                      );
                      if (prefillRes?.ok) {
                        const prefillData = await prefillRes.json();
                        const incoming =
                          prefillData.payload ||
                          prefillData.fields ||
                          prefillData;

                        const merged = mergePrefill(storeData, incoming, true);
                        setEditStoreFormData(merged);
                      } else {
                        setEditStoreFormData(storeData);
                      }
                    }
                  } catch (err) {
                    console.error(err);
                  } finally {
                    setEditStoreLoading(false);
                  }
                }}
              />
            </div>
          )}

        {/* Onboarding Status Tab */}
        {activeTab === 'onboardingBilling' &&
          onboardingBillingSubtab === 'status' && (
            <div className="space-y-6">
              <OnboardingStatusTab />
            </div>
          )}

        {activeTab === 'notifications' &&
          notificationsSubtab === 'subscriptions' && (
            <NotificationManager mode="admin" stores={storeDropdownSource} />
          )}
        {activeTab === 'notifications' && notificationsSubtab === 'sent' && (
          <AdminSentNotificationsTab {...sentNotifications} />
        )}

        {/* Edit Store Info Tab */}
        {activeTab === 'edit' && (
          <div className="space-y-6">
            <ComponentCard title="Edit Store">
              <form onSubmit={handleOpenConfirmEdit} className="space-y-6">
                <div>
                  <Label>Select Store</Label>
                  <SearchableSelect
                    options={storeOptions}
                    value={editStoreId || ''}
                    onChange={(val) => {
                      const id = val ? parseInt(val, 10) : 0;
                      setEditStoreId(id || null);
                      if (id) {
                        const selected = stores.find((s) => s.id === id);
                        setEditStoreFormData({
                          name: selected?.name || '',
                          phonetic_name: '',
                          call_intro_sentence: '',
                          speach_filler_phrase: 'Sure, one moment please',
                          vip_intro_phrase:
                            'Hello {{vip_caller_name}}! Thanks for being a valued member at {{store_name}}.',
                          agent_style: '',
                          use_default_call_intro_sentence: true,
                          default_call_intro_sentence: '',
                          legal_name: '',
                          timezone: 'America/New_York',
                          address_line_1: '',
                          address_line_2: '',
                          city: '',
                          state: '',
                          postal_code: '',
                          country: 'US',
                          website: '',
                          menu_link: '',
                          knowledge_base_id: selected?.knowledge_base_id || '',
                          ordering_link: '',
                          stripe_account_id: '',
                          reservation_link: '',
                          reservation_email: '',
                          location_secondary_phone_number: '',
                          location_secondary_phone_number_ext: '',
                          default_delivery_fee_cents: 0,
                          pass_through_dispatch_quote: true,
                          send_delivery_cost_to_deliverect: true,
                          assess_tax_on_fees: false,
                          make_fees_tax_inclusive: false,
                          fallback_tax_percent: '',
                          application_fee_default_cents: 50,
                          delivery_fee_label: '',
                          service_fee_label: '',
                          default_delivery_mode: 'none',
                          takes_reservations: false,
                          takes_catering: false,
                          takes_events: false,
                          agent_handles_events: false,
                          agent_handles_catering: false,
                          outdoor_seating: false,
                          collect_payment_online: true,
                          accept_pay_on_site: false,
                          monthly_minutes_limit: 500,
                          ot_max_party_size: '',
                          average_reservation_cover: '',
                          google_price_level: '',
                          explicit_only_table_types: [],
                          annotate_opentable_reservation_notes: false,
                          opentable_reservation_note_annotation_position:
                            'prepend',
                          opentable_reservation_note_annotation: '',
                          livemode: false,
                          store_group: null,
                          store_group_name: '',
                          accepts_deliveries: false,
                          how_to_order_delivery_message: '',
                          accepts_phone_orders: true,
                          collect_email: false,
                          posless_integration: false,
                          ...humanTransferFormFields(),
                          current_pos_system: 'UNKNOWN',
                          poshub_toast_group_id: '',
                          forward_order_requests_to_store: false,
                          forward_order_requests_to_text_link: false,
                          forward_order_requests_to_phone: false,
                          order_requests_phone_number: '',
                          order_requests_phone_number_ext: '',
                          forward_catering_requests_to_store: false,
                          forward_catering_requests_to_text_link: false,
                          forward_event_requests_to_store: false,
                          forward_event_requests_to_text_link: false,
                          catering_outreach_contact: '',
                          catering_forwarding_number: '',
                          catering_forwarding_number_ext: '',
                          catering_link: '',
                          event_forwarding_number: '',
                          event_forwarding_number_ext: '',
                          event_inquiry_questions: [],
                          catering_inquiry_questions: [],
                          parking_info: '',
                          business_number: '',
                          show_sms_on_dashboard: false,
                          manual_menu_snoozing_enabled: false,
                          cross_sell_enabled: true,
                          forward_late_reservations_to_text: false,
                          forward_reservation_cancelation_failures_to_text: false,
                        });
                        setEditStoreLogo(null);
                        if (editStoreLogoPreviewUrl) {
                          URL.revokeObjectURL(editStoreLogoPreviewUrl);
                          setEditStoreLogoPreviewUrl('');
                        }
                        setEditStoreCurrentLogoUrl('');
                        loadEditStoreDetails(id);
                      } else {
                        setEditStoreFormData(null);
                        setEditStoreLogo(null);
                        if (editStoreLogoPreviewUrl) {
                          URL.revokeObjectURL(editStoreLogoPreviewUrl);
                          setEditStoreLogoPreviewUrl('');
                        }
                        setEditStoreCurrentLogoUrl('');
                      }
                    }}
                    placeholder="Select a store..."
                  />
                </div>

                {editStoreLoading && (
                  <p className="text-sm text-gray-500">
                    Loading store details...
                  </p>
                )}
                {editStoreFormData && (
                  <>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Store Name</Label>
                        <Input
                          type="text"
                          name="name"
                          value={editStoreFormData.name}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                      <div>
                        <Label>Phonetic Name</Label>
                        <Input
                          type="text"
                          name="phonetic_name"
                          value={editStoreFormData.phonetic_name || ''}
                          onChange={handleEditStoreChange}
                          placeholder="in case AI agent pronounces the name differently"
                        />
                      </div>
                      <div>
                        <Label>Timezone</Label>
                        <select
                          name="timezone"
                          value={editStoreFormData.timezone}
                          onChange={handleEditStoreChange}
                          className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                        >
                          {STORE_TIMEZONE_OPTIONS.map((timezone) => (
                            <option key={timezone} value={timezone}>
                              {timezone}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Store Group</Label>
                        <SearchableSelect
                          options={storeGroupOptions}
                          value={editStoreFormData.store_group || ''}
                          onChange={(value) =>
                            setEditStoreFormData((prev: any) => ({
                              ...prev,
                              store_group: value ? parseInt(value, 10) : null,
                            }))
                          }
                          placeholder={
                            loadingStoreGroups
                              ? 'Loading store groups...'
                              : 'Select a store group'
                          }
                          disabled={loadingStoreGroups}
                        />
                      </div>
                    </div>
                    <CallIntroMessageField
                      customName="call_intro_sentence"
                      customValue={editStoreFormData.call_intro_sentence || ''}
                      useDefault={
                        editStoreFormData.use_default_call_intro_sentence ??
                        true
                      }
                      onCustomChange={handleEditStoreChange}
                      onUseDefaultChange={(useDefault) =>
                        setEditStoreFormData((prev: any) => ({
                          ...prev,
                          use_default_call_intro_sentence: useDefault,
                        }))
                      }
                      defaultIntro={
                        editStoreFormData.default_call_intro_sentence ||
                        getDefaultCallIntroSentence(
                          editStoreFormData.phonetic_name ||
                            editStoreFormData.name,
                        )
                      }
                    />
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                      <div>
                        <Label>Speech Filler Phrase</Label>
                        <Input
                          type="text"
                          name="speach_filler_phrase"
                          value={editStoreFormData.speach_filler_phrase || ''}
                          onChange={handleEditStoreChange}
                          placeholder="Sure, one moment please"
                        />
                      </div>
                      <div>
                        <Label>VIP Intro Phrase</Label>
                        <Input
                          type="text"
                          name="vip_intro_phrase"
                          value={editStoreFormData.vip_intro_phrase || ''}
                          onChange={handleEditStoreChange}
                          placeholder="Hello {{vip_caller_name}}! Thanks for being a valued member at {{store_name}}."
                        />
                      </div>
                      <div>
                        <Label>Agent Style</Label>
                        <textarea
                          name="agent_style"
                          value={editStoreFormData.agent_style || ''}
                          onChange={handleEditStoreChange}
                          rows={3}
                          className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                          placeholder="Describe how the voice agent should communicate"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Legal Name</Label>
                      <Input
                        type="text"
                        name="legal_name"
                        value={editStoreFormData.legal_name || ''}
                        onChange={handleEditStoreChange}
                      />
                    </div>
                    <div>
                      <Label>Business Number</Label>
                      <Input
                        type="text"
                        name="business_number"
                        value={editStoreFormData.business_number || ''}
                        onChange={handleEditStoreChange}
                      />
                    </div>
                    <div>
                      <Label>Store Logo (optional)</Label>
                      <div className="flex items-center gap-4">
                        {(editStoreLogoPreviewUrl ||
                          editStoreCurrentLogoUrl) && (
                          <img
                            src={
                              editStoreLogoPreviewUrl || editStoreCurrentLogoUrl
                            }
                            alt="Current store logo"
                            className="h-12 w-12 rounded object-contain border border-gray-200"
                          />
                        )}
                        <input
                          key={editStoreId || 'logo-input'}
                          type="file"
                          accept="image/*"
                          onChange={handleEditStoreLogoChange}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (editStoreLogoPreviewUrl) {
                              // Just clear local selection
                              clearLocalLogoSelection();
                            } else if (editStoreCurrentLogoUrl) {
                              // Remove saved logo on server
                              handleRemoveStoredLogo();
                            }
                          }}
                          disabled={
                            !editStoreLogoPreviewUrl && !editStoreCurrentLogoUrl
                          }
                          className="inline-flex items-center justify-center gap-2 rounded-lg transition px-3 py-2 text-xs bg-error-50 text-error-600 border border-error-200 hover:bg-error-100 disabled:opacity-50"
                          title={
                            editStoreLogoPreviewUrl
                              ? 'Clear selected file'
                              : 'Remove stored logo'
                          }
                        >
                          {editStoreLogoPreviewUrl
                            ? 'Clear Selection'
                            : 'Remove Logo'}
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Address Line 1</Label>
                        <Input
                          type="text"
                          name="address_line_1"
                          value={editStoreFormData.address_line_1}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                      <div>
                        <Label>Address Line 2</Label>
                        <Input
                          type="text"
                          name="address_line_2"
                          value={editStoreFormData.address_line_2}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                      <div>
                        <Label>City</Label>
                        <Input
                          type="text"
                          name="city"
                          value={editStoreFormData.city}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                      <div>
                        <Label>State</Label>
                        <Input
                          type="text"
                          name="state"
                          value={editStoreFormData.state}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                      <div>
                        <Label>Postal Code</Label>
                        <Input
                          type="text"
                          name="postal_code"
                          value={editStoreFormData.postal_code}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Country</Label>
                        <select
                          name="country"
                          value={editStoreFormData.country}
                          onChange={handleEditStoreChange}
                          className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                        >
                          {STORE_COUNTRY_OPTIONS.map((country) => (
                            <option key={country.value} value={country.value}>
                              {country.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Website</Label>
                        <Input
                          type="url"
                          name="website"
                          value={editStoreFormData.website}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Menu Link</Label>
                        <Input
                          type="text"
                          name="menu_link"
                          value={editStoreFormData.menu_link}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                      <div>
                        <Label>Ordering Link</Label>
                        <Input
                          type="url"
                          name="ordering_link"
                          value={editStoreFormData.ordering_link || ''}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                      <KnowledgeBaseSelect
                        value={editStoreFormData.knowledge_base_id || ''}
                        onChange={(value) =>
                          setEditStoreFormData((prev: any) => ({
                            ...prev,
                            knowledge_base_id: value,
                          }))
                        }
                        options={retellKnowledgeBaseOptions}
                        loading={loadingRetellKnowledgeBases}
                        error={retellKnowledgeBaseError}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Stripe Account ID</Label>
                        <Input
                          type="text"
                          name="stripe_account_id"
                          value={editStoreFormData.stripe_account_id}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Reservation Link</Label>
                        <Input
                          type="url"
                          name="reservation_link"
                          value={editStoreFormData.reservation_link || ''}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                      <div>
                        <Label>Gift Card Link</Label>
                        <Input
                          type="url"
                          name="gift_card_link"
                          value={editStoreFormData.gift_card_link || ''}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                      <div>
                        <Label>Reservation Email</Label>
                        <Input
                          type="email"
                          name="reservation_email"
                          value={editStoreFormData.reservation_email}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Secondary Phone Number</Label>
                        <Input
                          type="tel"
                          name="location_secondary_phone_number"
                          value={
                            editStoreFormData.location_secondary_phone_number ||
                            ''
                          }
                          onChange={handleEditStoreChange}
                          placeholder="+1 555-5555"
                        />
                      </div>
                      <div>
                        <Label>Secondary Phone Extension</Label>
                        <Input
                          type="text"
                          name="location_secondary_phone_number_ext"
                          value={
                            editStoreFormData.location_secondary_phone_number_ext ||
                            ''
                          }
                          onChange={handleEditStoreChange}
                          placeholder="123#"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>How To Order Delivery Message</Label>
                      <Input
                        type="text"
                        name="how_to_order_delivery_message"
                        value={
                          editStoreFormData.how_to_order_delivery_message || ''
                        }
                        onChange={handleEditStoreChange}
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                      <div>
                        <Label>Default Delivery Fee (cents)</Label>
                        <Input
                          type="number"
                          name="default_delivery_fee_cents"
                          value={editStoreFormData.default_delivery_fee_cents}
                          onChange={handleEditStoreChange}
                        />
                      </div>
                      <div>
                        <Label>Application Fee Default (cents)</Label>
                        <Input
                          type="number"
                          name="application_fee_default_cents"
                          value={
                            editStoreFormData.application_fee_default_cents
                          }
                          onChange={handleEditStoreChange}
                        />
                      </div>
                      <div>
                        <Label>Default Delivery Mode</Label>
                        <select
                          name="default_delivery_mode"
                          value={editStoreFormData.default_delivery_mode}
                          onChange={handleEditStoreChange}
                          className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                        >
                          <option value="none">No Deliveries</option>
                          <option value="dispatch">Third-Party Dispatch</option>
                          <option value="self">Restaurant Self-Delivery</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label>Monthly Minutes Limit</Label>
                      <Input
                        type="number"
                        name="monthly_minutes_limit"
                        value={editStoreFormData.monthly_minutes_limit}
                        onChange={handleEditStoreChange}
                      />
                    </div>
                    <div>
                      <Label>Parking Info</Label>
                      <textarea
                        name="parking_info"
                        value={editStoreFormData.parking_info || ''}
                        onChange={handleEditStoreChange}
                        rows={3}
                        className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                        placeholder="Parking details, lot location, valet info, etc."
                      />
                    </div>
                    <div>
                      <Label>POS System</Label>
                      <select
                        name="current_pos_system"
                        value={
                          editStoreFormData.current_pos_system || 'UNKNOWN'
                        }
                        onChange={handleEditStoreChange}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                      >
                        <option value="UNKNOWN">None / Unknown</option>
                        <option value="TOAST">Toast</option>
                        <option value="FOODHUB">FoodHub</option>
                        <option value="CLOVER">Clover</option>
                        <option value="SQUARE">Square</option>
                        <option value="SHOPIFY">Shopify</option>
                        <option value="LIGHTSPEED">Lightspeed</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    {renderStoreBooleanSections(
                      editStoreFormData,
                      handleEditStoreChange,
                      {
                        'Reservation & Seating Options': (
                          <>
                            <ExplicitOnlyTableTypesField
                              value={
                                editStoreFormData.explicit_only_table_types
                              }
                              onChange={(values) =>
                                setEditStoreFormData((prev: any) =>
                                  prev
                                    ? {
                                        ...prev,
                                        explicit_only_table_types: values,
                                      }
                                    : prev,
                                )
                              }
                            />
                            {editStoreId ? (
                              <ReservationLocationScopeSection
                                storeId={editStoreId}
                                storeName={
                                  editStoreFormData.name || 'This store'
                                }
                                storeOptions={storeOptions}
                                embedded
                              />
                            ) : null}
                          </>
                        ),
                      },
                    )}
                    {renderEventInquiryQuestionsEditor()}
                    {renderCateringInquiryQuestionsEditor()}
                    {renderBoostedKeywordsEditor()}

                    {editStoreError && (
                      <div className="p-3 text-sm text-error-600 bg-error-50 border border-error-200 rounded-lg">
                        {editStoreError}
                      </div>
                    )}
                    {editStoreSuccess && (
                      <div className="p-3 text-sm text-success-600 bg-success-50 border border-success-200 rounded-lg">
                        {editStoreSuccess}
                      </div>
                    )}
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-2 rounded-lg transition px-5 py-3.5 text-sm bg-brand-500 text-white hover:bg-brand-600"
                    >
                      Save Store Changes
                    </button>
                  </>
                )}
              </form>
              <Modal
                isOpen={isEditConfirmOpen}
                onClose={() => setIsEditConfirmOpen(false)}
                className="max-w-[700px] p-6 lg:p-8"
              >
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
                    Confirm Store Changes
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    You are about to apply the following changes to this store:
                  </p>
                  <div className="max-h-64 overflow-auto border rounded-lg p-3 bg-gray-50 dark:bg-gray-800/40">
                    {editStorePendingChanges.map((c, idx) => (
                      <div
                        key={idx}
                        className="text-sm py-1 border-b last:border-b-0 border-gray-200 dark:border-gray-700"
                      >
                        <span className="font-medium">{c.label}:</span>{' '}
                        <span className="text-gray-600 dark:text-gray-400">
                          {String(c.from ?? '')}
                        </span>{' '}
                        →{' '}
                        <span className="text-gray-900 dark:text-gray-200">
                          {String(c.to ?? '')}
                        </span>
                      </div>
                    ))}
                  </div>
                  {secondaryFollowers.length > 0 && (
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-500/30 dark:bg-amber-500/10">
                      <p className="font-medium text-gray-800 dark:text-white">
                        These numbers match the old secondary phone (
                        {editStoreBaseline?.location_secondary_phone_number}).
                        Change them to{' '}
                        {editStoreFormData?.location_secondary_phone_number}
                        {editStoreFormData?.location_secondary_phone_number_ext
                          ? `, extension ${editStoreFormData.location_secondary_phone_number_ext}`
                          : ', with no extension'}{' '}
                        too?
                      </p>
                      <p className="mt-1 text-gray-600 dark:text-gray-300">
                        Leave a box unticked to keep that number as it is.
                      </p>
                      <div className="mt-2 space-y-1.5">
                        {secondaryFollowers.map((key) => (
                          <label
                            key={key}
                            className="flex items-center gap-2 text-gray-700 dark:text-gray-300"
                          >
                            <input
                              type="checkbox"
                              checked={secondaryFollowersToUpdate.includes(key)}
                              onChange={(e) =>
                                setSecondaryFollowersToUpdate((prev) =>
                                  e.target.checked
                                    ? [...prev, key]
                                    : prev.filter((k) => k !== key),
                                )
                              }
                              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                            />
                            {secondaryFollowerLabel(key)}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-6 flex gap-3">
                    <button
                      type="button"
                      onClick={() => setIsEditConfirmOpen(false)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg transition px-5 py-3.5 text-sm bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmEditSubmit}
                      className="inline-flex items-center justify-center gap-2 rounded-lg transition px-5 py-3.5 text-sm bg-brand-500 text-white hover:bg-brand-600"
                    >
                      Confirm & Save
                    </button>
                  </div>
                </div>
              </Modal>
            </ComponentCard>

            <ComponentCard title="Edit Store Manager/Owner">
              <form onSubmit={handleEditUserSubmit} className="space-y-6">
                <div>
                  <Label>Select User</Label>
                  <SearchableSelect
                    options={storeUserOptions}
                    value={editUserId || ''}
                    onChange={(val) => {
                      const id = val ? parseInt(val, 10) : 0;
                      setEditUserId(id || null);
                      if (id) loadEditUserDetails(id);
                      else setEditUserFormData(null);
                    }}
                    placeholder="Select a user..."
                    disabled={loadingStoreUsers}
                  />
                </div>

                {editUserFormData && (
                  <>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>First Name</Label>
                        <Input
                          type="text"
                          name="first_name"
                          value={editUserFormData.first_name}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div>
                        <Label>Last Name</Label>
                        <Input
                          type="text"
                          name="last_name"
                          value={editUserFormData.last_name}
                          onChange={handleEditUserChange}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          name="email"
                          value={editUserFormData.email}
                          onChange={handleEditUserChange}
                        />
                      </div>
                      <div>
                        <Label>Password (optional)</Label>
                        <Input
                          type="password"
                          name="password"
                          value={editUserFormData.password}
                          onChange={handleEditUserChange}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Store</Label>
                        <SearchableSelect
                          options={storeOptionsNameOnly}
                          value={editUserFormData.store_id || ''}
                          onChange={(val) =>
                            handleEditUserChange({
                              target: {
                                name: 'store_id',
                                value: val,
                                type: 'text',
                              },
                            } as any)
                          }
                          placeholder="Select a store..."
                        />
                      </div>
                      <div>
                        <Label>Role</Label>
                        <select
                          name="role"
                          value={editUserFormData.role}
                          onChange={handleEditUserChange}
                          className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                        >
                          <option value="Manager">Manager</option>
                          <option value="Owner">Owner</option>
                        </select>
                      </div>
                    </div>

                    {editUserError && (
                      <div className="p-3 text-sm text-error-600 bg-error-50 border border-error-200 rounded-lg">
                        {editUserError}
                      </div>
                    )}
                    {editUserSuccess && (
                      <div className="p-3 text-sm text-success-600 bg-success-50 border border-success-200 rounded-lg">
                        {editUserSuccess}
                      </div>
                    )}
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-2 rounded-lg transition px-5 py-3.5 text-sm bg-brand-500 text-white hover:bg-brand-600"
                    >
                      Save User Changes
                    </button>
                  </>
                )}
              </form>
            </ComponentCard>

            <ComponentCard title="Edit Agent">
              <form
                onSubmit={handleEditAssistantSubtabSubmit}
                className="space-y-6"
              >
                <div>
                  <Label>Select Agent</Label>
                  <select
                    value={editAssistantId || ''}
                    onChange={(e) => {
                      const id = e.target.value
                        ? parseInt(e.target.value, 10)
                        : 0;
                      setEditAssistantId(id || null);
                      if (id) loadEditAssistantDetails(id);
                      else setEditAssistantSubtabData(null);
                    }}
                    className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                  >
                    <option value="">Select an agent...</option>
                    {assistants.map((a: any) => (
                      <option key={a.id} value={a.id}>
                        {a.assistant_id || a.id}
                      </option>
                    ))}
                  </select>
                </div>

                {editAssistantSubtabData && (
                  <>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Store</Label>
                        <SearchableSelect
                          options={storeOptionsNameOnly}
                          value={editAssistantSubtabData.store || ''}
                          onChange={(val) =>
                            handleEditAssistantSubtabChange({
                              target: {
                                name: 'store',
                                value: val,
                                type: 'text',
                              },
                            } as any)
                          }
                          placeholder="Select a store..."
                        />
                      </div>
                      <div>
                        <Label>Agent ID</Label>
                        <Input
                          type="text"
                          name="assistant_id"
                          value={editAssistantSubtabData.assistant_id}
                          onChange={handleEditAssistantSubtabChange}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Agent Provider</Label>
                        <Input
                          type="text"
                          name="assistant_provider"
                          value={editAssistantSubtabData.assistant_provider}
                          onChange={handleEditAssistantSubtabChange}
                        />
                      </div>
                      <div>
                        <Label>Phone Number</Label>
                        <Input
                          type="text"
                          name="phone_number"
                          value={editAssistantSubtabData.phone_number}
                          onChange={handleEditAssistantSubtabChange}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Retell Voice ID</Label>
                        <Input
                          type="text"
                          name="retell_voice_id"
                          value={editAssistantSubtabData.retell_voice_id || ''}
                          onChange={handleEditAssistantSubtabChange}
                          placeholder="e.g., retell-Rita"
                        />
                      </div>
                      <RetellLlmModelField
                        id="edit-agent-subtab-retell-llm-model"
                        value={editAssistantSubtabData.retell_llm_model || ''}
                        onChange={handleEditAssistantSubtabChange}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div>
                        <Label>Language</Label>
                        <Input
                          type="text"
                          name="language"
                          value={editAssistantSubtabData.language}
                          onChange={handleEditAssistantSubtabChange}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={editAssistantSubtabData.is_active}
                          onChange={handleEditAssistantSubtabChange}
                          className="h-4 w-4"
                        />
                        <span className="text-sm">Is Active</span>
                      </label>
                    </div>

                    {editAssistantSubtabError && (
                      <div className="p-3 text-sm text-error-600 bg-error-50 border border-error-200 rounded-lg">
                        {editAssistantSubtabError}
                      </div>
                    )}
                    {editAssistantSubtabSuccess && (
                      <div className="p-3 text-sm text-success-600 bg-success-50 border border-success-200 rounded-lg">
                        {editAssistantSubtabSuccess}
                      </div>
                    )}
                    <button
                      type="submit"
                      className="inline-flex items-center justify-center gap-2 rounded-lg transition px-5 py-3.5 text-sm bg-brand-500 text-white hover:bg-brand-600"
                    >
                      Save Agent Changes
                    </button>
                  </>
                )}
              </form>
            </ComponentCard>
          </div>
        )}

        {/* Activation Offers Tab */}
        {activeTab === 'onboardingBilling' &&
          onboardingBillingSubtab === 'offers' && (
            <div className="space-y-6">
              <ActivationOffersTab storeOptions={storeOptions} />
            </div>
          )}

        {/* Financials Tab */}
        {activeTab === 'onboardingBilling' &&
          onboardingBillingSubtab === 'financials' && (
            <div className="space-y-6">
              <FinancialsTab />
            </div>
          )}

        {/* Subscriptions Tab */}
        {activeTab === 'onboardingBilling' &&
          onboardingBillingSubtab === 'subscriptions' && (
            <div className="space-y-6">
              <SubscriptionsTab />
            </div>
          )}

        {/* Plans Tab */}

        {activeTab === 'onboardingBilling' &&
          onboardingBillingSubtab === 'plans' && (
            <div className="space-y-6">
              {planError && !isPlanModalOpen && (
                <div className="p-3 text-sm text-error-500 bg-error-50 dark:bg-error-900/20 rounded-lg border border-error-200 dark:border-error-800">
                  {planError}
                </div>
              )}
              {planSuccess && (
                <div className="p-3 text-sm text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  {planSuccess}
                </div>
              )}

              {plansSubTab === 'catalog' ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center bg-white dark:bg-gray-900 p-4 rounded-[28px] border border-gray-250 dark:border-gray-800">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">
                      Subscription Plans Catalog
                    </h3>
                    <button
                      onClick={() => {
                        setSelectedPlan(null);
                        setPlanFormData({
                          code: '',
                          name: '',
                          description: '',
                          currency: 'USD',
                          base_price_cents: 0,
                          included_minutes: 0,
                          overage_rate_cents_per_minute: 0,
                          setup_fee_cents: 0,
                          per_order_rate_cents: 0,
                          multilingual_addon_cents: 0,
                          multilingual_included: false,
                          sms_ordering_addon_cents: 0,
                          sms_ordering_included: false,
                          opentable_resy_addon_cents: 0,
                          opentable_resy_included: false,
                          is_active: true,
                          visible_on_plan_page: true,
                          trial_period_days: '',
                          feature_point_1: '',
                          feature_point_2: '',
                          feature_point_3: '',
                          feature_point_4: '',
                          feature_point_5: '',
                          feature_point_6: '',
                          feature_point_7: '',
                          feature_point_8: '',
                        });
                        setPlanError(null);
                        setIsPlanModalOpen(true);
                      }}
                      className={adminPrimaryButtonClassName}
                    >
                      + Create Plan
                    </button>
                  </div>
                  <ComponentCard title="Defined Plans">
                    <div className="mb-4 max-w-md">
                      <Label htmlFor="plan-catalog-search">Search</Label>
                      <Input
                        id="plan-catalog-search"
                        type="search"
                        value={planSearch}
                        onChange={(event) => setPlanSearch(event.target.value)}
                        placeholder="Search plan name or code"
                      />
                    </div>
                    {loadingPlans ? (
                      <p className="text-sm text-gray-500">
                        Loading plans catalog...
                      </p>
                    ) : plans.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No plans defined in the database.
                      </p>
                    ) : visiblePlans.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No plans match that search.
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                          <thead>
                            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-400">
                              <th className="py-3 px-4">Order</th>
                              {(
                                [
                                  ['name', 'Code / Name'],
                                  ['price', 'Price'],
                                  ['minutes', 'Minutes'],
                                  ['overage', 'Overage / Min'],
                                  ['setup', 'Setup / Order'],
                                ] as const
                              ).map(([key, label]) => (
                                <th key={key} className="py-3 px-4">
                                  <button
                                    type="button"
                                    aria-label={`Sort by ${label}`}
                                    onClick={() =>
                                      setPlanSort((current) =>
                                        cyclePlanSort(current, key),
                                      )
                                    }
                                  >
                                    {label}
                                    {planSort.key === key
                                      ? planSort.direction === 'asc'
                                        ? ' ↑'
                                        : ' ↓'
                                      : ''}
                                  </button>
                                </th>
                              ))}
                              <th className="py-3 px-4">Add-ons Included</th>
                              <th className="py-3 px-4">
                                <button
                                  type="button"
                                  aria-label="Sort by Status"
                                  onClick={() =>
                                    setPlanSort((current) =>
                                      cyclePlanSort(current, 'status'),
                                    )
                                  }
                                >
                                  Status
                                  {planSort.key === 'status'
                                    ? planSort.direction === 'asc'
                                      ? ' ↑'
                                      : ' ↓'
                                    : ''}
                                </button>
                              </th>
                              <th className="py-3 px-4 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-150 dark:divide-gray-800/80 text-sm">
                            {visiblePlans.map((p) => {
                              const idx = plans.findIndex(
                                (item) => item.id === p.id,
                              );
                              return (
                                <tr
                                  key={p.id}
                                  className="hover:bg-gray-50/50 dark:hover:bg-gray-850/40"
                                >
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        disabled={!planCatalogOrder || idx <= 0}
                                        onClick={() =>
                                          handleReorderPlan(idx, 'up')
                                        }
                                        className="p-1 rounded hover:bg-gray-150 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                                      >
                                        ▲
                                      </button>
                                      <button
                                        type="button"
                                        disabled={
                                          !planCatalogOrder ||
                                          idx < 0 ||
                                          idx >= plans.length - 1
                                        }
                                        onClick={() =>
                                          handleReorderPlan(idx, 'down')
                                        }
                                        className="p-1 rounded hover:bg-gray-150 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                                      >
                                        ▼
                                      </button>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="font-semibold text-gray-900 dark:text-white">
                                      {p.name}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {p.code}
                                    </div>
                                  </td>
                                  <td className="py-3 px-4">
                                    {(p.base_price_cents / 100).toLocaleString(
                                      'en-US',
                                      {
                                        style: 'currency',
                                        currency: p.currency,
                                      },
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    {p.included_minutes.toLocaleString()} min
                                  </td>
                                  <td className="py-3 px-4">
                                    {(
                                      p.overage_rate_cents_per_minute / 100
                                    ).toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: p.currency,
                                    })}
                                  </td>
                                  <td className="py-3 px-4">
                                    <div>
                                      Setup: $
                                      {(p.setup_fee_cents / 100).toFixed(2)}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      Order: $
                                      {(p.per_order_rate_cents / 100).toFixed(
                                        2,
                                      )}
                                    </div>
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="flex flex-wrap gap-1 text-xs">
                                      {p.multilingual_included && (
                                        <span className="bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 px-1.5 py-0.5 rounded">
                                          Multi
                                        </span>
                                      )}
                                      {p.sms_ordering_included && (
                                        <span className="bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 px-1.5 py-0.5 rounded">
                                          SMS
                                        </span>
                                      )}
                                      {p.opentable_resy_included && (
                                        <span className="bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 px-1.5 py-0.5 rounded">
                                          OpenTable
                                        </span>
                                      )}
                                      {!p.multilingual_included &&
                                        !p.sms_ordering_included &&
                                        !p.opentable_resy_included && (
                                          <span className="text-gray-400 italic">
                                            None
                                          </span>
                                        )}
                                    </div>
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="space-y-1">
                                      <span
                                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${p.is_active ? 'bg-green-100 text-green-800 dark:bg-green-950/35 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-950/35 dark:text-red-400'}`}
                                      >
                                        {p.is_active ? 'Active' : 'Inactive'}
                                      </span>
                                      <span
                                        className={`block text-[10px] text-gray-500`}
                                      >
                                        {p.visible_on_plan_page
                                          ? 'Visible on page'
                                          : 'Hidden'}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 text-right">
                                    <div className="flex justify-end gap-2">
                                      <button
                                        onClick={() => {
                                          setSelectedPlan(p);
                                          setPlanFormData({
                                            code: p.code,
                                            name: p.name,
                                            description: p.description || '',
                                            currency: p.currency || 'USD',
                                            base_price_cents:
                                              p.base_price_cents,
                                            included_minutes:
                                              p.included_minutes,
                                            overage_rate_cents_per_minute:
                                              p.overage_rate_cents_per_minute,
                                            setup_fee_cents: p.setup_fee_cents,
                                            per_order_rate_cents:
                                              p.per_order_rate_cents,
                                            multilingual_addon_cents:
                                              p.multilingual_addon_cents,
                                            multilingual_included:
                                              p.multilingual_included,
                                            sms_ordering_addon_cents:
                                              p.sms_ordering_addon_cents,
                                            sms_ordering_included:
                                              p.sms_ordering_included,
                                            opentable_resy_addon_cents:
                                              p.opentable_resy_addon_cents,
                                            opentable_resy_included:
                                              p.opentable_resy_included,
                                            is_active: p.is_active,
                                            visible_on_plan_page:
                                              p.visible_on_plan_page,
                                            trial_period_days:
                                              p.trial_period_days ?? '',
                                            feature_point_1:
                                              p.feature_point_1 || '',
                                            feature_point_2:
                                              p.feature_point_2 || '',
                                            feature_point_3:
                                              p.feature_point_3 || '',
                                            feature_point_4:
                                              p.feature_point_4 || '',
                                            feature_point_5:
                                              p.feature_point_5 || '',
                                            feature_point_6:
                                              p.feature_point_6 || '',
                                            feature_point_7:
                                              p.feature_point_7 || '',
                                            feature_point_8:
                                              p.feature_point_8 || '',
                                          });
                                          setPlanError(null);
                                          setIsPlanModalOpen(true);
                                        }}
                                        className="text-brand-500 hover:text-brand-700 font-semibold"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeletePlan(p.id)}
                                        className="text-error-500 hover:text-error-700 font-semibold"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </ComponentCard>
                </div>
              ) : (
                <ComponentCard title="Store-Specific Pricing Overrides">
                  {customPricingError && (
                    <div className="mb-4 p-3 text-sm text-error-500 bg-error-50 dark:bg-error-900/20 rounded-lg border border-error-200 dark:border-error-800">
                      {customPricingError}
                    </div>
                  )}
                  {customPricingSuccess && (
                    <div className="mb-4 p-3 text-sm text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                      {customPricingSuccess}
                    </div>
                  )}

                  <form
                    onSubmit={handleApplyCustomPricing}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label>Select Store</Label>
                        <SearchableSelect
                          options={storeOptions}
                          value={selectedCustomStoreId || ''}
                          onChange={(val) => {
                            const id = val ? parseInt(val, 10) : 0;
                            setSelectedCustomStoreId(id || null);
                            if (id) {
                              loadStoreCurrentSubscription(id);
                            }
                          }}
                        />
                      </div>

                      <div>
                        <Label>Select Plan Base</Label>
                        <select
                          value={selectedCustomPlanCode}
                          onChange={(e) => handleBasePlanChange(e.target.value)}
                          className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                        >
                          <option value="">-- Choose Plan --</option>
                          {plans.map((p) => (
                            <option key={p.code} value={p.code}>
                              {p.name} ({p.code})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label>Pricing Source</Label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="radio"
                            name="pricing_source"
                            value="default"
                            checked={customPricingSource === 'default'}
                            onChange={() => setCustomPricingSource('default')}
                          />
                          Use Default Plan Rates
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="radio"
                            name="pricing_source"
                            value="custom"
                            checked={customPricingSource === 'custom'}
                            onChange={() => setCustomPricingSource('custom')}
                          />
                          Custom Override Pricing
                        </label>
                      </div>
                    </div>

                    {customPricingSource === 'custom' && (
                      <div className="space-y-6 border-t border-gray-100 dark:border-gray-800/80 pt-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                          Custom Limits & Rates Override
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <Label>Base Price Cents (per month)</Label>
                            <Input
                              type="number"
                              value={customBasePriceCents}
                              onChange={(e) =>
                                setCustomBasePriceCents(Number(e.target.value))
                              }
                            />
                          </div>
                          <div>
                            <Label>Included Minutes</Label>
                            <Input
                              type="number"
                              value={customIncludedMinutes}
                              onChange={(e) =>
                                setCustomIncludedMinutes(Number(e.target.value))
                              }
                            />
                          </div>
                          <div>
                            <Label>Overage Rate Cents (per minute)</Label>
                            <Input
                              type="number"
                              value={customOverageRateCents}
                              onChange={(e) =>
                                setCustomOverageRateCents(
                                  Number(e.target.value),
                                )
                              }
                            />
                          </div>
                          <div>
                            <Label>Setup Fee Cents</Label>
                            <Input
                              type="number"
                              value={customSetupFeeCents}
                              onChange={(e) =>
                                setCustomSetupFeeCents(Number(e.target.value))
                              }
                            />
                          </div>
                          <div>
                            <Label>Per-Order Fee Cents</Label>
                            <Input
                              type="number"
                              value={customPerOrderRateCents}
                              onChange={(e) =>
                                setCustomPerOrderRateCents(
                                  Number(e.target.value),
                                )
                              }
                            />
                          </div>
                        </div>

                        <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider pt-4">
                          Add-ons Overrides
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="border border-gray-200 dark:border-gray-800 p-4 rounded-xl space-y-4">
                            <span className="font-semibold text-xs text-gray-900 dark:text-white block">
                              Multilingual Support
                            </span>
                            <div>
                              <Label>Price Cents</Label>
                              <Input
                                type="number"
                                value={customMultilingualCents}
                                onChange={(e) =>
                                  setCustomMultilingualCents(
                                    Number(e.target.value),
                                  )
                                }
                              />
                            </div>
                            <div className="flex flex-col gap-2">
                              <label className="flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={customMultilingualIncluded}
                                  onChange={(e) =>
                                    setCustomMultilingualIncluded(
                                      e.target.checked,
                                    )
                                  }
                                />
                                Included in Base Plan
                              </label>
                              <label className="flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={customMultilingualActive}
                                  onChange={(e) =>
                                    setCustomMultilingualActive(
                                      e.target.checked,
                                    )
                                  }
                                />
                                Active / Enabled for Store
                              </label>
                            </div>
                          </div>

                          <div className="border border-gray-200 dark:border-gray-800 p-4 rounded-xl space-y-4">
                            <span className="font-semibold text-xs text-gray-900 dark:text-white block">
                              SMS Ordering
                            </span>
                            <div>
                              <Label>Price Cents</Label>
                              <Input
                                type="number"
                                value={customSmsOrderingCents}
                                onChange={(e) =>
                                  setCustomSmsOrderingCents(
                                    Number(e.target.value),
                                  )
                                }
                              />
                            </div>
                            <div className="flex flex-col gap-2">
                              <label className="flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={customSmsOrderingIncluded}
                                  onChange={(e) =>
                                    setCustomSmsOrderingIncluded(
                                      e.target.checked,
                                    )
                                  }
                                />
                                Included in Base Plan
                              </label>
                              <label className="flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={customSmsOrderingActive}
                                  onChange={(e) =>
                                    setCustomSmsOrderingActive(e.target.checked)
                                  }
                                />
                                Active / Enabled for Store
                              </label>
                            </div>
                          </div>

                          <div className="border border-gray-200 dark:border-gray-800 p-4 rounded-xl space-y-4">
                            <span className="font-semibold text-xs text-gray-900 dark:text-white block">
                              OpenTable / Resy
                            </span>
                            <div>
                              <Label>Price Cents</Label>
                              <Input
                                type="number"
                                value={customOpentableResyCents}
                                onChange={(e) =>
                                  setCustomOpentableResyCents(
                                    Number(e.target.value),
                                  )
                                }
                              />
                            </div>
                            <div className="flex flex-col gap-2">
                              <label className="flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={customOpentableResyIncluded}
                                  onChange={(e) =>
                                    setCustomOpentableResyIncluded(
                                      e.target.checked,
                                    )
                                  }
                                />
                                Included in Base Plan
                              </label>
                              <label className="flex items-center gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  checked={customOpentableResyActive}
                                  onChange={(e) =>
                                    setCustomOpentableResyActive(
                                      e.target.checked,
                                    )
                                  }
                                />
                                Active / Enabled for Store
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-4 border-t border-gray-100 dark:border-gray-800/80 pt-6">
                      <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                        Contract & Details
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label>Contract Start Date</Label>
                          <Input
                            type="datetime-local"
                            value={contractStart}
                            onChange={(e) => setContractStart(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Contract End Date</Label>
                          <Input
                            type="datetime-local"
                            value={contractEnd}
                            onChange={(e) => setContractEnd(e.target.value)}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Custom Pricing Note</Label>
                        <textarea
                          value={customPricingNote}
                          onChange={(e) => setCustomPricingNote(e.target.value)}
                          placeholder="e.g. Enterprise discount approved by Ishaan"
                          className="w-full min-h-[80px] px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end pt-4">
                      <button
                        type="submit"
                        disabled={customPricingLoading}
                        className={adminPrimaryButtonClassName}
                      >
                        {customPricingLoading
                          ? 'Applying Override...'
                          : 'Apply Pricing Override'}
                      </button>
                    </div>
                  </form>
                </ComponentCard>
              )}
            </div>
          )}

        {/* Edit Store Modal (from Stores table) */}
        <Modal
          isOpen={isEditStoreModalOpen}
          onClose={() => {
            setIsEditStoreModalOpen(false);
            setStoreTableMenuId(null);
          }}
          className="max-w-4xl p-6 lg:p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Edit Store
              {editStoreFormData?.name ? ` — ${editStoreFormData.name}` : ''}
            </h3>
          </div>

          {editStoreLoading && (
            <p className="text-sm text-gray-500">Loading store details...</p>
          )}

          {editStoreFormData && !editStoreLoading && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                handleOpenConfirmEdit(e);
              }}
              className="space-y-5 max-h-[70vh] overflow-y-auto pr-2"
            >
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label>Store Name</Label>
                  <Input
                    type="text"
                    name="name"
                    value={editStoreFormData.name}
                    onChange={handleEditStoreChange}
                  />
                </div>
                <div>
                  <Label>Phonetic Name</Label>
                  <Input
                    type="text"
                    name="phonetic_name"
                    value={editStoreFormData.phonetic_name || ''}
                    onChange={handleEditStoreChange}
                  />
                </div>
                <div>
                  <Label>Timezone</Label>
                  <select
                    name="timezone"
                    value={editStoreFormData.timezone}
                    onChange={handleEditStoreChange}
                    className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                  >
                    {STORE_TIMEZONE_OPTIONS.map((timezone) => (
                      <option key={timezone} value={timezone}>
                        {timezone}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Store Group</Label>
                  <SearchableSelect
                    options={storeGroupOptions}
                    value={editStoreFormData.store_group || ''}
                    onChange={(value) =>
                      setEditStoreFormData((prev: any) => ({
                        ...prev,
                        store_group: value ? parseInt(value, 10) : null,
                      }))
                    }
                    placeholder={
                      loadingStoreGroups
                        ? 'Loading store groups...'
                        : 'Select a store group'
                    }
                    disabled={loadingStoreGroups}
                  />
                </div>
              </div>
              <CallIntroMessageField
                customName="call_intro_sentence"
                customValue={editStoreFormData.call_intro_sentence || ''}
                useDefault={
                  editStoreFormData.use_default_call_intro_sentence ?? true
                }
                onCustomChange={handleEditStoreChange}
                onUseDefaultChange={(useDefault) =>
                  setEditStoreFormData((prev: any) => ({
                    ...prev,
                    use_default_call_intro_sentence: useDefault,
                  }))
                }
                defaultIntro={
                  editStoreFormData.default_call_intro_sentence ||
                  getDefaultCallIntroSentence(
                    editStoreFormData.phonetic_name || editStoreFormData.name,
                  )
                }
              />
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label>Speech Filler Phrase</Label>
                  <Input
                    type="text"
                    name="speach_filler_phrase"
                    value={editStoreFormData.speach_filler_phrase || ''}
                    onChange={handleEditStoreChange}
                    placeholder="Sure, one moment please"
                  />
                </div>
                <div>
                  <Label>VIP Intro Phrase</Label>
                  <Input
                    type="text"
                    name="vip_intro_phrase"
                    value={editStoreFormData.vip_intro_phrase || ''}
                    onChange={handleEditStoreChange}
                    placeholder="Hello {{vip_caller_name}}! Thanks for being a valued member at {{store_name}}."
                  />
                </div>
                <div>
                  <Label>Agent Style</Label>
                  <textarea
                    name="agent_style"
                    value={editStoreFormData.agent_style || ''}
                    onChange={handleEditStoreChange}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                    placeholder="Describe how the voice agent should communicate"
                  />
                </div>
              </div>
              <div>
                <Label>Legal Name</Label>
                <Input
                  type="text"
                  name="legal_name"
                  value={editStoreFormData.legal_name || ''}
                  onChange={handleEditStoreChange}
                />
              </div>
              <div>
                <Label>Business Number</Label>
                <Input
                  type="text"
                  name="business_number"
                  value={editStoreFormData.business_number || ''}
                  onChange={handleEditStoreChange}
                />
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label>Address Line 1</Label>
                  <Input
                    type="text"
                    name="address_line_1"
                    value={editStoreFormData.address_line_1}
                    onChange={handleEditStoreChange}
                  />
                </div>
                <div>
                  <Label>Address Line 2</Label>
                  <Input
                    type="text"
                    name="address_line_2"
                    value={editStoreFormData.address_line_2}
                    onChange={handleEditStoreChange}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <div>
                  <Label>City</Label>
                  <Input
                    type="text"
                    name="city"
                    value={editStoreFormData.city}
                    onChange={handleEditStoreChange}
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    type="text"
                    name="state"
                    value={editStoreFormData.state}
                    onChange={handleEditStoreChange}
                  />
                </div>
                <div>
                  <Label>Postal Code</Label>
                  <Input
                    type="text"
                    name="postal_code"
                    value={editStoreFormData.postal_code}
                    onChange={handleEditStoreChange}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label>Country</Label>
                  <select
                    name="country"
                    value={editStoreFormData.country}
                    onChange={handleEditStoreChange}
                    className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                  >
                    {STORE_COUNTRY_OPTIONS.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Website</Label>
                  <Input
                    type="url"
                    name="website"
                    value={editStoreFormData.website}
                    onChange={handleEditStoreChange}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label>Menu Link</Label>
                  <Input
                    type="text"
                    name="menu_link"
                    value={editStoreFormData.menu_link}
                    onChange={handleEditStoreChange}
                  />
                </div>
                <div>
                  <Label>Ordering Link</Label>
                  <Input
                    type="url"
                    name="ordering_link"
                    value={editStoreFormData.ordering_link || ''}
                    onChange={handleEditStoreChange}
                  />
                </div>
                <KnowledgeBaseSelect
                  value={editStoreFormData.knowledge_base_id || ''}
                  onChange={(value) =>
                    setEditStoreFormData((prev: any) => ({
                      ...prev,
                      knowledge_base_id: value,
                    }))
                  }
                  options={retellKnowledgeBaseOptions}
                  loading={loadingRetellKnowledgeBases}
                  error={retellKnowledgeBaseError}
                />
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label>Stripe Account ID</Label>
                  <Input
                    type="text"
                    name="stripe_account_id"
                    value={editStoreFormData.stripe_account_id}
                    onChange={handleEditStoreChange}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label>Reservation Link</Label>
                  <Input
                    type="url"
                    name="reservation_link"
                    value={editStoreFormData.reservation_link || ''}
                    onChange={handleEditStoreChange}
                  />
                </div>
                <div>
                  <Label>Gift Card Link</Label>
                  <Input
                    type="url"
                    name="gift_card_link"
                    value={editStoreFormData.gift_card_link || ''}
                    onChange={handleEditStoreChange}
                  />
                </div>
                <div>
                  <Label>Reservation Email</Label>
                  <Input
                    type="email"
                    name="reservation_email"
                    value={editStoreFormData.reservation_email}
                    onChange={handleEditStoreChange}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label>Secondary Phone Number</Label>
                  <Input
                    type="tel"
                    name="location_secondary_phone_number"
                    value={
                      editStoreFormData.location_secondary_phone_number || ''
                    }
                    onChange={handleEditStoreChange}
                    placeholder="+1 555-5555"
                  />
                </div>
                <div>
                  <Label>Secondary Phone Extension</Label>
                  <Input
                    type="text"
                    name="location_secondary_phone_number_ext"
                    value={
                      editStoreFormData.location_secondary_phone_number_ext ||
                      ''
                    }
                    onChange={handleEditStoreChange}
                    placeholder="123#"
                  />
                </div>
              </div>
              <div>
                <Label>How To Order Delivery Message</Label>
                <Input
                  type="text"
                  name="how_to_order_delivery_message"
                  value={editStoreFormData.how_to_order_delivery_message || ''}
                  onChange={handleEditStoreChange}
                />
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <div>
                  <Label>Default Delivery Fee (cents)</Label>
                  <Input
                    type="number"
                    name="default_delivery_fee_cents"
                    value={editStoreFormData.default_delivery_fee_cents}
                    onChange={handleEditStoreChange}
                  />
                </div>
                <div>
                  <Label>Application Fee Default (cents)</Label>
                  <Input
                    type="number"
                    name="application_fee_default_cents"
                    value={editStoreFormData.application_fee_default_cents}
                    onChange={handleEditStoreChange}
                  />
                </div>
                <div>
                  <Label>Default Delivery Mode</Label>
                  <select
                    name="default_delivery_mode"
                    value={editStoreFormData.default_delivery_mode}
                    onChange={handleEditStoreChange}
                    className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                  >
                    <option value="none">No Deliveries</option>
                    <option value="dispatch">Third-Party Dispatch</option>
                    <option value="self">Restaurant Self-Delivery</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <Label>Monthly Minutes Limit</Label>
                  <Input
                    type="number"
                    name="monthly_minutes_limit"
                    value={editStoreFormData.monthly_minutes_limit}
                    onChange={handleEditStoreChange}
                  />
                </div>
              </div>
              <div>
                <Label>Parking Info</Label>
                <textarea
                  name="parking_info"
                  value={editStoreFormData.parking_info || ''}
                  onChange={handleEditStoreChange}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                  placeholder="Parking details, lot location, valet info, etc."
                />
              </div>
              <div>
                <Label>POS System</Label>
                <select
                  name="current_pos_system"
                  value={editStoreFormData.current_pos_system || 'UNKNOWN'}
                  onChange={handleEditStoreChange}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                  <option value="UNKNOWN">None / Unknown</option>
                  <option value="TOAST">Toast</option>
                  <option value="FOODHUB">FoodHub</option>
                  <option value="CLOVER">Clover</option>
                  <option value="SQUARE">Square</option>
                  <option value="SHOPIFY">Shopify</option>
                  <option value="LIGHTSPEED">Lightspeed</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              {renderStoreBooleanSections(
                editStoreFormData,
                handleEditStoreChange,
                {
                  'Reservation & Seating Options': (
                    <ExplicitOnlyTableTypesField
                      value={editStoreFormData.explicit_only_table_types}
                      onChange={(values) =>
                        setEditStoreFormData((prev: any) =>
                          prev
                            ? { ...prev, explicit_only_table_types: values }
                            : prev,
                        )
                      }
                    />
                  ),
                },
              )}

              {editStoreError && (
                <div className="p-3 text-sm text-error-600 bg-error-50 border border-error-200 rounded-lg">
                  {editStoreError}
                </div>
              )}
              {editStoreSuccess && (
                <div className="p-3 text-sm text-success-600 bg-success-50 border border-success-200 rounded-lg">
                  {editStoreSuccess}
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => setIsEditStoreModalOpen(false)}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
                >
                  Save Changes
                </button>
              </div>
            </form>
          )}
        </Modal>

        <Modal
          isOpen={intakePickerOpen && !intakePickerInUsersTab}
          onClose={() => setIntakePickerOpen(false)}
          className="max-w-2xl p-6 lg:p-8"
        >
          <div className="space-y-5">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Populate from Onboarding Intake
            </h3>
            <div>
              <Label>Onboarding Intake</Label>
              <SearchableSelect
                options={intakePickerOptions.map((intake) => {
                  const contact = getIntakePrimaryContact(intake);
                  return {
                    value: intake.id,
                    label: `${getIntakeRestaurantName(intake) || `Intake #${intake.id}`} (ID: ${intake.id})${
                      contact.email ? ` - ${contact.email}` : ''
                    }`,
                  };
                })}
                value={intakePickerSelectedId}
                onChange={setIntakePickerSelectedId}
                placeholder={
                  intakePickerLoading
                    ? 'Loading intakes...'
                    : 'Select an onboarding intake'
                }
                disabled={intakePickerLoading}
                required
              />
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={intakePickerOverwrite}
                onChange={(e) => setIntakePickerOverwrite(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <span>
                Overwrite existing fields instead of filling only blank fields
              </span>
            </label>
            {intakePickerError && (
              <div className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-600 dark:border-error-800 dark:bg-error-900/20">
                {intakePickerError}
              </div>
            )}
            <div className="flex justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-800">
              <button
                type="button"
                onClick={() => setIntakePickerOpen(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyIntakePrefill}
                disabled={intakePickerLoading || !intakePickerSelectedId}
                className="inline-flex items-center rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
              >
                {intakePickerLoading ? 'Applying...' : 'Apply Prefill'}
              </button>
            </div>
          </div>
        </Modal>

        {isOnboardingOpen && (
          <div className="fixed inset-0 z-[9999] flex flex-col bg-white dark:bg-gray-950">
            {/* Top bar — title left, steps centered, exit top-right */}
            <div className="flex items-center border-b border-gray-200 px-6 py-4 dark:border-gray-800">
              <span className="shrink-0 text-sm font-semibold text-gray-900 dark:text-white">
                New Store Onboarding
              </span>
              <div className="flex flex-1 items-center justify-center gap-2 px-4">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`h-2 w-8 rounded-full transition-colors ${
                      i < obStep
                        ? 'bg-green-500'
                        : i === obStep
                          ? 'bg-brand-500'
                          : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleCloseOnboarding}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
                aria-label="Exit onboarding"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Exit
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="mx-auto max-w-2xl px-6 py-12">
                {/* Step 0: Create Store */}
                {obStep === 0 && (
                  <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-brand-500">
                        Step 1 of 4
                      </p>
                      <h2 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                        Create a Store
                      </h2>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Fill in the store details below. You can always edit
                        these later.
                      </p>
                    </div>

                    <div className="space-y-5">
                      <div className="rounded-2xl border border-brand-100 bg-brand-50/70 p-4 dark:border-brand-900/40 dark:bg-brand-900/10">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                              Populate with onboarding info
                            </h3>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Fill this store form from a submitted onboarding
                              intake. Existing fields are preserved by default.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => openIntakePicker('store')}
                            className="inline-flex items-center justify-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
                          >
                            Populate
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <div>
                          <Label>
                            Store Name <span className="text-error-500">*</span>
                          </Label>
                          <Input
                            type="text"
                            name="name"
                            value={storeFormData.name}
                            onChange={handleStoreChange}
                            required
                            placeholder="e.g., Black Iron"
                          />
                        </div>
                        <div>
                          <Label>Phonetic Name</Label>
                          <Input
                            type="text"
                            name="phonetic_name"
                            value={storeFormData.phonetic_name}
                            onChange={handleStoreChange}
                            placeholder="How the AI pronounces it"
                          />
                        </div>
                      </div>

                      <CallIntroMessageField
                        customName="call_intro_sentence"
                        customValue={storeFormData.call_intro_sentence}
                        useDefault={
                          storeFormData.use_default_call_intro_sentence
                        }
                        onCustomChange={handleStoreChange}
                        onUseDefaultChange={(useDefault) =>
                          setStoreFormData((prev) => ({
                            ...prev,
                            use_default_call_intro_sentence: useDefault,
                          }))
                        }
                        defaultIntro={getDefaultCallIntroSentence(
                          storeFormData.phonetic_name || storeFormData.name,
                        )}
                      />
                      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <div>
                          <Label>Speech Filler Phrase</Label>
                          <Input
                            type="text"
                            name="speach_filler_phrase"
                            value={storeFormData.speach_filler_phrase}
                            onChange={handleStoreChange}
                          />
                        </div>
                        <div>
                          <Label>VIP Intro Phrase</Label>
                          <Input
                            type="text"
                            name="vip_intro_phrase"
                            value={storeFormData.vip_intro_phrase}
                            onChange={handleStoreChange}
                          />
                        </div>
                        <div>
                          <Label>Agent Style</Label>
                          <textarea
                            name="agent_style"
                            value={storeFormData.agent_style}
                            onChange={handleStoreChange}
                            rows={3}
                            className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                            placeholder="Describe how the voice agent should communicate"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <div>
                          <Label>Legal Name</Label>
                          <Input
                            type="text"
                            name="legal_name"
                            value={storeFormData.legal_name}
                            onChange={handleStoreChange}
                            placeholder="Registered entity name"
                          />
                        </div>
                        <div>
                          <Label>Business Number</Label>
                          <Input
                            type="text"
                            name="business_number"
                            value={storeFormData.business_number}
                            onChange={handleStoreChange}
                            placeholder="Business registration number"
                          />
                        </div>
                        <div>
                          <Label>Store Group</Label>
                          <SearchableSelect
                            options={storeGroupOptions}
                            value={storeFormData.store_group || ''}
                            onChange={(value) =>
                              setStoreFormData((prev) => ({
                                ...prev,
                                store_group: value ? parseInt(value, 10) : null,
                              }))
                            }
                            placeholder={
                              loadingStoreGroups
                                ? 'Loading store groups...'
                                : 'No store group'
                            }
                            disabled={loadingStoreGroups}
                          />
                        </div>
                        <div>
                          <Label>Timezone</Label>
                          <select
                            name="timezone"
                            value={storeFormData.timezone}
                            onChange={handleStoreChange}
                            className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                          >
                            {STORE_TIMEZONE_OPTIONS.map((timezone) => (
                              <option key={timezone} value={timezone}>
                                {timezone}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <Label>Address Line 1</Label>
                        <Input
                          type="text"
                          name="address_line_1"
                          value={storeFormData.address_line_1}
                          onChange={handleStoreChange}
                          placeholder="Street address"
                        />
                      </div>
                      <div>
                        <Label>Address Line 2</Label>
                        <Input
                          type="text"
                          name="address_line_2"
                          value={storeFormData.address_line_2}
                          onChange={handleStoreChange}
                          placeholder="Apartment, suite, etc."
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                        <div>
                          <Label>City</Label>
                          <Input
                            type="text"
                            name="city"
                            value={storeFormData.city}
                            onChange={handleStoreChange}
                          />
                        </div>
                        <div>
                          <Label>State</Label>
                          <Input
                            type="text"
                            name="state"
                            value={storeFormData.state}
                            onChange={handleStoreChange}
                            placeholder="e.g., NY"
                          />
                        </div>
                        <div>
                          <Label>Postal Code</Label>
                          <Input
                            type="text"
                            name="postal_code"
                            value={storeFormData.postal_code}
                            onChange={handleStoreChange}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Country</Label>
                        <select
                          name="country"
                          value={storeFormData.country}
                          onChange={handleStoreChange}
                          className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                        >
                          {STORE_COUNTRY_OPTIONS.map((country) => (
                            <option key={country.value} value={country.value}>
                              {country.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <Label>Website</Label>
                        <Input
                          type="url"
                          name="website"
                          value={storeFormData.website}
                          onChange={handleStoreChange}
                          placeholder="https://example.com"
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <div>
                          <Label>Menu Link</Label>
                          <Input
                            type="text"
                            name="menu_link"
                            value={storeFormData.menu_link}
                            onChange={handleStoreChange}
                            placeholder="Menu link"
                          />
                        </div>
                        <div>
                          <Label>Ordering Link</Label>
                          <Input
                            type="url"
                            name="ordering_link"
                            value={storeFormData.ordering_link}
                            onChange={handleStoreChange}
                            placeholder="https://order.example.com"
                          />
                        </div>
                        <div>
                          <Label>Reservation Link</Label>
                          <Input
                            type="url"
                            name="reservation_link"
                            value={storeFormData.reservation_link}
                            onChange={handleStoreChange}
                            placeholder="https://reservations.example.com"
                          />
                        </div>
                        <div>
                          <Label>Gift Card Link</Label>
                          <Input
                            type="url"
                            name="gift_card_link"
                            value={storeFormData.gift_card_link}
                            onChange={handleStoreChange}
                            placeholder="https://giftcards.example.com"
                          />
                        </div>
                        <KnowledgeBaseSelect
                          value={storeFormData.knowledge_base_id}
                          onChange={(value) =>
                            setStoreFormData((prev) => ({
                              ...prev,
                              knowledge_base_id: value,
                            }))
                          }
                          options={retellKnowledgeBaseOptions}
                          loading={loadingRetellKnowledgeBases}
                          error={retellKnowledgeBaseError}
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <div>
                          <Label>Secondary Phone Number</Label>
                          <Input
                            type="tel"
                            name="location_secondary_phone_number"
                            value={
                              storeFormData.location_secondary_phone_number
                            }
                            onChange={handleStoreChange}
                            placeholder="+1 555-5555"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Number that the agent will call to reach the store.
                          </p>
                        </div>
                        <div>
                          <Label>Secondary Phone Extension</Label>
                          <Input
                            type="text"
                            name="location_secondary_phone_number_ext"
                            value={
                              storeFormData.location_secondary_phone_number_ext
                            }
                            onChange={handleStoreChange}
                            placeholder="123#"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Leave blank when this number has no extension.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        <div>
                          <Label>
                            Monthly Minutes Limit{' '}
                            <span className="text-error-500">*</span>
                          </Label>
                          <Input
                            type="number"
                            name="monthly_minutes_limit"
                            value={storeFormData.monthly_minutes_limit}
                            onChange={handleStoreChange}
                            required
                            placeholder="500"
                            min="0"
                            step={1}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Parking Info</Label>
                        <textarea
                          name="parking_info"
                          value={storeFormData.parking_info || ''}
                          onChange={handleStoreChange}
                          rows={3}
                          className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                          placeholder="Parking details, lot location, valet info, etc."
                        />
                      </div>
                      <div>
                        <Label>POS System</Label>
                        <select
                          name="current_pos_system"
                          value={storeFormData.current_pos_system || 'UNKNOWN'}
                          onChange={handleStoreChange}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        >
                          <option value="UNKNOWN">None / Unknown</option>
                          <option value="TOAST">Toast</option>
                          <option value="FOODHUB">FoodHub</option>
                          <option value="CLOVER">Clover</option>
                          <option value="SQUARE">Square</option>
                          <option value="SHOPIFY">Shopify</option>
                          <option value="LIGHTSPEED">Lightspeed</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                      {renderStoreBooleanSections(
                        storeFormData,
                        handleStoreChange,
                        {
                          'Reservation & Seating Options': (
                            <ExplicitOnlyTableTypesField
                              value={storeFormData.explicit_only_table_types}
                              onChange={(values) =>
                                setStoreFormData((prev) => ({
                                  ...prev,
                                  explicit_only_table_types: values,
                                }))
                              }
                            />
                          ),
                        },
                      )}
                    </div>

                    {obError && (
                      <div className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-600 dark:border-error-800 dark:bg-error-900/20">
                        {obError}
                      </div>
                    )}
                    {obSuccess && (
                      <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-600 dark:border-success-800 dark:bg-success-900/20">
                        {obSuccess}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                      <button
                        type="button"
                        onClick={obNext}
                        className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        Skip this step
                      </button>
                      <button
                        type="button"
                        onClick={handleObStoreSubmit}
                        disabled={obSubmitting || !storeFormData.name}
                        className="inline-flex items-center rounded-lg bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
                      >
                        {obSubmitting
                          ? 'Creating...'
                          : 'Create Store & Continue'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 1: Store Hours */}
                {obStep === 1 && (
                  <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-brand-500">
                        Step 2 of 4
                      </p>
                      <h2 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                        Set Store Hours
                      </h2>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {obCreatedStoreId
                          ? `These manual hours will be saved for "${obCreatedStoreName}" (ID: ${obCreatedStoreId}).`
                          : 'Create a store first, then add weekly hours.'}
                      </p>
                    </div>

                    {!obCreatedStoreId && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                        No store was created in the previous step. You can skip
                        this step or go back.
                      </div>
                    )}

                    {obCreatedStoreId && (
                      <div className="space-y-4">
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-200">
                          These hours are saved as a manual dashboard schedule.
                          POS-connected automatic hours can be configured later.
                        </div>
                        <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700">
                          <table className="w-full text-left">
                            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                              <tr>
                                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                                  Day
                                </th>
                                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                                  Status
                                </th>
                                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                                  Opens
                                </th>
                                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                                  Closes
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {obWeeklyHours.map((row) => (
                                <tr
                                  key={row.day_of_week}
                                  className="border-b border-gray-100 last:border-b-0 dark:border-gray-800"
                                >
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                                    {row.day_of_week}
                                  </td>
                                  <td className="px-4 py-3">
                                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                                      <input
                                        type="checkbox"
                                        checked={row.is_closed}
                                        onChange={(e) =>
                                          updateObWeeklyHour(row.day_of_week, {
                                            is_closed: e.target.checked,
                                          })
                                        }
                                        className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
                                      />
                                      Closed
                                    </label>
                                  </td>
                                  <td className="px-4 py-3">
                                    {row.is_closed ? (
                                      <span className="text-sm text-gray-400">
                                        -
                                      </span>
                                    ) : (
                                      <input
                                        type="time"
                                        value={row.opening_time}
                                        onChange={(e) =>
                                          updateObWeeklyHour(row.day_of_week, {
                                            opening_time: e.target.value,
                                          })
                                        }
                                        className="w-full min-w-[130px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                      />
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    {row.is_closed ? (
                                      <span className="text-sm text-gray-400">
                                        -
                                      </span>
                                    ) : (
                                      <input
                                        type="time"
                                        value={row.closing_time}
                                        onChange={(e) =>
                                          updateObWeeklyHour(row.day_of_week, {
                                            closing_time: e.target.value,
                                          })
                                        }
                                        className="w-full min-w-[130px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                      />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {obError && (
                      <div className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-600 dark:border-error-800 dark:bg-error-900/20">
                        {obError}
                      </div>
                    )}
                    {obSuccess && (
                      <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-600 dark:border-success-800 dark:bg-success-900/20">
                        {obSuccess}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={obPrev}
                          className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={obNext}
                          className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Skip
                        </button>
                      </div>
                      {obCreatedStoreId && (
                        <button
                          type="button"
                          onClick={handleObStoreHoursSubmit}
                          disabled={obSubmitting}
                          className="inline-flex items-center rounded-lg bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
                        >
                          {obSubmitting ? 'Saving...' : 'Save Hours & Continue'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 2: Create Store Owner */}
                {obStep === 2 && (
                  <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-brand-500">
                        Step 3 of 4
                      </p>
                      <h2 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                        Create a Store Owner
                      </h2>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {obCreatedStoreId
                          ? `This owner will be assigned to "${obCreatedStoreName}" (ID: ${obCreatedStoreId}).`
                          : 'You skipped store creation. You can still create an owner for an existing store later.'}
                      </p>
                    </div>

                    {!obCreatedStoreId && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                        No store was created in the previous step. You can skip
                        this step or go back.
                      </div>
                    )}

                    {obCreatedStoreId && (
                      <div className="space-y-5">
                        <div className="rounded-2xl border border-brand-100 bg-brand-50/70 p-4 dark:border-brand-900/40 dark:bg-brand-900/10">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                Populate owner from onboarding info
                              </h3>
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Fill owner user fields from the selected intake.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => openIntakePicker('owner_user')}
                              className="inline-flex items-center justify-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
                            >
                              Populate Owner
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                          <div>
                            <Label>
                              First Name{' '}
                              <span className="text-error-500">*</span>
                            </Label>
                            <Input
                              type="text"
                              name="first_name"
                              value={obUserForm.first_name}
                              onChange={handleObUserChange}
                              required
                            />
                          </div>
                          <div>
                            <Label>
                              Last Name{' '}
                              <span className="text-error-500">*</span>
                            </Label>
                            <Input
                              type="text"
                              name="last_name"
                              value={obUserForm.last_name}
                              onChange={handleObUserChange}
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <Label>
                            Email <span className="text-error-500">*</span>
                          </Label>
                          <Input
                            type="email"
                            name="email"
                            value={obUserForm.email}
                            onChange={handleObUserChange}
                            required
                          />
                        </div>
                        <div>
                          <Label>
                            Password <span className="text-error-500">*</span>
                          </Label>
                          <Input
                            type="password"
                            name="password"
                            value={obUserForm.password}
                            onChange={handleObUserChange}
                            required
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            At least 8 characters
                          </p>
                        </div>
                        <div>
                          <Label>Role</Label>
                          <select
                            name="role"
                            value={obUserForm.role}
                            onChange={handleObUserChange}
                            className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                          >
                            <option value="Owner">Owner</option>
                            <option value="Manager">Manager</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {obError && (
                      <div className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-600 dark:border-error-800 dark:bg-error-900/20">
                        {obError}
                      </div>
                    )}
                    {obSuccess && (
                      <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-600 dark:border-success-800 dark:bg-success-900/20">
                        {obSuccess}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={obPrev}
                          className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={obNext}
                          className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Skip
                        </button>
                      </div>
                      {obCreatedStoreId && (
                        <button
                          type="button"
                          onClick={handleObUserSubmit}
                          disabled={
                            obSubmitting ||
                            !obUserForm.email ||
                            !obUserForm.password ||
                            !obUserForm.first_name
                          }
                          className="inline-flex items-center rounded-lg bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
                        >
                          {obSubmitting
                            ? 'Creating...'
                            : 'Create Owner & Continue'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Create Agent */}
                {obStep === 3 && (
                  <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-brand-500">
                        Step 4 of 4
                      </p>
                      <h2 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                        Create an Agent
                      </h2>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {obCreatedStoreId
                          ? `This agent will be linked to "${obCreatedStoreName}" (ID: ${obCreatedStoreId}).`
                          : 'You skipped store creation. You can skip this step or go back.'}
                      </p>
                    </div>

                    {!obCreatedStoreId && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                        No store was created. You can skip this step or go back.
                      </div>
                    )}

                    {obCreatedStoreId && (
                      <div className="space-y-5">
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                          <div>
                            <Label>
                              Retell Agent{' '}
                              <span className="text-error-500">*</span>
                            </Label>
                            <SearchableSelect
                              options={retellAgentOptions}
                              value={obSelectedRetellAgentId}
                              onChange={handleObRetellAgentSelect}
                              placeholder="Select a Retell agent..."
                              required
                              disabled={loadingRetellAgents}
                            />
                            {obSelectedRetellAgentId ===
                              MANUAL_ASSISTANT_ID_OPTION && (
                              <div className="mt-3">
                                <Label>
                                  Manual Agent ID{' '}
                                  <span className="text-error-500">*</span>
                                </Label>
                                <Input
                                  type="text"
                                  name="assistant_id"
                                  value={obAssistantForm.assistant_id}
                                  onChange={handleObAssistantChange}
                                  required
                                  placeholder="e.g., agent_xxxxxxxxxxxxx"
                                />
                              </div>
                            )}
                            <p className="mt-1 text-xs text-gray-500">
                              Choose none to enter an ID manually.
                            </p>
                          </div>
                          <div>
                            <Label>Agent Provider</Label>
                            <Input
                              type="text"
                              name="assistant_provider"
                              value={obAssistantForm.assistant_provider}
                              onChange={handleObAssistantChange}
                              placeholder="retell"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Default: retell
                            </p>
                          </div>
                          <div>
                            <Label>Phone Number</Label>
                            <SearchableSelect
                              options={approvedUnassignedPhoneNumberOptions}
                              value={obAssistantForm.phone_number}
                              onChange={(val) =>
                                handleObAssistantChange({
                                  target: {
                                    name: 'phone_number',
                                    value: val,
                                    type: 'text',
                                  },
                                } as any)
                              }
                              placeholder="Select an approved unassigned number..."
                              disabled={loadingApprovedUnassignedPhoneNumbers}
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Optional. Only approved, unassigned Twilio numbers
                              are shown.
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                          <RetellLlmModelField
                            id="onboarding-agent-retell-llm-model"
                            value={obAssistantForm.retell_llm_model}
                            onChange={handleObAssistantChange}
                          />
                          <div>
                            <Label>Language</Label>
                            <Input
                              type="text"
                              name="language"
                              value={obAssistantForm.language}
                              onChange={handleObAssistantChange}
                              placeholder="en"
                            />
                          </div>
                        </div>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            name="is_active"
                            checked={obAssistantForm.is_active}
                            onChange={handleObAssistantChange}
                            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Is Active
                          </span>
                        </label>
                      </div>
                    )}

                    {obError && (
                      <div className="rounded-lg border border-error-200 bg-error-50 p-3 text-sm text-error-600 dark:border-error-800 dark:bg-error-900/20">
                        {obError}
                      </div>
                    )}
                    {obSuccess && (
                      <div className="rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-600 dark:border-success-800 dark:bg-success-900/20">
                        {obSuccess}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={obPrev}
                          className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Back
                        </button>
                        <button
                          type="button"
                          onClick={() => setObStep(ONBOARDING_TOTAL_STEPS)}
                          className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Skip
                        </button>
                      </div>
                      {obCreatedStoreId && (
                        <button
                          type="button"
                          onClick={handleObAssistantSubmit}
                          disabled={
                            obSubmitting || !obAssistantForm.assistant_id.trim()
                          }
                          className="inline-flex items-center rounded-lg bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
                        >
                          {obSubmitting
                            ? 'Creating...'
                            : 'Create Agent & Finish'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Done */}
                {obStep >= ONBOARDING_TOTAL_STEPS && (
                  <div className="flex flex-col items-center justify-center py-20 text-center animate-[fadeIn_0.3s_ease-out]">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                      <svg
                        className="h-8 w-8 text-green-600 dark:text-green-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                    </div>
                    <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">
                      Onboarding Complete
                    </h2>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-md">
                      {obCreatedStoreId
                        ? `"${obCreatedStoreName}" is all set up. You can now manage it from the admin panel.`
                        : 'You can configure stores, owners, and assistants from the admin panel tabs.'}
                    </p>
                    <button
                      type="button"
                      onClick={handleCloseOnboarding}
                      className="mt-8 inline-flex items-center rounded-lg bg-brand-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
                    >
                      Back to Admin Panel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <Modal
          isOpen={isPlanModalOpen}
          onClose={() => {
            setIsPlanModalOpen(false);
            setSelectedPlan(null);
            setPlanError(null);
          }}
          className="max-w-4xl p-6 lg:p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {selectedPlan
                ? `Edit Subscription Plan: ${selectedPlan.name}`
                : 'Create New Subscription Plan'}
            </h3>
          </div>

          {planError && (
            <div
              role="alert"
              className="mb-5 p-3 text-sm text-error-500 bg-error-50 dark:bg-error-900/20 rounded-lg border border-error-200 dark:border-error-800"
            >
              {planError}
            </div>
          )}

          <form
            onSubmit={handleSavePlan}
            className="space-y-5 max-h-[70vh] overflow-y-auto pr-2"
          >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <Label>Plan Code (e.g. starter, growth)</Label>
                <Input
                  type="text"
                  required
                  disabled={!!selectedPlan}
                  value={planFormData.code}
                  onChange={(e) =>
                    setPlanFormData({ ...planFormData, code: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Plan Name</Label>
                <Input
                  type="text"
                  required
                  value={planFormData.name}
                  onChange={(e) =>
                    setPlanFormData({ ...planFormData, name: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <textarea
                value={planFormData.description}
                onChange={(e) =>
                  setPlanFormData({
                    ...planFormData,
                    description: e.target.value,
                  })
                }
                className="w-full min-h-[60px] px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              <div>
                <Label>Currency</Label>
                <Input
                  type="text"
                  required
                  value={planFormData.currency}
                  onChange={(e) =>
                    setPlanFormData({
                      ...planFormData,
                      currency: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <Label>Base Price ($/mo)</Label>
                <Input
                  type="number"
                  className={numberInputWithoutStepperClassName}
                  step={0.01}
                  required
                  value={
                    planFormData.base_price_cents
                      ? (planFormData.base_price_cents / 100).toString()
                      : '0'
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setPlanFormData({
                      ...planFormData,
                      base_price_cents:
                        val === '' ? 0 : Math.round(parseFloat(val) * 100) || 0,
                    });
                  }}
                />
              </div>
              <div>
                <Label>Included Minutes</Label>
                <Input
                  type="number"
                  className={numberInputWithoutStepperClassName}
                  required
                  value={planFormData.included_minutes}
                  onChange={(e) =>
                    setPlanFormData({
                      ...planFormData,
                      included_minutes: Number(e.target.value),
                    })
                  }
                />
              </div>
              <div>
                <Label>Overage Rate ($/min)</Label>
                <Input
                  type="number"
                  className={numberInputWithoutStepperClassName}
                  step={0.01}
                  required
                  value={
                    planFormData.overage_rate_cents_per_minute
                      ? (
                          planFormData.overage_rate_cents_per_minute / 100
                        ).toString()
                      : '0'
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setPlanFormData({
                      ...planFormData,
                      overage_rate_cents_per_minute:
                        val === '' ? 0 : Math.round(parseFloat(val) * 100) || 0,
                    });
                  }}
                />
              </div>
              <div>
                <Label>Setup Fee ($)</Label>
                <Input
                  type="number"
                  className={numberInputWithoutStepperClassName}
                  step={0.01}
                  required
                  value={
                    planFormData.setup_fee_cents
                      ? (planFormData.setup_fee_cents / 100).toString()
                      : '0'
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setPlanFormData({
                      ...planFormData,
                      setup_fee_cents:
                        val === '' ? 0 : Math.round(parseFloat(val) * 100) || 0,
                    });
                  }}
                />
              </div>
              <div>
                <Label>Per Order Fee ($/order)</Label>
                <Input
                  type="number"
                  className={numberInputWithoutStepperClassName}
                  step={0.01}
                  required
                  value={
                    planFormData.per_order_rate_cents
                      ? (planFormData.per_order_rate_cents / 100).toString()
                      : '0'
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setPlanFormData({
                      ...planFormData,
                      per_order_rate_cents:
                        val === '' ? 0 : Math.round(parseFloat(val) * 100) || 0,
                    });
                  }}
                />
              </div>
            </div>

            <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider pt-2">
              Plan Add-ons Configuration
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="border border-gray-200 dark:border-gray-800 p-4 rounded-xl space-y-4">
                <span className="font-semibold text-xs text-gray-900 dark:text-white block">
                  Multilingual Support
                </span>
                <div>
                  <Label>Add-on Price ($/mo)</Label>
                  <Input
                    type="number"
                    className={numberInputWithoutStepperClassName}
                    step={0.01}
                    value={
                      planFormData.multilingual_addon_cents
                        ? (
                            planFormData.multilingual_addon_cents / 100
                          ).toString()
                        : '0'
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setPlanFormData({
                        ...planFormData,
                        multilingual_addon_cents:
                          val === ''
                            ? 0
                            : Math.round(parseFloat(val) * 100) || 0,
                      });
                    }}
                  />
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={planFormData.multilingual_included}
                    onChange={(e) =>
                      setPlanFormData({
                        ...planFormData,
                        multilingual_included: e.target.checked,
                      })
                    }
                  />
                  Included in Base Plan
                </label>
              </div>

              <div className="border border-gray-200 dark:border-gray-800 p-4 rounded-xl space-y-4">
                <span className="font-semibold text-xs text-gray-900 dark:text-white block">
                  SMS Ordering
                </span>
                <div>
                  <Label>Add-on Price ($/mo)</Label>
                  <Input
                    type="number"
                    className={numberInputWithoutStepperClassName}
                    step={0.01}
                    value={
                      planFormData.sms_ordering_addon_cents
                        ? (
                            planFormData.sms_ordering_addon_cents / 100
                          ).toString()
                        : '0'
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setPlanFormData({
                        ...planFormData,
                        sms_ordering_addon_cents:
                          val === ''
                            ? 0
                            : Math.round(parseFloat(val) * 100) || 0,
                      });
                    }}
                  />
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={planFormData.sms_ordering_included}
                    onChange={(e) =>
                      setPlanFormData({
                        ...planFormData,
                        sms_ordering_included: e.target.checked,
                      })
                    }
                  />
                  Included in Base Plan
                </label>
              </div>

              <div className="border border-gray-200 dark:border-gray-800 p-4 rounded-xl space-y-4">
                <span className="font-semibold text-xs text-gray-900 dark:text-white block">
                  OpenTable / Resy
                </span>
                <div>
                  <Label>Add-on Price ($/mo)</Label>
                  <Input
                    type="number"
                    className={numberInputWithoutStepperClassName}
                    step={0.01}
                    value={
                      planFormData.opentable_resy_addon_cents
                        ? (
                            planFormData.opentable_resy_addon_cents / 100
                          ).toString()
                        : '0'
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setPlanFormData({
                        ...planFormData,
                        opentable_resy_addon_cents:
                          val === ''
                            ? 0
                            : Math.round(parseFloat(val) * 100) || 0,
                      });
                    }}
                  />
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={planFormData.opentable_resy_included}
                    onChange={(e) =>
                      setPlanFormData({
                        ...planFormData,
                        opentable_resy_included: e.target.checked,
                      })
                    }
                  />
                  Included in Base Plan
                </label>
              </div>
            </div>

            <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider pt-2">
              Plan Feature Points (Shown on selection page)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => {
                const key = `feature_point_${num}` as
                  | 'feature_point_1'
                  | 'feature_point_2'
                  | 'feature_point_3'
                  | 'feature_point_4'
                  | 'feature_point_5'
                  | 'feature_point_6'
                  | 'feature_point_7'
                  | 'feature_point_8';
                return (
                  <div key={num}>
                    <Label>Feature Point {num}</Label>
                    <Input
                      type="text"
                      placeholder={`e.g. Custom feature ${num}`}
                      value={planFormData[key] || ''}
                      onChange={(e) =>
                        setPlanFormData({
                          ...planFormData,
                          [key]: e.target.value,
                        })
                      }
                    />
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-3 pt-2">
              <div>
                <Label>Trial Period Days (optional)</Label>
                <Input
                  type="number"
                  className={numberInputWithoutStepperClassName}
                  value={planFormData.trial_period_days}
                  onChange={(e) =>
                    setPlanFormData({
                      ...planFormData,
                      trial_period_days:
                        e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                />
              </div>
              <div className="flex items-center gap-4 mt-6">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={planFormData.is_active}
                    onChange={(e) =>
                      setPlanFormData({
                        ...planFormData,
                        is_active: e.target.checked,
                      })
                    }
                  />
                  Plan is Active
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={planFormData.visible_on_plan_page}
                    onChange={(e) =>
                      setPlanFormData({
                        ...planFormData,
                        visible_on_plan_page: e.target.checked,
                      })
                    }
                  />
                  Visible on Selection Page
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
              <button
                type="button"
                onClick={() => {
                  setIsPlanModalOpen(false);
                  setSelectedPlan(null);
                }}
                className="inline-flex items-center justify-center gap-2 rounded-lg transition px-4 py-2.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 rounded-lg transition px-4 py-2.5 text-sm bg-[rgb(var(--app-brand))] text-white shadow-theme-xs hover:bg-[rgb(var(--app-brand-strong))]"
              >
                {selectedPlan ? 'Save Changes' : 'Create Plan'}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </>
  );
}
